import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import useDriverDniLookup from "./useDriverDniLookup";
import "flatpickr/dist/themes/material_green.css";
import Flatpickr from "flatpickr";
import { Spanish } from "flatpickr/dist/l10n/es.js";
import { toast } from "react-toastify";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import PhoneLoader from "../../components/loader/PhoneLoader";
import { useAuth } from "../../provider/authProvider";
import inventoryApi from "../../services/inventoryApi";
import electronicDocumentsApi from "../../services/electronicDocumentsApi";
import {
  downloadProductImportTemplate,
  parseProductImportFile,
} from "../../utils/inventoryProductExcel";
import ShippingGuides from "./ShippingGuides";
import { downloadEntriesExcel } from "../../utils/inventoryEntriesExcel";
import { downloadTransferExcel } from "../../utils/inventoryTransferExcel";
import PromotionsAdmin from "./PromotionsAdmin";
import {
  handleImeiInput,
  handlePriceInput,
  handleSkuInput,
  normalizePriceValue,
} from "../../utils/inputUtils";
import "../../styles/Inventory.css";
import "../../styles/electronicDocuments.css";

const tabs = [
  ["dashboard", "Dashboard"],
  ["products", "Productos"],
  ["entries", "Ingresos"],
  ["purchaseInvoices", "Facturas Compra"],
  ["receipts", "Recojos"],
  ["stock", "Stock global"],
  ["transfers", "Traslados"],
  ["shippingGuides", "Guías"],
  ["trace", "Trazabilidad"],
  ["locations", "Ubicaciones"],
  ["catalogs", "Catálogos"],
  ["saleReceipts", "Comprobantes"],
  ["audit", "Auditoría"],
  ["promotions", "Promociones"],
];
// Rutas canónicas de cada sección. Las pestañas no crean un segundo router.
export const INVENTORY_TAB_PATHS = Object.freeze({
  dashboard: "/inventory",
  products: "/inventory/products",
  entries: "/inventory/entries",
  purchaseInvoices: "/inventory/purchase-invoices",
  receipts: "/inventory/logistics-receipts",
  stock: "/inventory/stock",
  transfers: "/inventory/transfers",
  shippingGuides: "/inventory/shipping-guides",
  trace: "/inventory/traceability",
  locations: "/inventory/locations",
  catalogs: "/inventory/catalogs",
  saleReceipts: "/inventory/documents",
  audit: "/inventory/audit",
  promotions: "/inventory/promotions",
});
const money = (v) =>
  new Intl.NumberFormat("es-PE", { style: "currency", currency: "PEN" }).format(
    Number(v || 0),
  );
const option = (rows, label = "name") =>
  (rows || []).map((r) => ({ value: Number(r.id), label: r[label], raw: r }));
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 44,
    borderRadius: 10,
    borderColor: state.isFocused ? "#2563eb" : "#d7deea",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(37,99,235,.12)" : "none",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};
const unwrap = (r) => r?.data ?? r ?? [];

// Regla única del Dashboard: la ciudad operativa es la primera palabra
// del nombre de la sede. No depende de fp_inventory_locations.city ni del distrito.
const dashboardCityFromSedeName = (name) => {
  const normalized = String(name || "").trim();
  return normalized ? normalized.split(/\s+/)[0] : "";
};

const DATE_TIME_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?$/;
const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const inventoryTodayLima = () => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Lima",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const values = Object.fromEntries(
    parts.filter((part) => part.type !== "literal").map((part) => [part.type, part.value]),
  );
  return `${values.year}-${values.month}-${values.day}`;
};

const to12HourClock = (hour24) => {
  const hour = Number(hour24);
  const period = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;

  return {
    hour: String(hour12).padStart(2, "0"),
    period,
  };
};

const formatInventoryDate = (value) => {
  if (value === null || value === undefined || value === "") return "—";

  const normalizedValue = String(value).trim();
  const dateTimeMatch = normalizedValue.match(DATE_TIME_PATTERN);

  if (dateTimeMatch) {
    // Cuando MySQL devuelve un DATETIME como objeto Date, JSON lo serializa en UTC
    // (termina en Z). En ese caso convertimos explícitamente al horario peruano.
    // Si la fecha llega sin zona horaria, la conservamos como hora local almacenada
    // para no aplicar una segunda conversión sobre valores ya guardados en hora Perú.
    const hasExplicitTimeZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalizedValue);

    if (hasExplicitTimeZone) {
      const parsed = new Date(normalizedValue);

      if (!Number.isNaN(parsed.getTime())) {
        const parts = new Intl.DateTimeFormat("en-US", {
          timeZone: "America/Lima",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
        }).formatToParts(parsed);
        const values = Object.fromEntries(
          parts
            .filter((part) => part.type !== "literal")
            .map((part) => [part.type, part.value]),
        );

        return `${values.day}/${values.month}/${values.year} ${values.hour}:${values.minute}:${values.second} ${values.dayPeriod}`;
      }
    }

    const [, year, month, day, hour24, minute, second] = dateTimeMatch;
    const { hour, period } = to12HourClock(hour24);
    return `${day}/${month}/${year} ${hour}:${minute}:${second} ${period}`;
  }

  const dateOnlyMatch = normalizedValue.match(DATE_ONLY_PATTERN);

  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return `${day}/${month}/${year}`;
  }

  return normalizedValue;
};

const isInventoryDateField = (key) =>
  key === "expiration_date" ||
  key.endsWith("_at") ||
  key.endsWith("_date");
const formatMoneyInput = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  return new Intl.NumberFormat("es-PE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value));
};

const normalizeImeiValue = (value) =>
  String(value ?? "")
    .replace(/\D/g, "")
    .slice(0, 15);

const validateImeiValue = (value) => {
  const normalized = normalizeImeiValue(value);
  const valid = /^\d{15}$/.test(normalized);

  return {
    normalized,
    valid,
    code: valid ? "IMEI_VALID_LENGTH" : "IMEI_INVALID_LENGTH",
    message: valid
      ? "IMEI completo: 15/15 dígitos"
      : `${normalized.length}/15 dígitos`,
  };
};

const normalizeModelMatchText = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");

const getModelTitleMatch = (title, modelName) => {
  const normalizedTitle = normalizeModelMatchText(title);
  const normalizedModel = normalizeModelMatchText(modelName);

  if (!normalizedTitle || !normalizedModel) {
    return {
      valid: false,
      pending: true,
      message: "",
    };
  }

  const valid = ` ${normalizedTitle} `.includes(` ${normalizedModel} `);

  return {
    valid,
    pending: false,
    message: valid
      ? `El título contiene el modelo “${modelName}”.`
      : `El título debe incluir el modelo completo “${modelName}”.`,
  };
};


const transferStatusMeta = (status) => {
  const key = String(status || "").toUpperCase();

  return (
    {
      DRAFT: {
        label: "Preparación",
        tone: "neutral",
        step: "Preparando el traslado",
      },
      PENDING_APPROVAL: {
        label: "Por aprobar",
        tone: "warning",
        step: "Esperando aprobación",
      },
      APPROVED: {
        label: "Listo para despacho",
        tone: "info",
        step: "Aprobado y listo para salir",
      },
      DISPATCHED: {
        label: "En tránsito",
        tone: "primary",
        step: "Equipos en ruta",
      },
      RECEIVED: {
        label: "Recibido",
        tone: "success",
        step: "Recepción confirmada",
      },
      CANCELLED: {
        label: "Cancelado",
        tone: "danger",
        step: "Traslado cancelado",
      },
      REJECTED: {
        label: "Rechazado",
        tone: "danger",
        step: "Traslado rechazado",
      },
    }[key] || {
      label: status || "—",
      tone: "neutral",
      step: status || "Sin estado",
    }
  );
};

const inventoryStatusLabel = (value) => {
  const key = String(value || "").toUpperCase();

  return (
    {
      AVAILABLE: "Disponible",
      RESERVED: "Reservado",
      IN_TRANSIT: "En tránsito",
      SOLD: "Vendido",
      DAMAGED: "Dañado",
      WRITTEN_OFF: "Dado de baja",
      RETURNED: "Devuelto",
      PENDING: "Pendiente",
      PENDING_CONFIRMATION: "Pendiente de confirmación",
      CONFIRMED: "Confirmado",
      COMPLETED: "Completado",
      ACCEPTED: "Aceptado",
      OBSERVED: "Aceptado con observación",
      REJECTED: "Rechazado",
      CANCELLED: "Cancelado",
      ERROR: "Error",
      PROCESSING: "Procesando SUNAT",
      RETRY_PENDING: "Reintento pendiente",
      ACTIVE: "Activo",
      INACTIVE: "Inactivo",
      ...Object.fromEntries(
        ["DRAFT", "PENDING_APPROVAL", "APPROVED", "DISPATCHED", "RECEIVED"].map(
          (status) => [status, transferStatusMeta(status).label],
        ),
      ),
    }[key] || value || "—"
  );
};

const auditEntityLabel = (value) => {
  const key = String(value || "").toUpperCase();

  return (
    {
      PRODUCT: "Producto",
      PRODUCTS: "Producto",
      CATEGORY: "Categoría",
      BRAND: "Marca",
      MODEL: "Modelo",
      LOCATION: "Ubicación",
      STOCK: "Stock",
      SERIAL: "IMEI / serie",
      INVENTORY_SERIAL: "IMEI / serie",
      TRANSFER: "Traslado",
      TRANSFER_ITEM: "Detalle de traslado",
      LOGISTICS_RECEIPT: "Recojo logístico",
      RECEIPT: "Recojo",
      SHIPPING_GUIDE: "Guía de remisión",
      SHIPPING_GUIDE_DOCUMENT: "Documento GRE",
      ELECTRONIC_DOCUMENT: "Comprobante electrónico",
      CREDIT_NOTE: "Nota de crédito",
      RUC: "RUC",
      SUPPLIER: "Proveedor",
    }[key] ||
    String(value || "")
      .replace(/^FP_INVENTORY_/i, "")
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/(^|\s)\S/g, (letter) => letter.toUpperCase()) ||
    "—"
  );
};

const auditActionLabel = (value) => {
  const key = String(value || "").toUpperCase();

  return (
    {
      CREATE: "Creación",
      CREATED: "Creación",
      UPDATE: "Actualización",
      UPDATED: "Actualización",
      DELETE: "Eliminación",
      DELETED: "Eliminación",
      INITIAL_ENTRY: "Ingreso inicial",
      STOCK_ENTRY: "Ingreso de stock",
      STOCK_ADJUSTMENT: "Ajuste de stock",
      REACTIVATE: "Reactivación",
      CONFIRM: "Confirmación",
      CONFIRMED: "Confirmación",
      REJECT: "Rechazo",
      REJECTED: "Rechazo",
      SUBMIT: "Envío a aprobación",
      APPROVE: "Aprobación",
      APPROVED: "Aprobación",
      DISPATCH: "Despacho",
      DISPATCHED: "Despacho",
      RECEIVE: "Recepción",
      RECEIVED: "Recepción",
      CANCEL: "Cancelación",
      CANCELLED: "Cancelación",
      RESERVE: "Reserva",
      RESERVED: "Reserva",
      RELEASE: "Liberación",
      SALE: "Venta",
      RETURN: "Devolución",
      DOCUMENT: "Documentación",
      GENERATE: "Generación",
      SEND: "Envío",
      RETRY: "Reintento",
      VALIDATE: "Validación",
      GRE_LOGISTIC_REVERSAL_WITHOUT_PHYSICAL_MOVEMENT: "Reversión logística · GRE conservada",
      TRANSFER_CANCELLED_WITHOUT_PHYSICAL_MOVEMENT: "Traslado cancelado · stock liberado",
    }[key] ||
    String(value || "")
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/(^|\s)\S/g, (letter) => letter.toUpperCase()) ||
    "—"
  );
};

const movementTypeLabel = (value) => {
  const key = String(value || "").toUpperCase();

  return (
    {
      INITIAL_BALANCE: "Saldo inicial",
      INITIAL_ENTRY: "Ingreso inicial",
      PURCHASE_IN: "Ingreso por compra",
      TRANSFER_OUT: "Salida por traslado",
      TRANSFER_IN: "Ingreso por traslado",
      SALE: "Venta",
      SALE_OUT: "Salida por venta",
      RETURN: "Devolución",
      RETURN_IN: "Ingreso por devolución",
      ADJUSTMENT_IN: "Ajuste de ingreso",
      ADJUSTMENT_OUT: "Ajuste de salida",
      RESERVATION: "Reserva de stock",
      RESERVATION_RELEASE: "Liberación de reserva",
      DAMAGE: "Equipo dañado",
      WRITE_OFF: "Baja de inventario",
      WRITTEN_OFF: "Baja de inventario",
    }[key] || auditActionLabel(value)
  );
};

const traceStatusTone = (value) => {
  const key = String(value || "").toUpperCase();
  if (["AVAILABLE", "RECEIVED", "ACCEPTED", "COMPLETED"].includes(key)) return "success";
  if (["RESERVED", "IN_TRANSIT", "PENDING", "PROCESSING", "RETRY_PENDING"].includes(key)) return "warning";
  if (["SOLD"].includes(key)) return "sold";
  if (["WRITTEN_OFF", "INACTIVE", "CANCELLED"].includes(key)) return "neutral";
  if (["DAMAGED", "REJECTED", "ERROR"].includes(key)) return "danger";
  return "neutral";
};

const traceSunatStatusLabel = (value) => {
  const key = String(value || "").toUpperCase();
  return (
    {
      PENDING: "Pendiente",
      ACCEPTED: "Aceptado",
      OBSERVED: "Aceptado con observación",
      REJECTED: "Rechazado",
      ERROR: "Error",
      PROCESSING: "Procesando",
      SENT: "Enviado",
      RETRY_PENDING: "Reintento pendiente",
    }[key] || inventoryStatusLabel(value)
  );
};

const traceLocationRoute = (event) => {
  const origin = String(event?.originName || "").trim();
  const destination = String(event?.destinationName || "").trim();
  if (origin && destination) return `${origin} → ${destination}`;
  return destination || origin || "";
};

const traceEventDescription = (event) => {
  const type = String(event?.movement_type || "").toUpperCase();
  const source = String(event?.source || "").toUpperCase();
  const origin = String(event?.originName || "").trim();
  const destination = String(event?.destinationName || "").trim();
  const route = traceLocationRoute(event);

  if (source === "SHIPPING_GUIDE") {
    const mode = String(event?.guide_mode || "").toUpperCase();
    const documentType = mode === "SUNAT" ? "GRE SUNAT" : "Guía interna";
    const status = event?.sunat_status ? ` · ${traceSunatStatusLabel(event.sunat_status)}` : "";
    return `${documentType}${route ? ` · ${route}` : ""}${status}`;
  }

  if (source === "LOGISTIC_REVERSAL") {
    const gre = event?.full_number ? ` · GRE ${event.full_number}` : "";
    const pending = String(event?.sunat_cancellation_status || "").toUpperCase() === "PENDING"
      ? " · Baja SUNAT pendiente"
      : "";
    return `Reversión logística sin movimiento físico${route ? ` · ${route}` : ""}${gre}${pending}`;
  }

  if (source === "AUDIT") {
    const entity = auditEntityLabel(event?.entity_type);
    const action = auditActionLabel(event?.action);
    return `${action} de ${String(entity || "registro").toLowerCase()}`;
  }

  switch (type) {
    case "TRANSFER_OUT":
      return origin && destination
        ? `Salida desde ${origin} con destino a ${destination}`
        : route ? `Salida por traslado · ${route}` : "Salida por traslado";
    case "TRANSFER_IN":
      return origin && destination
        ? `Ingreso a ${destination} procedente de ${origin}`
        : route ? `Ingreso por traslado · ${route}` : "Ingreso por traslado";
    case "INITIAL_BALANCE":
    case "INITIAL_ENTRY":
      return destination ? `Stock registrado en ${destination}` : (event?.description || "Stock registrado al crear el producto");
    case "PURCHASE_IN":
      return destination ? `Ingreso por compra a ${destination}` : (event?.description || "Ingreso por compra");
    case "RESERVATION":
      return origin ? `Reserva de stock en ${origin}` : destination ? `Reserva de stock en ${destination}` : (event?.description || "Reserva de stock");
    case "RESERVATION_RELEASE":
      return origin ? `Liberación de reserva en ${origin}` : destination ? `Liberación de reserva en ${destination}` : (event?.description || "Liberación de reserva");
    case "SALE":
    case "SALE_OUT":
      return origin ? `Venta registrada en ${origin}` : (event?.description || "Venta registrada");
    case "RETURN":
    case "RETURN_IN":
      return destination ? `Devolución ingresada a ${destination}` : (event?.description || "Devolución registrada");
    case "ADJUSTMENT_IN":
      if (String(event?.reference_type || "").toUpperCase() === "WRITE_OFF_REVERSAL") {
        return destination ? `Equipo reactivado en ${destination} por reversión de baja` : (event?.description || "Equipo reactivado por reversión de baja");
      }
      return destination ? `Ajuste de ingreso en ${destination}` : (event?.description || "Ajuste de ingreso");
    case "ADJUSTMENT_OUT":
      return origin ? `Ajuste de salida en ${origin}` : (event?.description || "Ajuste de salida");
    case "DAMAGE":
      return origin ? `Equipo marcado como dañado en ${origin}` : destination ? `Equipo marcado como dañado en ${destination}` : (event?.description || "Equipo marcado como dañado");
    case "WRITE_OFF":
    case "WRITTEN_OFF":
      return origin ? `Baja de inventario en ${origin}` : destination ? `Baja de inventario en ${destination}` : (event?.description || "Equipo dado de baja del inventario");
    default: {
      const description = String(event?.description || "").trim();
      if (description && route && !description.includes(origin) && !description.includes(destination)) {
        return `${description} · ${route}`;
      }
      return description || (route ? `Ubicación: ${route}` : auditEntityLabel(event?.entity_type));
    }
  }
};

const traceEventLocation = (event) => {
  const route = traceLocationRoute(event);
  return route || "";
};

const auditRoleLabel = (value) => {
  const key = String(value ?? "");

  return (
    {
      "1": "Administrador",
      "2": "Supervisor",
      "3": "Asesor",
      "4": "Créditos",
      "5": "Cobranza",
      "6": "Oficina",
      "7": "Almacén",
      "8": "Contabilidad",
      "9": "Call Center",
      "11": "Logística",
    }[key] || (value ? `Rol ${value}` : "—")
  );
};

const todayLima = () => new Intl.DateTimeFormat("en-CA", { timeZone: "America/Lima", year:"numeric", month:"2-digit", day:"2-digit" }).format(new Date());
const momentStartOfMonth = () => `${todayLima().slice(0,7)}-01`;

export default function Inventory({ namePage = "inventoryPage" }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const role = String(auth.rol || "");
  const permissions = useMemo(
    () => ({
      dashboard: role === "1",
      locations: role === "1",
      catalogs: role === "1",
      productView: ["1", "7", "8", "9"].includes(role),
      productCreate: ["1", "7"].includes(role),
      productEdit: ["1", "7"].includes(role),
      productDelete: role === "1",
      logistics: ["1", "11"].includes(role),
      confirm: ["1", "7"].includes(role),
      stock: ["1", "7"].includes(role),
      transfer: ["1", "7"].includes(role),
      shippingGuides: ["1", "7", "8"].includes(role),
      shippingGuidesWrite: ["1", "7"].includes(role),
      audit: ["1", "8"].includes(role),
      receipts: ["1", "3", "5", "7", "8"].includes(role),
      purchaseInvoicesWrite: ["1", "7"].includes(role),
    }),
    [role],
  );
  const requestedInitialTab =
    {
      inventoryDashboardPage: "dashboard",
      inventoryProductsPage: "products",
      inventoryEntriesPage: "entries",
      inventoryPurchaseInvoicesPage: "purchaseInvoices",
      inventoryReceiptsPage: "receipts",
      inventoryStockPage: "stock",
      inventoryTransfersPage: "transfers",
      inventoryShippingGuidesPage: "shippingGuides",
      inventoryTracePage: "trace",
      inventoryLocationsPage: "locations",
      inventoryCatalogsPage: "catalogs",
      inventoryAuditPage: "audit",
      inventorySaleReceiptsPage: "saleReceipts",
    }[namePage] || "dashboard";
  const initialTab =
    role === "9"
      ? "products"
      : (requestedInitialTab === "dashboard" && !permissions.dashboard) ||
          (requestedInitialTab === "locations" && !permissions.locations)
        ? "products"
        : requestedInitialTab;
  const routeTab = Object.keys(INVENTORY_TAB_PATHS).find(
    (key) => INVENTORY_TAB_PATHS[key] === location.pathname.replace(/\/$/, "")
  ) || initialTab;
  const isAllowedTab = (key) =>
    (role !== "9" || key === "products") &&
    (key !== "dashboard" || permissions.dashboard) &&
    (key !== "promotions" || role === "1") &&
    (key !== "locations" || permissions.locations) &&
    (key !== "audit" || permissions.audit) &&
    (key !== "saleReceipts" || permissions.receipts) &&
    (key !== "shippingGuides" || permissions.shippingGuides);
  const [tab, setTab] = useState(routeTab);
  useEffect(() => {
    if (isAllowedTab(routeTab)) {
      setTab(routeTab);
    } else {
      navigate(INVENTORY_TAB_PATHS["products"], { replace: true });
    }
  }, [routeTab, role, navigate]);
  const goToInventoryTab = (key, state) => {
    if (!isAllowedTab(key)) return;
    setTab(key);
    navigate(INVENTORY_TAB_PATHS[key], { state });
  };
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState({
    categories: [],
    brands: [],
    models: [],
    locations: [],
    sedes: [],
    suppliers: [],
    rucs: [],
    igvAffectations: [],
  });
  const [data, setData] = useState({});
  const [search, setSearch] = useState("");
  const [purchaseInvoiceFilters, setPurchaseInvoiceFilters] = useState({ status: "", supplierId: null, dateFrom: momentStartOfMonth(), dateTo: todayLima() });
  const [modal, setModal] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [productPage, setProductPage] = useState(1);
  const [productPageSize, setProductPageSize] = useState(20);
  const [productFilters, setProductFilters] = useState({
    categoryId: null,
    brandId: null,
    locationId: null,
    sedeId: null,
    supplierId: null,
    rucId: null,
    stockStatus: null,
  });
  const [dashboardFilters, setDashboardFilters] = useState({
    department: null,
    city: null,
    sedeId: null,
  });
  const [stockPage, setStockPage] = useState(1);
  const [stockPageSize, setStockPageSize] = useState(20);
  const [saleReceiptsPage, setSaleReceiptsPage] = useState(1);
  const [saleReceiptsPageSize, setSaleReceiptsPageSize] = useState(50);
  const [saleReceiptsFilters, setSaleReceiptsFilters] = useState({
    rucId: null,
    sedeId: null,
    advisorId: null,
    operationClass: null,
  });
  const [saleReceiptsSearch, setSaleReceiptsSearch] = useState("");
  const [debouncedSaleReceiptsSearch, setDebouncedSaleReceiptsSearch] = useState("");
  const today = inventoryTodayLima();
  const [saleReceiptsDateFilter, setSaleReceiptsDateFilter] = useState({
    mode: "single",
    dateFrom: today,
    dateTo: today,
  });
  const [historyFilters, setHistoryFilters] = useState({
    dashboard: { mode: "single", dateFrom: today, dateTo: today },
    products: { mode: "single", dateFrom: "", dateTo: "" },
    entries: { mode: "single", dateFrom: "", dateTo: "" },
    transfers: { mode: "single", dateFrom: today, dateTo: today },
    shippingGuides: { mode: "single", dateFrom: today, dateTo: today },
  });
  const [historyBounds, setHistoryBounds] = useState({
    dashboard: { minDate: today, maxDate: today },
    products: { minDate: today, maxDate: today },
    entries: { minDate: today, maxDate: today },
    transfers: { minDate: today, maxDate: today },
    shippingGuides: { minDate: today, maxDate: today },
  });

  const loadMeta = useCallback(async () => {
    const response = await inventoryApi.meta();
    setMeta(unwrap(response));
  }, []);

  const loadHistoryBounds = useCallback(async () => {
    if (!["1", "7", "8", "9"].includes(role)) return;
    const [dashboardRange, productRange, transferRange, guideRange] = await Promise.all([
      permissions.dashboard ? inventoryApi.dashboardDateRange() : Promise.resolve(null),
      inventoryApi.productDateRange(),
      role === "9" ? Promise.resolve(null) : inventoryApi.transferDateRange(),
      role === "9" ? Promise.resolve(null) : inventoryApi.shippingGuideDateRange(),
    ]);
    const next = {
      dashboard: unwrap(dashboardRange) || {},
      products: unwrap(productRange) || {},
      entries: unwrap(await inventoryApi.initialEntryDateRange()) || {},
      transfers: unwrap(transferRange) || {},
      shippingGuides: unwrap(guideRange) || {},
    };
    setHistoryBounds((current) => ({
      dashboard: { ...current.dashboard, ...next.dashboard },
      products: { ...current.products, ...next.products },
      entries: { ...current.entries, ...next.entries },
      transfers: { ...current.transfers, ...next.transfers },
      shippingGuides: { ...current.shippingGuides, ...next.shippingGuides },
    }));
  }, [role, permissions.dashboard]);

  useEffect(() => {
    loadHistoryBounds().catch((error) => {
      toast.error(error.response?.data?.error || "No se pudo cargar el rango histórico del inventario.");
    });
  }, [loadHistoryBounds]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSaleReceiptsSearch(saleReceiptsSearch.trim());
      setSaleReceiptsPage(1);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [saleReceiptsSearch]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (role !== "9" && !meta.categories.length) await loadMeta();
      let response;
      if (tab === "dashboard")
        response = await inventoryApi.dashboard({
          dateFrom: historyFilters.dashboard.dateFrom,
          dateTo: historyFilters.dashboard.dateTo,
          department: dashboardFilters.department || undefined,
          city: dashboardFilters.city || undefined,
          sedeId: dashboardFilters.sedeId || undefined,
        });
      if (tab === "products" || tab === "entries")
        response = await (tab === "entries" ? inventoryApi.initialEntries : inventoryApi.products)({
          search,
          dateFrom: historyFilters[tab].dateFrom || undefined,
          dateTo: historyFilters[tab].dateTo || undefined,
          categoryId: productFilters.categoryId || undefined,
          brandId: productFilters.brandId || undefined,
          locationId: productFilters.locationId || undefined,
          sedeId: productFilters.sedeId || undefined,
          supplierId: productFilters.supplierId || undefined,
          rucId: productFilters.rucId || undefined,
          stockStatus: productFilters.stockStatus || undefined,
          page: productPage,
          limit: productPageSize === "all" ? "all" : productPageSize,
        });
      if (tab === "purchaseInvoices") response = await inventoryApi.purchaseInvoices({
        search: search || undefined,
        dateFrom: purchaseInvoiceFilters.dateFrom || undefined,
        dateTo: purchaseInvoiceFilters.dateTo || undefined,
        supplierId: purchaseInvoiceFilters.supplierId || undefined,
        status: purchaseInvoiceFilters.status || undefined,
      });
      if (tab === "receipts") response = await inventoryApi.receipts();
      if (tab === "stock")
        response = await inventoryApi.stock({
          search,
          page: stockPage,
          limit: stockPageSize === "all" ? "all" : stockPageSize,
        });
      if (tab === "transfers") response = await inventoryApi.transfers({
        search: search || undefined,
        dateFrom: historyFilters.transfers.dateFrom,
        dateTo: historyFilters.transfers.dateTo,
      });
      if (tab === "shippingGuides" && permissions.shippingGuides)
        response = await inventoryApi.shippingGuides({
          search,
          dateFrom: historyFilters.shippingGuides.dateFrom,
          dateTo: historyFilters.shippingGuides.dateTo,
        });
      if (tab === "locations")
        response = await inventoryApi.locations({ search });
      if (tab === "promotions") response = [];
      if (tab === "catalogs")
        response = await Promise.all([
          inventoryApi.categories({ search }),
          inventoryApi.brands({ search }),
          inventoryApi.models({ search }),
        ]);
      if (tab === "saleReceipts" && permissions.receipts)
        response = await inventoryApi.saleReceipts({
          search: debouncedSaleReceiptsSearch || undefined,
          dateFrom: saleReceiptsDateFilter.dateFrom || undefined,
          dateTo: saleReceiptsDateFilter.dateTo || undefined,
          rucId: saleReceiptsFilters.rucId || undefined,
          sedeId: saleReceiptsFilters.sedeId || undefined,
          advisorId: saleReceiptsFilters.advisorId || undefined,
          operationClass: saleReceiptsFilters.operationClass || undefined,
          page: saleReceiptsPage,
          limit: saleReceiptsPageSize === "all" ? "all" : saleReceiptsPageSize,
        });
      if (tab === "audit" && permissions.audit)
        response = await inventoryApi.audit({ search });
      if (tab !== "trace") setData((old) => ({ ...old, [tab]: response }));
    } catch (error) {
      toast.error(
        error.response?.data?.error || "No se pudo cargar el inventario.",
      );
    } finally {
      setLoading(false);
    }
  }, [
    loadMeta,
    meta.categories.length,
    permissions.audit,
    permissions.shippingGuides,
    historyFilters.dashboard.dateFrom,
    historyFilters.dashboard.dateTo,
    dashboardFilters.department,
    dashboardFilters.city,
    dashboardFilters.sedeId,
    productPage,
    productPageSize,
    productFilters.categoryId,
    productFilters.brandId,
    productFilters.locationId,
    productFilters.sedeId,
    productFilters.supplierId,
    productFilters.rucId,
    productFilters.stockStatus,
    historyFilters.products.dateFrom,
    historyFilters.products.dateTo,
    historyFilters.entries.dateFrom,
    historyFilters.entries.dateTo,
    purchaseInvoiceFilters.dateFrom, purchaseInvoiceFilters.dateTo, purchaseInvoiceFilters.supplierId, purchaseInvoiceFilters.status,
    search,
    stockPage,
    stockPageSize,
    saleReceiptsPage,
    saleReceiptsPageSize,
    saleReceiptsFilters.rucId,
    saleReceiptsFilters.sedeId,
    saleReceiptsFilters.advisorId,
    saleReceiptsFilters.operationClass,
    debouncedSaleReceiptsSearch,
    saleReceiptsDateFilter.dateFrom,
    saleReceiptsDateFilter.dateTo,
    historyFilters.transfers.dateFrom,
    historyFilters.transfers.dateTo,
    historyFilters.shippingGuides.dateFrom,
    historyFilters.shippingGuides.dateTo,
    tab,
  ]);

  useEffect(() => {
    load();
  }, [load]);
  const rows = unwrap(data[tab]);

  return (
    <div>
      {loading && <PhoneLoader />}
      <Header namePage={namePage} />
      <section className="inventory-page">
        <section className="inventory-hero">
          <div>
            <span>CONTROL EMPRESARIAL</span>
            <h1>Inventario y trazabilidad</h1>
            <p>
              Productos, recojos, stock, IMEI y traslados conectados con tu base
              de datos.
            </p>
          </div>
          <div className="inventory-role">FP</div>
        </section>
        <nav className="inventory-tabs" aria-label="Secciones de inventario">
          {tabs
            .filter(([key]) =>
              (role !== "9" || key === "products") &&
              (key !== "dashboard" || permissions.dashboard) &&
              (key !== "promotions" || role === "1") &&
              (key !== "locations" || permissions.locations) &&
              (key !== "audit" || permissions.audit) &&
              (key !== "saleReceipts" || permissions.receipts) &&
              (key !== "shippingGuides" || permissions.shippingGuides)
            )
            .map(([key, label]) => (
              <button
                key={key}
                className={tab === key ? "active" : ""}
                onClick={() => {
                  goToInventoryTab(key);
                  setSearch("");
                  setProductPage(1);
                  setStockPage(1);
                }}
              >
                {label}
              </button>
            ))}
        </nav>
        <section className="inventory-panel">
          <Toolbar
            tab={tab}
            search={search}
            setSearch={(value) => {
              setSearch(value);
              if (["products","entries"].includes(tab)) setProductPage(1);
              if (tab === "stock") setStockPage(1);
            }}
            onReload={load}
            onExportEntries={async () => {
              try {
                const result=await inventoryApi.initialEntries({search,dateFrom:historyFilters.entries.dateFrom||undefined,dateTo:historyFilters.entries.dateTo||undefined,categoryId:productFilters.categoryId||undefined,brandId:productFilters.brandId||undefined,locationId:productFilters.locationId||undefined,sedeId:productFilters.sedeId||undefined,supplierId:productFilters.supplierId||undefined,rucId:productFilters.rucId||undefined,stockStatus:productFilters.stockStatus||undefined,limit:'all'});
                downloadEntriesExcel(unwrap(result)?.data||[]);
                toast.success('Ingresos exportados correctamente.');
              } catch(e) {console.error(e);toast.error('No se pudo exportar los ingresos.');}
            }}
            onExportTransfers={async () => {
              try {
                const { dateFrom, dateTo } = historyFilters.transfers;
                const response = await inventoryApi.exportTransferDetails({ dateFrom, dateTo });
                downloadTransferExcel(response.data || [], dateFrom, dateTo);
                toast.success("Reporte de traslados descargado.");
              } catch (error) {
                console.error(error);
                toast.error(error?.response?.data?.error || "No se pudo exportar los traslados.");
              }
            }}
            onCreate={() => setModal({ type: tab })}
            onImport={() => setModal({ type: "product-import" })}
            onDownloadTemplate={() => {
              try {
                downloadProductImportTemplate(meta);
                toast.success("Plantilla Excel generada con los catálogos actuales.");
              } catch (error) {
                console.error(error);
                toast.error("No se pudo generar la plantilla Excel.");
              }
            }}
            permissions={permissions}
            meta={meta}
            productFilters={productFilters}
            setProductFilters={(updater) => {
              setProductFilters((current) =>
                typeof updater === "function" ? updater(current) : updater,
              );
              setProductPage(1);
            }}
            productDateFilter={historyFilters[tab] || historyFilters.products}
            productDateBounds={historyBounds[tab] || historyBounds.products}
            setProductDateFilter={(next) => {
              setHistoryFilters((current) => ({ ...current, [tab]: next }));
              setProductPage(1);
            }}
          />
          {["dashboard", "transfers", "shippingGuides"].includes(tab) && (
            <InventoryDateRangeFilter
              value={historyFilters[tab]}
              minDate={historyBounds[tab]?.minDate || today}
              maxDate={historyBounds[tab]?.maxDate || today}
              onChange={(next) =>
                setHistoryFilters((current) => ({ ...current, [tab]: next }))
              }
            />
          )}
          {tab === "dashboard" && (
            <Dashboard
              value={unwrap(data.dashboard)}
              meta={meta}
              filters={dashboardFilters}
              setFilters={setDashboardFilters}
            />
          )}
          {["products","entries"].includes(tab) && (
            <Products
              rows={rows?.data || rows}
              canView={tab === "products" && permissions.productView}
              canEdit={tab === "products" && permissions.productEdit}
              canDelete={tab === "products" && permissions.productDelete}
              onView={(product) => setModal({ type: "product-view", product })}
              onEdit={(product) => setModal({ type: "products", product })}
              onDelete={(product) =>
                setModal({ type: "product-delete", product })
              }
              pagination={rows?.pagination}
              pageSize={productPageSize}
              meta={meta}
              productFilters={productFilters}
              setProductFilters={(updater) => {
                setProductFilters((current) =>
                  typeof updater === "function" ? updater(current) : updater,
                );
                setProductPage(1);
              }}
              onPageSizeChange={(value) => {
                setProductPageSize(value);
                setProductPage(1);
              }}
              onPageChange={setProductPage}
            />
          )}
          {tab === "purchaseInvoices" && (
            <PurchaseInvoices rows={rows || []} meta={meta} search={search} setSearch={setSearch} filters={purchaseInvoiceFilters} setFilters={setPurchaseInvoiceFilters} canWrite={permissions.purchaseInvoicesWrite} reload={load} />
          )}
          {tab === "receipts" && (
            <Receipts rows={rows} permissions={permissions} reload={load} />
          )}
          {tab === "stock" && (
            <Stock
              rows={rows?.data || rows}
              pagination={rows?.pagination}
              pageSize={stockPageSize}
              onPageSizeChange={(value) => {
                setStockPageSize(value);
                setStockPage(1);
              }}
              onPageChange={setStockPage}
            />
          )}
          {tab === "transfers" && (
            <Transfers
              rows={rows}
              permissions={permissions}
              reload={load}
              onOpenGuides={(transfer) => {
                const guideDate = String(transfer.created_at || transfer.requested_at || "").slice(0, 10) || today;
                setHistoryFilters((current) => ({
                  ...current,
                  shippingGuides: { mode: "single", dateFrom: guideDate, dateTo: guideDate },
                }));
                setSearch(transfer.transfer_number || "");
                goToInventoryTab("shippingGuides", {
                  transferSearch: transfer.transfer_number || "",
                  transferDate: guideDate,
                });
              }}
            />
          )}
          {tab === "shippingGuides" && permissions.shippingGuides && (
            <ShippingGuides
              rows={rows}
              canManage={permissions.shippingGuidesWrite}
              reload={load}
              dateFilters={historyFilters.shippingGuides}
            />
          )}
          {tab === "trace" && <Trace />}
          {tab === "locations" && (
            <Locations
              rows={rows}
              canManage={permissions.catalogs}
              onCreate={() => setModal({ type: "locations" })}
              onEdit={(location) => setModal({ type: "locations", product: location })}
              onDelete={(location) =>
                setModal({ type: "location-delete", product: location })
              }
            />
          )}
          {tab === "promotions" && role === "1" && <PromotionsAdmin meta={meta} />}
          {tab === "catalogs" && (
            <Catalogs
              responses={data.catalogs}
              canManage={permissions.catalogs}
              onCreate={(catalogType) => setModal({ type: catalogType })}
              onEdit={(catalogType, record) =>
                setModal({ type: catalogType, product: record })
              }
              onDelete={(catalogType, record) =>
                setModal({ type: "catalog-delete", catalogType, product: record })
              }
            />
          )}
          {tab === "saleReceipts" && (
            <SaleReceipts
              rows={rows?.data || rows}
              pagination={rows?.pagination}
              filterOptions={rows?.filterOptions}
              pageSize={saleReceiptsPageSize}
              filters={saleReceiptsFilters}
              search={saleReceiptsSearch}
              setSearch={setSaleReceiptsSearch}
              dateFilter={saleReceiptsDateFilter}
              setDateFilter={(next) => {
                setSaleReceiptsDateFilter(next);
                setSaleReceiptsPage(1);
              }}
              setFilters={(updater) => {
                setSaleReceiptsFilters((current) =>
                  typeof updater === "function" ? updater(current) : updater,
                );
                setSaleReceiptsPage(1);
              }}
              onPageSizeChange={(value) => {
                setSaleReceiptsPageSize(value);
                setSaleReceiptsPage(1);
              }}
              onPageChange={setSaleReceiptsPage}
              reload={load}
            />
          )}
          {tab === "audit" && <Audit rows={rows} />}
        </section>
      </section>
      {modal && (
        modal.type === "product-view" ? (
          <ProductReadOnlyModal
            product={modal.product}
            close={() => setModal(null)}
          />
        ) : modal.type === "product-import" ? (
          <ProductImportModal
            close={() => setModal(null)}
            saved={() => {
              setModal(null);
              loadMeta();
              load();
            }}
          />
        ) : (
          <Modal
            type={modal.type}
            product={modal.product}
            catalogType={modal.catalogType}
            deleting={deleting}
            setDeleting={setDeleting}
            meta={meta}
            close={() => setModal(null)}
            saved={() => {
              setModal(null);
              loadMeta();
              load();
            }}
          />
        )
      )}
      <Footer />
    </div>
  );
}

function flatpickrDateToIso(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function InventoryHistoryCalendar({ mode, value, minDate, maxDate, onChange }) {
  const inputRef = useRef(null);
  const instanceRef = useRef(null);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!inputRef.current) return undefined;

    if (instanceRef.current) {
      instanceRef.current.destroy();
      instanceRef.current = null;
    }

    const isRange = mode === "range";
    const defaultDate = isRange
      ? [value?.dateFrom, value?.dateTo].filter(Boolean)
      : value?.dateFrom
        ? [value.dateFrom]
        : [];

    instanceRef.current = Flatpickr(inputRef.current, {
      locale: Spanish,
      mode: isRange ? "range" : "single",
      dateFormat: "Y-m-d",
      altInput: true,
      altFormat: "d/m/Y",
      defaultDate,
      minDate,
      maxDate,
      disableMobile: true,
      allowInput: false,
      clickOpens: true,
      monthSelectorType: "static",
      showMonths: 1,
      onReady: (_dates, _dateStr, instance) => {
        if (instance.altInput) {
          instance.altInput.classList.add("inventory-flatpickr-input");
          instance.altInput.setAttribute(
            "aria-label",
            isRange ? "Seleccionar rango de fechas" : "Seleccionar fecha",
          );
          instance.altInput.setAttribute(
            "placeholder",
            isRange ? "Seleccionar rango" : "Seleccionar fecha",
          );
        }
      },
      onChange: (selectedDates) => {
        if (!selectedDates.length) return;

        const dateFrom = flatpickrDateToIso(selectedDates[0]);
        if (!dateFrom) return;

        if (!isRange) {
          onChangeRef.current({ mode: "single", dateFrom, dateTo: dateFrom });
          return;
        }

        if (selectedDates.length < 2) return;

        const dateTo = flatpickrDateToIso(selectedDates[1]);
        if (!dateTo) return;

        onChangeRef.current({ mode: "range", dateFrom, dateTo });
      },
    });

    return () => {
      if (instanceRef.current) {
        instanceRef.current.destroy();
        instanceRef.current = null;
      }
    };
  }, [maxDate, minDate, mode, value?.dateFrom, value?.dateTo]);

  return (
    <div className="inventory-history-calendar-control">
      <span className="inventory-history-calendar-label">
        {mode === "range" ? "Rango seleccionado" : "Fecha seleccionada"}
      </span>
      <div className="inventory-history-calendar-input-wrap">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M7 2v3M17 2v3M3.5 9h17M5 4h14a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" />
        </svg>
        <input ref={inputRef} type="text" className="inventory-flatpickr-source" />
      </div>
    </div>
  );
}

function InventoryDateRangeFilter({ value, minDate, maxDate, onChange }) {
  const mode = value?.mode === "range" ? "range" : "single";
  const single = mode === "single";
  const selectedText = single
    ? formatInventoryDate(value?.dateFrom)
    : `${formatInventoryDate(value?.dateFrom)} → ${formatInventoryDate(value?.dateTo)}`;

  const setMode = (nextMode) => {
    if (nextMode === "single") {
      const selected = value?.dateFrom || maxDate;
      onChange({ mode: "single", dateFrom: selected, dateTo: selected });
      return;
    }

    onChange({
      mode: "range",
      dateFrom: value?.dateFrom || maxDate,
      dateTo: value?.dateTo || value?.dateFrom || maxDate,
    });
  };

  return (
    <section className="inventory-date-filter" aria-label="Filtro histórico por fecha">
      <div className="inventory-date-filter-heading">
        <div className="inventory-date-filter-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <path d="M7 2v3M17 2v3M3.5 9h17M5 4h14a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" />
          </svg>
        </div>
        <div>
          <span className="inventory-date-filter-eyebrow">HISTORIAL</span>
          <strong>Periodo de consulta</strong>
          <small>
            Disponible desde {formatInventoryDate(minDate)} hasta {formatInventoryDate(maxDate)}.
          </small>
        </div>
      </div>

      <div className="inventory-date-filter-mode" role="group" aria-label="Tipo de selección de fecha">
        <button
          type="button"
          className={single ? "active" : ""}
          aria-pressed={single}
          onClick={() => setMode("single")}
        >
          <span>Un día</span>
          <small>Consulta puntual</small>
        </button>
        <button
          type="button"
          className={!single ? "active" : ""}
          aria-pressed={!single}
          onClick={() => setMode("range")}
        >
          <span>Rango</span>
          <small>Periodo personalizado</small>
        </button>
      </div>

      <InventoryHistoryCalendar
        mode={mode}
        value={value}
        minDate={minDate}
        maxDate={maxDate}
        onChange={onChange}
      />

      <div className="inventory-date-filter-summary">
        <span>Mostrando</span>
        <strong>{selectedText}</strong>
      </div>

      <button
        type="button"
        className="inventory-date-filter-today"
        onClick={() =>
          onChange({ mode: "single", dateFrom: maxDate, dateTo: maxDate })
        }
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 8v4l3 2M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z" />
        </svg>
        Hoy
      </button>
    </section>
  );
}

function ProductToolbarCalendar({
  value,
  minDate,
  maxDate,
  onChange,
  ariaLabel = "Periodo de registro del producto",
  itemLabel = "productos",
}) {
  const mode = value?.mode === "range" ? "range" : "single";
  const hasDateFilter = Boolean(value?.dateFrom && value?.dateTo);

  const clearPeriod = () => {
    onChange({ mode: "single", dateFrom: "", dateTo: "" });
  };

  const setMode = (nextMode) => {
    if (!hasDateFilter) {
      onChange({ mode: nextMode, dateFrom: "", dateTo: "" });
      return;
    }

    const selected = value?.dateFrom || "";
    if (nextMode === "single") {
      onChange({ mode: "single", dateFrom: selected, dateTo: selected });
      return;
    }

    onChange({
      mode: "range",
      dateFrom: selected,
      dateTo: value?.dateTo || selected,
    });
  };

  return (
    <div className="inventory-product-calendar" aria-label={ariaLabel}>
      <div className="inventory-product-calendar-mode" role="group" aria-label="Tipo de periodo">
        <button
          type="button"
          className={!hasDateFilter ? "active" : ""}
          aria-pressed={!hasDateFilter}
          onClick={clearPeriod}
          title={`Mostrar todos los ${itemLabel} sin filtrar por fecha`}
        >
          Todos
        </button>
        <button
          type="button"
          className={hasDateFilter && mode === "single" ? "active" : ""}
          aria-pressed={hasDateFilter && mode === "single"}
          onClick={() => setMode("single")}
        >
          1 día
        </button>
        <button
          type="button"
          className={hasDateFilter && mode === "range" ? "active" : ""}
          aria-pressed={hasDateFilter && mode === "range"}
          onClick={() => setMode("range")}
        >
          Rango
        </button>
      </div>
      <InventoryHistoryCalendar
        mode={mode}
        value={value}
        minDate={minDate}
        maxDate={maxDate}
        onChange={onChange}
      />
      {hasDateFilter && (
        <button
          type="button"
          className="inventory-product-calendar-clear"
          title="Quitar el filtro de fecha"
          aria-label={`Mostrar todos los ${itemLabel} sin filtro de fecha`}
          onClick={clearPeriod}
        >
          Quitar fecha
        </button>
      )}
    </div>
  );
}

const purchaseInvoiceDate = (value) => {
  if (!value) return null;
  const raw = String(value).trim();
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw);
  const normalized = hasZone ? raw : raw.replace(" ", "T") + "-05:00";
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
};

const purchaseInvoiceDateTime = (value) => {
  const date = purchaseInvoiceDate(value);
  if (!date) return value ? formatInventoryDate(value) : "—";
  return new Intl.DateTimeFormat("es-PE", {
    timeZone: "America/Lima", day: "2-digit", month: "2-digit", year: "numeric",
    hour: "numeric", minute: "2-digit", hour12: true,
  }).format(date).replace(",", " ·");
};

const purchaseInvoiceElapsed = (start, end, count = 0) => {
  if (!start || !end || Number(count) <= 1) return "";
  const a = purchaseInvoiceDate(start);
  const b = purchaseInvoiceDate(end);
  if (!a || !b) return "";
  let minutes = Math.max(0, Math.floor((b.getTime() - a.getTime()) / 60000));
  const days = Math.floor(minutes / 1440); minutes %= 1440;
  const hours = Math.floor(minutes / 60); minutes %= 60;
  const parts = [];
  if (days) parts.push(`${days} ${days === 1 ? "día" : "días"}`);
  if (hours) parts.push(`${hours} h`);
  if (minutes || !parts.length) parts.push(`${minutes} min`);
  return parts.join(" ");
};

const purchaseInvoiceRegistrationText = (row) => {
  const count = Number(row?.productCount || 0);
  const a = purchaseInvoiceDate(row?.firstRegisteredAt);
  const b = purchaseInvoiceDate(row?.lastRegisteredAt);
  if (!a) return "Sin fecha de registro";
  if (count <= 1 || !b) return `Registrado: ${purchaseInvoiceDateTime(row.firstRegisteredAt)}`;
  const day = (d) => new Intl.DateTimeFormat("en-CA", { timeZone: "America/Lima", year:"numeric",month:"2-digit",day:"2-digit" }).format(d);
  const clock = (d) => new Intl.DateTimeFormat("es-PE", { timeZone:"America/Lima",hour:"numeric",minute:"2-digit",hour12:true }).format(d);
  const dateOnly = (d) => new Intl.DateTimeFormat("es-PE", { timeZone:"America/Lima",day:"2-digit",month:"2-digit",year:"numeric" }).format(d);
  if (day(a) === day(b)) return `${dateOnly(a)} · ${clock(a)} a ${clock(b)}`;
  return `${purchaseInvoiceDateTime(row.firstRegisteredAt)} → ${purchaseInvoiceDateTime(row.lastRegisteredAt)}`;
};

function PurchaseInvoices({ rows=[], meta={}, search, setSearch, filters, setFilters, canWrite, reload }) {
  const [open, setOpen] = useState(null);
  const [detail, setDetail] = useState([]);
  const [expected, setExpected] = useState("");
  const [detailLoading, setDetailLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const suppliers = option(meta.suppliers || [], "business_name");
  const [invoiceDateRange, setInvoiceDateRange] = useState({ minDate: todayLima(), maxDate: todayLima() });

  useEffect(() => {
    let active = true;
    inventoryApi.purchaseInvoiceDateRange()
      .then((response) => {
        const range = unwrap(response) || {};
        if (active) setInvoiceDateRange({
          minDate: range.minDate || todayLima(),
          maxDate: range.maxDate || todayLima(),
        });
      })
      .catch(() => {
        if (active) setInvoiceDateRange({ minDate: todayLima(), maxDate: todayLima() });
      });
    return () => { active = false; };
  }, []);

  const invoicePeriod = {
    mode: filters.dateFrom && filters.dateTo && filters.dateFrom !== filters.dateTo ? "range" : "single",
    dateFrom: filters.dateFrom || "",
    dateTo: filters.dateTo || "",
  };

  const changeInvoicePeriod = (next) => {
    setFilters((current) => ({
      ...current,
      dateFrom: next?.dateFrom || "",
      dateTo: next?.dateTo || "",
    }));
  };

  const closeDetail = () => {
    if (uploading || confirming) return;
    setOpen(null);
    setDetail([]);
    setExpected("");
  };

  const showDetail = async (row) => {
    setOpen(row);
    setDetail([]);
    setExpected(String(row.expectedQuantity || row.productCount || ""));
    setDetailLoading(true);
    try {
      const r = await inventoryApi.purchaseInvoice(row.id, {
        supplierId: row.supplierId,
        rucId: row.rucId,
        invoiceKey: row.invoiceKey,
      });
      setDetail(unwrap(r)?.items || []);
    } catch (e) {
      setOpen(null);
      toast.error(e.response?.data?.error || "No se pudo abrir la factura.");
    } finally {
      setDetailLoading(false);
    }
  };

  const confirm = async () => {
    if (!open) return;
    try {
      setConfirming(true);
      await inventoryApi.confirmPurchaseInvoice(open.id, {
        supplierId: open.supplierId,
        rucId: open.rucId,
        invoiceNumber: open.invoiceNumber,
        expectedQuantity: Number(expected),
      });
      toast.success("Factura conciliada y confirmada.");
      setOpen(null);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.error || "No se pudo confirmar la factura.");
    } finally {
      setConfirming(false);
    }
  };

  const upload = async (file) => {
    if (!file || !open?.id) return;
    const fd = new FormData();
    fd.append("invoiceFile", file);
    try {
      setUploading(true);
      await inventoryApi.attachPurchaseInvoice(open.id, fd);
      toast.success("Documento de factura guardado.");
      setOpen((current) => current ? { ...current, attachmentOriginalName: file.name } : current);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.error || "No se pudo guardar el archivo.");
    } finally {
      setUploading(false);
    }
  };

  const viewFile = async (row) => {
    try {
      const r = await inventoryApi.downloadPurchaseInvoiceAttachment(row.id);
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e.response?.data?.error || "No se pudo abrir el archivo.");
    }
  };

  const registered = detail.length;
  const declared = Number(expected || 0);
  const difference = declared - registered;
  const canConfirm = Boolean(open?.id > 0 && registered > 0 && declared === registered);

  return <div className="purchase-invoices-page">
    <div className="purchase-invoices-toolbar">
      <div className="purchase-invoice-period-filter">
        <span className="purchase-invoice-filter-label">Periodo</span>
        <ProductToolbarCalendar
          value={invoicePeriod}
          minDate={invoiceDateRange.minDate}
          maxDate={invoiceDateRange.maxDate}
          onChange={changeInvoicePeriod}
          ariaLabel="Periodo de facturas de compra"
          itemLabel="facturas"
        />
      </div>
      <label className="purchase-invoice-search">
        <span className="purchase-invoice-filter-label">Buscar</span>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Buscar factura, producto o IMEI..." />
      </label>
      <label className="purchase-invoice-filter">
        <span className="purchase-invoice-filter-label">Proveedor</span>
        <Select styles={selectStyles} className="purchase-invoice-select" options={suppliers} isClearable placeholder="Proveedor" value={suppliers.find(x=>x.value===Number(filters.supplierId))||null} onChange={x=>setFilters(f=>({...f,supplierId:x?.value||null}))}/>
      </label>
      <label className="purchase-invoice-filter">
        <span className="purchase-invoice-filter-label">Estado</span>
        <Select styles={selectStyles} className="purchase-invoice-select" isClearable placeholder="Estado" options={[{value:"PENDING",label:"Pendiente"},{value:"CONFIRMED",label:"Confirmada"}]} value={[{value:"PENDING",label:"Pendiente"},{value:"CONFIRMED",label:"Confirmada"}].find(x=>x.value===filters.status)||null} onChange={x=>setFilters(f=>({...f,status:x?.value||""}))}/>
      </label>
    </div>
    <div className="purchase-invoice-list">{rows.map(row=><article className="purchase-invoice-card" key={`${row.supplierId}-${row.rucId}-${row.invoiceKey}`}>
      <div className="purchase-invoice-main"><small>N. FACTURA</small><strong>{row.invoiceNumber}</strong><span>{row.supplierName || "Sin proveedor"}</span></div>
      <div><small>PRODUCTOS</small><strong>{row.productCount}</strong><span>Declarados: {row.expectedQuantity || "—"}</span></div>
      <div><small>ESTADO</small><strong className={`invoice-status ${row.status?.toLowerCase()}`}>{row.status==='CONFIRMED'?'Confirmada':'Pendiente'}</strong><span>{row.confirmedByName || "Sin confirmar"}</span></div>
      <div className="purchase-invoice-registration"><small>REGISTRO</small><strong>{purchaseInvoiceRegistrationText(row)}</strong><span>{Number(row.productCount) > 1 ? `Tiempo transcurrido: ${purchaseInvoiceElapsed(row.firstRegisteredAt,row.lastRegisteredAt,row.productCount)}` : "Registro individual"}</span></div>
      <div className="purchase-invoice-registered-by"><small>REGISTRADO POR</small>{Array.isArray(row.registeredByUsers) && row.registeredByUsers.length ? row.registeredByUsers.map((user)=><span key={`${row.invoiceKey}-${user.userId || user.name}`}><strong>{user.name}</strong> <em>({user.productCount} {Number(user.productCount)===1 ? "equipo" : "equipos"})</em></span>) : <span>Sin información</span>}</div>
      <div className="purchase-invoice-ruc"><small>RUC DESTINO</small><strong>{row.destinationRucName || "Sin RUC asignado"}</strong><span>{row.destinationRucNumber || "—"}</span></div>
      <div><small>CONFIRMACIÓN</small><strong>{row.confirmedAt ? formatInventoryDate(row.confirmedAt) : "—"}</strong><span>{row.attachmentOriginalName || "Sin adjunto"}</span></div>
      <div className="purchase-invoice-actions"><button onClick={()=>showDetail(row)}>Ver productos</button>{row.attachmentOriginalName&&<button onClick={()=>viewFile(row)}>Ver factura</button>}</div>
    </article>)}</div>
    {!rows.length && <div className="inventory-empty">No hay facturas de compra para los filtros seleccionados.</div>}

    {open && <div className="inventory-modal purchase-invoice-detail-modal">
      <div className="backdrop" onClick={closeDetail} />
      <section>
        <header>
          <div>
            <small>FACTURA DE COMPRA</small>
            <h2>{open.invoiceNumber}</h2>
            <p>{open.supplierName || "Sin proveedor"}</p>
          </div>
          <button type="button" onClick={closeDetail} disabled={uploading || confirming} aria-label="Cerrar">×</button>
        </header>

        <div className="purchase-invoice-detail-content">
          <div className="purchase-invoice-detail-summary">
            <article><small>Registrados</small><strong>{registered}</strong><span>Productos vinculados</span></article>
            <article><small>Declarados</small><strong>{declared || "—"}</strong><span>Cantidad de factura</span></article>
            <article className={difference === 0 && registered > 0 ? "is-ok" : "is-warning"}><small>Diferencia</small><strong>{declared ? difference : "—"}</strong><span>{difference === 0 && registered > 0 ? "Conciliación correcta" : "Requiere revisión"}</span></article>
            <article><small>Estado</small><strong>{open.status === "CONFIRMED" ? "Confirmada" : "Pendiente"}</strong><span>{open.confirmedByName || "Sin confirmar"}</span></article>
          </div>

          {detailLoading ? <div className="purchase-invoice-detail-state"><span className="transfer-products-spinner" aria-hidden="true"/><span>Consultando productos de la factura...</span></div> :
          <div className="purchase-invoice-items">
            <table>
              <thead><tr><th>#</th><th>Producto</th><th>IMEI 1</th><th>IMEI 2</th><th>Registrado</th><th>Registrado por</th><th>RUC destino</th><th>Estado</th><th>Ubicación</th></tr></thead>
              <tbody>{detail.length ? detail.map((x,index)=><tr key={x.id}><td>{index+1}</td><td><strong>{x.title}</strong></td><td><code>{x.imei||"—"}</code></td><td><code>{x.imei2||"—"}</code></td><td>{purchaseInvoiceDateTime(x.registeredAt)}</td><td>{x.registeredByName||"—"}</td><td><strong>{x.destinationRucName||"—"}</strong>{x.destinationRucNumber&&<span className="purchase-invoice-ruc-number">{x.destinationRucNumber}</span>}</td><td>{inventoryStatusLabel(x.currentStatus)}</td><td>{x.locationName||"—"}</td></tr>) : <tr><td colSpan="9" className="purchase-invoice-items-empty">Esta factura no tiene productos relacionados.</td></tr>}</tbody>
            </table>
          </div>}

          {canWrite && open.status !== 'CONFIRMED' && <div className="purchase-invoice-management">
            <label className="purchase-invoice-quantity-field"><span>Cantidad declarada</span><input type="number" min="1" value={expected} disabled={confirming} onChange={e=>setExpected(e.target.value)}/><small>Debe coincidir exactamente con los productos registrados.</small></label>
            {open.id>0 && <label className={`purchase-invoice-upload${uploading ? " is-loading" : ""}`}><span>{uploading ? "Guardando documento..." : open.attachmentOriginalName ? "Reemplazar factura adjunta" : "Adjuntar factura"}</span><small>JPG, PNG, WEBP o PDF · máximo 10 MB</small><input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" disabled={uploading || confirming} onChange={e=>upload(e.target.files?.[0])}/></label>}
          </div>}

          {open.id===0 && <p className="purchase-invoice-note">Esta factura proviene de datos históricos. Para confirmar, la migración SQL incluida debe haberse ejecutado primero.</p>}
        </div>

        <footer className="inventory-modal-footer">
          {open.attachmentOriginalName&&<button type="button" className="modal-button modal-button-secondary" onClick={()=>viewFile(open)}>Ver documento</button>}
          <button type="button" className="modal-button modal-button-secondary" onClick={closeDetail} disabled={uploading || confirming}>Cerrar</button>
          {canWrite&&open.status!=='CONFIRMED'&&<button type="button" className="modal-button modal-button-primary" disabled={!canConfirm || detailLoading || uploading || confirming} onClick={confirm}>{confirming ? "Confirmando..." : "Confirmar conciliación"}</button>}
        </footer>
      </section>
    </div>}
  </div>;
}

function Toolbar({
  tab,
  search,
  setSearch,
  onReload,
  onExportTransfers,
  onExportEntries,
  onCreate,
  onImport,
  onDownloadTemplate,
  permissions,
  meta = {},
  productFilters = {},
  setProductFilters,
  productDateFilter,
  productDateBounds = {},
  setProductDateFilter,
}) {
  const canCreate =
    (tab === "products" && permissions.productCreate) ||
    (tab === "locations" && permissions.catalogs) ||
    (tab === "receipts" && permissions.logistics) ||
    (tab === "transfers" && permissions.transfer) ||
    (tab === "stock" && permissions.stock);

  const productMode = tab === "products" || tab === "entries";
  const categoryOptions = option(meta.categories || []);
  const brandOptions = option(meta.brands || []);
  const warehouseOptions = option(
    (meta.locations || []).filter(
      (location) =>
        String(location.type || "").toUpperCase() === "CENTRAL_WAREHOUSE" &&
        Number(location.is_active ?? 1) !== 0,
    ),
  ).map((item) => ({
    ...item,
    label: `${item.label}${item.raw?.city ? ` · ${item.raw.city}` : ""}`,
  }));
  const sedeOptions = option(meta.sedes || []).map((item) => ({
    ...item,
    label: `${item.label}${
      item.raw?.districtName ? ` · ${item.raw.districtName}` : ""
    }`,
  }));

  const selected = (options, value) =>
    options.find((item) => Number(item.value) === Number(value)) || null;

  const patchProductFilters = (patch) => {
    if (!setProductFilters) return;
    setProductFilters((current) => ({ ...current, ...patch }));
  };

  const hasProductFilters = Boolean(
    productFilters.categoryId ||
      productFilters.brandId ||
      productFilters.locationId ||
      productFilters.sedeId ||
      productFilters.supplierId ||
      productFilters.rucId ||
      productDateFilter?.dateFrom ||
      productDateFilter?.dateTo ||
      search,
  );

  return (
    <div className={`inventory-toolbar${productMode ? " inventory-toolbar-products" : ""}`}>
      {!productMode && tab !== "promotions" && (
        <div className="inventory-toolbar-heading">
          <h2>{tabs.find(([k]) => k === tab)?.[1]}</h2>
          <small>Información actualizada desde el backend</small>
        </div>
      )}

      {productMode && (
        <>
        {tab === "entries" && <div className="inventoryButtons"><button type="button" className="inventory-template-button" onClick={onExportEntries}><span className="inventory-action-copy"><strong>Descargar Excel</strong><small>Ingresos según filtros</small></span></button></div>}
        {tab === "products" && canCreate && (
          <div className="inventoryButtons" aria-label="Herramientas de importación de productos">
            <button
              type="button"
              className="inventory-template-button"
              onClick={onDownloadTemplate}
              title="Descargar plantilla Excel con los catálogos actuales"
            >
              <span className="inventory-action-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24"><path d="M12 3v11m0 0 4-4m-4 4-4-4M5 16v3h14v-3" /></svg>
              </span>
              <span className="inventory-action-copy">
                <strong>Descargar plantilla</strong>
                <small>Excel listo para completar</small>
              </span>
            </button>
            <button
              type="button"
              className="inventory-import-button"
              onClick={onImport}
              title="Importar productos desde la plantilla Excel"
            >
              <span className="inventory-action-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24"><path d="M12 21V10m0 0-4 4m4-4 4 4M5 8V5h14v3" /></svg>
              </span>
              <span className="inventory-action-copy">
                <strong>Importar Excel</strong>
                <small>Validar antes de registrar</small>
              </span>
            </button>
          </div>
        )}
        <div className="inventory-product-toolbar-controls">
          <div className="inventory-product-filter inventory-product-filter-calendar">
            <span className="inventory-product-filter-label">Periodo</span>
            <ProductToolbarCalendar
              value={productDateFilter}
              minDate={productDateBounds?.minDate || inventoryTodayLima()}
              maxDate={productDateBounds?.maxDate || inventoryTodayLima()}
              onChange={setProductDateFilter}
            />
          </div>

          <label className="inventory-product-search">
            <span>Buscar</span>
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar producto, SKU, IMEI..."
            />
          </label>

          <div className="inventory-product-filter">
            <span className="inventory-product-filter-label">Categoría</span>
            <Select
              styles={selectStyles}
              menuPortalTarget={document.body}
              options={categoryOptions}
              value={selected(categoryOptions, productFilters.categoryId)}
              onChange={(item) => patchProductFilters({ categoryId: item?.value || null })}
              isClearable
              placeholder="Todas"
              noOptionsMessage={() => "Sin categorías"}
              classNamePrefix="inventory-product-select"
            />
          </div>

          <div className="inventory-product-filter">
            <span className="inventory-product-filter-label">Marca</span>
            <Select
              styles={selectStyles}
              menuPortalTarget={document.body}
              options={brandOptions}
              value={selected(brandOptions, productFilters.brandId)}
              onChange={(item) => patchProductFilters({ brandId: item?.value || null })}
              isClearable
              placeholder="Todas"
              noOptionsMessage={() => "Sin marcas"}
              classNamePrefix="inventory-product-select"
            />
          </div>

          <div className="inventory-product-filter">
            <span className="inventory-product-filter-label">Almacén</span>
            <Select
              styles={selectStyles}
              menuPortalTarget={document.body}
              options={warehouseOptions}
              value={selected(warehouseOptions, productFilters.locationId)}
              onChange={(item) => {
                const location = item?.raw || null;
                patchProductFilters({
                  locationId: item?.value || null,
                  sedeId: location?.sede_id
                    ? Number(location.sede_id)
                    : productFilters.sedeId,
                });
              }}
              isClearable
              placeholder="Todos"
              noOptionsMessage={() => "Sin almacenes"}
              classNamePrefix="inventory-product-select"
            />
          </div>

          <div className="inventory-product-filter">
            <span className="inventory-product-filter-label">Sede</span>
            <Select
              styles={selectStyles}
              menuPortalTarget={document.body}
              options={sedeOptions}
              value={selected(sedeOptions, productFilters.sedeId)}
              onChange={(item) => {
                const sedeId = item?.value || null;
                const currentWarehouse = warehouseOptions.find(
                  (warehouse) =>
                    Number(warehouse.value) === Number(productFilters.locationId),
                );
                patchProductFilters({
                  sedeId,
                  locationId:
                    currentWarehouse &&
                    sedeId &&
                    Number(currentWarehouse.raw?.sede_id) !== Number(sedeId)
                      ? null
                      : productFilters.locationId,
                });
              }}
              isClearable
              placeholder="Todas"
              noOptionsMessage={() => "Sin sedes"}
              classNamePrefix="inventory-product-select"
            />
          </div>

          {hasProductFilters && (
            <button
              type="button"
              className="inventory-product-clear-filters"
              onClick={() => {
                setSearch("");
                setProductFilters({
                  categoryId: null,
                  brandId: null,
                  locationId: null,
                  sedeId: null,
                  supplierId: null,
                  rucId: null,
                  stockStatus: null,
                });
                setProductDateFilter?.({
                  mode: "single",
                  dateFrom: "",
                  dateTo: "",
                });
              }}
            >
              Limpiar filtros
            </button>
          )}
        </div>
        </>
      )}

      {!productMode && !["dashboard", "trace", "promotions"].includes(tab) && (
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={tab === "transfers" ? "Código, sede, solicitante, producto, IMEI o estado..." : tab === "shippingGuides" ? "Guía, traslado, RUC, producto, IMEI o estado..." : "Buscar..."}
        />
      )}

      {tab !== "promotions" && <div className="inventory-toolbar-actions">
        {canCreate && (
          <button className="primary" onClick={onCreate}>
            + Nuevo
          </button>
        )}
        <button onClick={onReload}>Actualizar</button>
        {tab === "transfers" && <button type="button" onClick={onExportTransfers}>Descargar Excel</button>}
      </div>}
    </div>
  );
}

function Dashboard({ value = {}, meta = {}, filters = {}, setFilters }) {
  const s = value.summary || {};
  const period = value.period || {};
  const integrity = value.integrity || {};
  const hierarchyValidation = value.hierarchyValidation || {};
  const reconciliation = value.reconciliationBySede || [];
  const num = (v) => Number(v || 0).toLocaleString("es-PE");
  const pct = (v) => `${Number(v || 0).toFixed(1)}%`;

  const departmentOptions = useMemo(() => {
    const map = new Map();
    (meta.sedes || []).forEach((sede) => {
      const name = String(sede.departmentName || "").trim();
      if (name && !map.has(name.toLowerCase())) map.set(name.toLowerCase(), { value: name, label: name });
    });
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label, "es"));
  }, [meta.sedes]);

  const cityOptions = useMemo(() => {
    const map = new Map();
    (meta.sedes || []).forEach((sede) => {
      if (filters.department && String(sede.departmentName || "").toLowerCase() !== String(filters.department).toLowerCase()) return;
      const city = dashboardCityFromSedeName(sede.name);
      if (city && !map.has(city.toLowerCase())) map.set(city.toLowerCase(), { value: city, label: city });
    });
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label, "es"));
  }, [meta.sedes, filters.department]);

  const sedeOptions = useMemo(() =>
    (meta.sedes || [])
      .filter((sede) => {
        if (filters.department && String(sede.departmentName || "").toLowerCase() !== String(filters.department).toLowerCase()) return false;
        if (filters.city) {
          const city = dashboardCityFromSedeName(sede.name);
          if (city.toLowerCase() !== String(filters.city).toLowerCase()) return false;
        }
        return true;
      })
      .map((sede) => ({
        value: Number(sede.id),
        label: `${sede.name}${sede.districtName ? ` · ${sede.districtName}` : ""}`,
      })), [meta.sedes, filters.department, filters.city]);

  const selectedDepartment = departmentOptions.find((item) => item.value === filters.department) || null;
  const selectedCity = cityOptions.find((item) => item.value === filters.city) || null;
  const selectedSede = sedeOptions.find((item) => Number(item.value) === Number(filters.sedeId)) || null;
  const hasFilters = Boolean(filters.department || filters.city || filters.sedeId);
  const isSingleDay = period.dateFrom && period.dateFrom === period.dateTo;
  const periodLabel = isSingleDay ? formatInventoryDate(period.dateFrom) : `${formatInventoryDate(period.dateFrom)} → ${formatInventoryDate(period.dateTo)}`;

  return (
    <div className="inventory-dashboard-pro inventory-dashboard-v2">
      <section className="inventory-dashboard-filterbar inventory-dashboard-filterbar-v2">
        <div>
          <span>CONTROL GERENCIAL DE INVENTARIO</span>
          <strong>Empresa → Departamento → Ciudad operativa → Sede</strong>
          <small>Ciudad operativa = primera palabra del nombre de la sede. Distrito se conserva únicamente como referencia geográfica.</small>
        </div>
        <div className="inventory-dashboard-selects inventory-dashboard-selects-v2">
          <div>
            <span>Departamento</span>
            <Select
              styles={selectStyles}
              menuPortalTarget={document.body}
              options={departmentOptions}
              value={selectedDepartment}
              onChange={(item) => setFilters((current) => ({ ...current, department: item?.value || null, city: null, sedeId: null }))}
              isClearable
              placeholder="Todos"
              classNamePrefix="inventory-dashboard-select"
            />
          </div>
          <div>
            <span>Ciudad</span>
            <Select
              styles={selectStyles}
              menuPortalTarget={document.body}
              options={cityOptions}
              value={selectedCity}
              onChange={(item) => setFilters((current) => ({ ...current, city: item?.value || null, sedeId: null }))}
              isClearable
              placeholder="Todas"
              classNamePrefix="inventory-dashboard-select"
            />
          </div>
          <div>
            <span>Sede</span>
            <Select
              styles={selectStyles}
              menuPortalTarget={document.body}
              options={sedeOptions}
              value={selectedSede}
              onChange={(item) => setFilters((current) => ({ ...current, sedeId: item?.value || null }))}
              isClearable
              placeholder="Todas"
              classNamePrefix="inventory-dashboard-select"
            />
          </div>
          {hasFilters && (
            <button type="button" onClick={() => setFilters({ department: null, city: null, sedeId: null })}>
              Limpiar filtros
            </button>
          )}
        </div>
      </section>

      <DashboardIntegrityBanner integrity={integrity} filters={filters} />

      <section className="inventory-dashboard-section">
        <DashboardSectionTitle
          eyebrow={isSingleDay ? "MOVIMIENTO DEL DÍA" : "MOVIMIENTO DEL PERÍODO"}
          title="Stock inicial, movimientos y stock resultante"
          subtitle={`${periodLabel} · El stock inicial se reconstruye desde el kardex antes de comenzar el período.`}
        />
        <div className="inventory-dashboard-kpis inventory-dashboard-kpis-flow">
          <DashboardKpi title="Stock inicial en sedes" value={num(period.openingStock)} detail="Existencia operativa al inicio del período" />
          <DashboardKpi title="Ingresos" value={`+${num(period.entryUnits)}`} detail={`Costo ingresado ${money(period.entryCost)}`} />
          <DashboardKpi title="Traslados recibidos" value={`+${num(period.transferInUnits)}`} detail="Entradas desde otras sedes" />
          <DashboardKpi title="Vendidos" value={`-${num(period.soldUnits)}`} detail={`Venta realizada ${money(period.soldRevenue)}`} emphasis />
          <DashboardKpi title="Traslados enviados" value={`-${num(period.transferOutUnits)}`} detail="Salidas hacia otras ubicaciones" />
          <DashboardKpi title="Devoluciones" value={`+${num(period.returnUnits)}`} detail="Retornos de clientes / reversas" />
          <DashboardKpi title="Stock al cierre en sedes" value={num(period.closingStock)} detail={period.isCurrentPeriod ? "Resultado del período hasta hoy" : "Stock reconstruido al cierre histórico"} />
          <DashboardKpi title="Stock actual en sedes" value={num(s.operationalCurrentStock)} detail="Existencia operativa actual; tránsito se muestra aparte" />
        </div>
      </section>

      <section className="inventory-dashboard-section inventory-dashboard-period">
        <DashboardSectionTitle eyebrow="RESULTADO COMERCIAL" title="Ventas y rentabilidad realizadas" subtitle={periodLabel} />
        <div className="inventory-dashboard-kpis inventory-dashboard-kpis-period">
          <DashboardKpi title="Equipos vendidos" value={num(period.soldUnits)} detail="Ventas finalizadas por IMEI" />
          <DashboardKpi title="Monto vendido" value={money(period.soldRevenue)} detail="Ingreso comercial realizado" />
          <DashboardKpi title="Costo vendido" value={money(period.soldCost)} detail="Costo de compra de los vendidos" />
          <DashboardKpi title="Ganancia realizada" value={money(period.realizedProfit)} detail={`Margen ${pct(period.realizedMargin)}`} emphasis />
        </div>
      </section>

      <section className="inventory-dashboard-section">
        <DashboardSectionTitle eyebrow="ESTADO ACTUAL" title="Existencia y valorización" subtitle="Estos valores representan lo que existe ahora, no las ventas realizadas del período." />
        <div className="inventory-dashboard-kpis inventory-dashboard-kpis-stock">
          <DashboardKpi title="Existencia controlada" value={num(s.totalStock)} detail={`${num(s.totalAvailable)} disponibles`} />
          <DashboardKpi title="Reservado" value={num(s.totalReserved)} detail="Unidades comprometidas" />
          <DashboardKpi title="En tránsito" value={num(s.totalTransit)} detail="Unidades en traslado" />
          <DashboardKpi title="Valor de costo del stock" value={money(s.purchaseValue)} detail="Capital actual en inventario" />
          <DashboardKpi title="Valor potencial de venta" value={money(s.saleValue)} detail="Ingreso si se vende el stock actual" />
          <DashboardKpi title="Margen potencial" value={money(s.potentialProfit)} detail={`Margen ${pct(s.potentialMargin)}`} emphasis />
        </div>
      </section>

      <section className="inventory-dashboard-section">
        <DashboardSectionTitle eyebrow="JERARQUÍA TERRITORIAL" title="Rendimiento por Departamento, Ciudad y Sede" subtitle="Departamento proviene del UBIGEO; Ciudad operativa se deriva de la primera palabra de fp_sedes.sd_sede." />
        <div className="inventory-dashboard-grid inventory-dashboard-grid-hierarchy">
          <DashboardTable title="Por departamento" subtitle="Nivel territorial principal" rows={value.stockVsSalesByDepartment} columns={[
            ["departmentName", "Departamento"], ["stockTotalStock", "Stock actual"], ["salesUnits", "Vendidos"], ["stockPurchaseValue", "Valor costo", "money"], ["salesRevenue", "Venta realizada", "money"], ["salesProfit", "Ganancia", "money"]
          ]} />
          <DashboardTable title="Por ciudad" subtitle="Agrupación dinámica por la primera palabra de la sede" rows={value.stockVsSalesByCity} columns={[
            ["city", "Ciudad"], ["stockTotalStock", "Stock actual"], ["salesUnits", "Vendidos"], ["stockPurchaseValue", "Valor costo", "money"], ["salesRevenue", "Venta realizada", "money"], ["salesProfit", "Ganancia", "money"]
          ]} />
        </div>
      </section>

      <section className="inventory-dashboard-section">
        <DashboardSectionTitle eyebrow="CONCILIACIÓN" title="Movimiento de inventario por sede" subtitle="Permite ver con cuánto comenzó cada sede, qué recibió, qué vendió, qué envió y con cuánto cerró." />
        <DashboardReconciliationTable rows={reconciliation} />
      </section>

      <section className="inventory-dashboard-section">
        <DashboardSectionTitle eyebrow="DETALLE DE SEDES" title="Stock actual y valorización por establecimiento" subtitle="La columna Distrito es solo referencia geográfica." />
        <DashboardTable title="Stock actual por sede" rows={value.stockBySede} columns={[
          ["sedeName", "Sede"], ["departmentName", "Departamento"], ["city", "Ciudad"], ["districtName", "Distrito"], ["totalStock", "Existencia"], ["available", "Disponible"], ["reserved", "Reservado"], ["purchaseValue", "Valor costo", "money"], ["saleValue", "Venta potencial", "money"], ["potentialProfit", "Margen potencial", "money"]
        ]} />
      </section>

      <div className="inventory-dashboard-grid inventory-dashboard-grid-wide">
        <DashboardTable title="Evolución diaria" subtitle="Ingresos, ventas y utilidad por día" rows={value.dailyFlow} columns={[
          ["date", "Fecha", "date"], ["entryUnits", "Ingresos"], ["soldUnits", "Vendidos"], ["soldRevenue", "Venta realizada", "money"], ["realizedProfit", "Ganancia", "money"]
        ]} />
        <DashboardTable title="Productos más vendidos" subtitle="Ranking del período seleccionado" rows={value.topProducts} columns={[
          ["title", "Producto"], ["units", "Unidades"], ["revenue", "Venta", "money"], ["profit", "Ganancia", "money"]
        ]} />
        <DashboardTable title="Stock con baja rotación" subtitle="Productos que requieren atención comercial" rows={value.slowMoving} columns={[
          ["title", "Producto"], ["stock", "Stock"], ["lastSaleAt", "Última venta", "datetime"]
        ]} />
      </div>
    </div>
  );
}

function DashboardHierarchyValidation({ validation = {} }) {
  const issues = validation.issues || [];
  if (validation.valid !== false) {
    return (
      <section className="inventory-dashboard-hierarchy-validation ok">
        <div className="inventory-dashboard-integrity-icon">✓</div>
        <div>
          <strong>Convención de sedes válida</strong>
          <span>La ciudad operativa se obtiene automáticamente de la primera palabra de cada sede activa.</span>
        </div>
      </section>
    );
  }

  return (
    <section className="inventory-dashboard-hierarchy-validation warning">
      <div className="inventory-dashboard-integrity-icon">!</div>
      <div>
        <strong>Revisar nombres de sede</strong>
        <span>{Number(validation.issueCount || issues.length)} sede(s) comienzan con una palabra genérica y podrían agruparse en una ciudad incorrecta.</span>
        <div className="inventory-dashboard-hierarchy-issues">
          {issues.slice(0, 6).map((item) => (
            <small key={`hierarchy-issue-${item.sedeId}`}>
              {item.sedeName} → ciudad calculada: <b>{item.city}</b>
            </small>
          ))}
          {issues.length > 6 && <small>+{issues.length - 6} sede(s) adicionales por revisar.</small>}
        </div>
      </div>
    </section>
  );
}

function DashboardIntegrityBanner({ integrity = {}, filters = {} }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const load = async () => {
    setLoading(true); setError("");
    try {
      const response = await inventoryApi.dashboardKardexDifferences(filters);
      setItems(response?.data?.items || []);
    } catch (e) { setError(e?.response?.data?.error || "No se pudo consultar el detalle del Kardex."); }
    finally { setLoading(false); }
  };
  const visible = items.filter(r => `${r.sedeName} ${r.title} ${r.sku} ${r.imei}`.toLowerCase().includes(query.toLowerCase()));
  const ok = integrity.ok !== false;
  return (
    <section className={`inventory-dashboard-integrity ${ok ? "ok" : "warning"}`}>
      <div className="inventory-dashboard-integrity-icon">{ok ? "✓" : "!"}</div>
      <div>
        <strong>{ok ? "Kardex conciliado" : "Revisar conciliación del kardex"}</strong>
        <span>
          {ok
            ? "La existencia actual de las sedes coincide con el stock reconstruido desde los movimientos registrados."
            : `${Number(integrity.differenceCount || 0)} sede(s) presentan diferencia; variación absoluta: ${Number(integrity.absoluteDifference || 0)} unidad(es).`}
        </span>
      </div>
      {!ok && <button type="button" className="inventory-kardex-view-button" onClick={() => { setOpen(true); load(); }}>Ver diferencias</button>}
      {open && <div className="inventory-kardex-overlay" role="presentation" onMouseDown={e => { if (e.target === e.currentTarget) setOpen(false); }}>
        <section className="inventory-kardex-modal" role="dialog" aria-modal="true" aria-label="Diferencias de conciliación del Kardex">
          <header className="inventory-kardex-modal-head">
            <div><small>CONTROL DE INVENTARIO</small><h2>Diferencias de conciliación del Kardex</h2><p>Diagnóstico de solo lectura por sede y producto. No modifica existencias.</p></div>
            <button type="button" onClick={() => setOpen(false)} aria-label="Cerrar">✕</button>
          </header>
          <div className="inventory-kardex-modal-content">
            <div className="inventory-kardex-metrics"><div><small>Sedes afectadas</small><strong>{integrity.differenceCount || 0}</strong></div><div><small>Variación absoluta por sede</small><strong>{integrity.absoluteDifference || 0}</strong></div><div><small>Filas con diferencia</small><strong>{items.length}</strong></div></div>
            <div className="inventory-kardex-tools"><input aria-label="Buscar producto, sede o IMEI" placeholder="Buscar sede, producto, SKU o IMEI..." value={query} onChange={e => setQuery(e.target.value)} /><button type="button" onClick={load} disabled={loading}>{loading ? "Consultando..." : "Actualizar diagnóstico"}</button></div>
            {error && <p className="inventory-kardex-error" role="alert">{error}</p>}
            <div className="inventory-kardex-table-wrap"><table><thead><tr><th>Sede</th><th>Producto / SKU / IMEI</th><th>Stock sistema</th><th>Stock Kardex</th><th>Diferencia</th><th>Revisión sugerida</th></tr></thead><tbody>
              {visible.map((r, i) => <tr key={`${r.sedeId}-${r.productId}-${r.variantId}-${i}`}><td>{r.sedeName}</td><td><strong>{r.title}</strong><small>SKU: {r.sku || "—"} · IMEI: {r.imei || "No aplica"}</small></td><td>{Number(r.systemStock)}</td><td>{Number(r.ledgerStock)}</td><td className="inventory-kardex-diff">{Number(r.difference)>0?"+":""}{Number(r.difference)}</td><td>{Number(r.difference)>0 ? "Revisar ingresos, devoluciones y movimientos no registrados." : "Revisar ventas, traslados, anulaciones y salidas duplicadas."}</td></tr>)}
              {!loading && !visible.length && <tr><td colSpan={6}>{error ? "Consulta no disponible." : "No se encontraron diferencias por producto con los criterios actuales."}</td></tr>}
            </tbody></table></div>
            <p className="inventory-kardex-footnote">La variación absoluta por sede puede diferir de la suma de diferencias por producto. Verifica el historial de movimientos antes de realizar cualquier regularización. Este reporte no ajusta stock.</p>
          </div>
        </section>
      </div>}
    </section>
  );
}

function DashboardSectionTitle({ eyebrow, title, subtitle }) {
  return (
    <header className="inventory-dashboard-section-title">
      <span>{eyebrow}</span>
      <h3>{title}</h3>
      {subtitle && <p>{subtitle}</p>}
    </header>
  );
}

function DashboardKpi({ title, value, detail, emphasis = false }) {
  return (
    <article className={`inventory-dashboard-kpi${emphasis ? " emphasis" : ""}`}>
      <span>{title}</span>
      <strong>{value ?? 0}</strong>
      <small>{detail}</small>
    </article>
  );
}

function DashboardTable({ title, subtitle, rows = [], columns = [] }) {
  const format = (value, type) => {
    if (type === "money") return money(value);
    if (type === "date") return formatInventoryDate(value);
    if (type === "datetime") return value ? formatInventoryDate(value) : "Sin ventas";
    return value ?? "—";
  };
  return (
    <article className="inventory-dashboard-table-card">
      <header>
        <div>
          <h3>{title}</h3>
          {subtitle && <small>{subtitle}</small>}
        </div>
        <span>{(rows || []).length} filas</span>
      </header>
      <div className="inventory-dashboard-table-scroll">
        <table>
          <thead><tr>{columns.map(([key, label]) => <th key={key}>{label}</th>)}</tr></thead>
          <tbody>
            {(rows || []).map((row, index) => (
              <tr key={`${title}-${row.id || row.sedeId || row.productId || row.departmentName || row.city || row.date || index}`}>
                {columns.map(([key, _label, type]) => <td key={key}>{format(row?.[key], type)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!rows?.length && <Empty />}
    </article>
  );
}

function DashboardReconciliationTable({ rows = [] }) {
  const num = (v) => Number(v || 0).toLocaleString("es-PE");
  return (
    <article className="inventory-dashboard-table-card inventory-dashboard-reconciliation-card">
      <header>
        <div>
          <h3>Conciliación por sede</h3>
          <small>Stock inicial + entradas − salidas = stock al cierre del período.</small>
        </div>
        <span>{rows.length} sedes</span>
      </header>
      <div className="inventory-dashboard-table-scroll">
        <table>
          <thead>
            <tr>
              <th>Sede</th><th>Inicial</th><th>Ingresos</th><th>Recibidos</th><th>Vendidos</th><th>Enviados</th><th>Devol.</th><th>Otros</th><th>Cierre</th><th>Actual</th><th>Integridad</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={`reconciliation-${row.sedeId}`}>
                <td>
                  <strong>{row.sedeName}</strong>
                  <small className="inventory-dashboard-cell-meta">{row.departmentName} · {row.city}{row.districtName ? ` · ${row.districtName}` : ""}</small>
                </td>
                <td>{num(row.openingStock)}</td>
                <td className="inventory-dashboard-positive">+{num(row.entries)}</td>
                <td className="inventory-dashboard-positive">+{num(row.transferIn)}</td>
                <td className="inventory-dashboard-negative">-{num(row.sold)}</td>
                <td className="inventory-dashboard-negative">-{num(row.transferOut)}</td>
                <td className="inventory-dashboard-positive">+{num(row.returns)}</td>
                <td>{num(Number(row.adjustmentIn || 0) - Number(row.otherOut || 0))}</td>
                <td><strong>{num(row.closingStock)}</strong></td>
                <td><strong>{num(row.currentStock)}</strong></td>
                <td>
                  <span className={`inventory-dashboard-status ${Number(row.integrityDifference || 0) === 0 ? "ok" : "warning"}`}>
                    {Number(row.integrityDifference || 0) === 0 ? "OK" : `${Number(row.integrityDifference) > 0 ? "+" : ""}${num(row.integrityDifference)}`}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!rows.length && <Empty />}
    </article>
  );
}
function Products({
  rows = [],
  canView,
  canEdit,
  canDelete,
  onView,
  onEdit,
  onDelete,
  pagination = {},
  pageSize,
  meta = {},
  productFilters = {},
  setProductFilters,
  onPageSizeChange,
  onPageChange,
}) {
  const [distributionProduct, setDistributionProduct] = useState(null);
  const currentPage = Number(pagination.page || 1);
  const totalPages = Math.max(1, Number(pagination.pages || 1));
  const total = Number(pagination.total || rows.length || 0);
  const numericLimit = pageSize === "all" ? total || 1 : Number(pageSize || 20);
  const rowNumberStart = pageSize === "all" ? 0 : (currentPage - 1) * numericLimit;
  const firstVisible = total ? rowNumberStart + 1 : 0;
  const lastVisible = total ? Math.min(rowNumberStart + rows.length, total) : 0;
  const supplierOptions = (meta.suppliers || []).map((supplier) => ({
    value: Number(supplier.id),
    label: supplier.business_name || supplier.name || `Proveedor ${supplier.id}`,
    raw: supplier,
  }));
  const rucOptions = (meta.rucs || []).map((ruc) => ({
    value: Number(ruc.id),
    label: `${ruc.business_name || ruc.alias || "Empresa"}${ruc.ruc ? ` · ${ruc.ruc}` : ""}`,
    raw: ruc,
  }));
  const selectedSupplier =
    supplierOptions.find(
      (item) => Number(item.value) === Number(productFilters.supplierId),
    ) || null;
  const selectedRuc =
    rucOptions.find((item) => Number(item.value) === Number(productFilters.rucId)) ||
    null;
  const stockStatusOptions = [
    { value: "AVAILABLE", label: "Disponible" },
    { value: "SOLD", label: "Vendido" },
  ];
  const selectedStockStatus =
    stockStatusOptions.find((item) => item.value === productFilters.stockStatus) || null;
  const patchListFilters = (patch) => {
    if (!setProductFilters) return;
    setProductFilters((current) => ({ ...current, ...patch }));
  };

  return (
    <div className="inventory-products-list">
      <div className="inventory-list-controls">
        <div className="inventory-list-summary" aria-live="polite">
          <strong>{total}</strong>
          <span>{total === 1 ? "producto encontrado" : "productos encontrados"}</span>
          <small>
            Mostrando {firstVisible}–{lastVisible}
          </small>
        </div>

        <div className="inventory-list-filter-group">
          <div className="inventory-list-filter inventory-list-filter-stock">
            <span>Stock</span>
            <Select
              styles={selectStyles}
              menuPortalTarget={document.body}
              options={stockStatusOptions}
              value={selectedStockStatus}
              onChange={(item) =>
                patchListFilters({ stockStatus: item?.value || null })
              }
              isClearable
              placeholder="Todos"
              noOptionsMessage={() => "Sin estados"}
              classNamePrefix="inventory-list-select"
            />
          </div>

          <div className="inventory-list-filter">
            <span>Proveedor</span>
            <Select
              styles={selectStyles}
              menuPortalTarget={document.body}
              options={supplierOptions}
              value={selectedSupplier}
              onChange={(item) =>
                patchListFilters({ supplierId: item?.value || null })
              }
              isClearable
              placeholder="Todos"
              noOptionsMessage={() => "Sin proveedores"}
              classNamePrefix="inventory-list-select"
            />
          </div>

          <div className="inventory-list-filter inventory-list-filter-ruc">
            <span>RUC de la empresa</span>
            <Select
              styles={selectStyles}
              menuPortalTarget={document.body}
              options={rucOptions}
              value={selectedRuc}
              onChange={(item) => patchListFilters({ rucId: item?.value || null })}
              isClearable
              placeholder="Todos"
              noOptionsMessage={() => "Sin RUC"}
              classNamePrefix="inventory-list-select"
            />
          </div>

          <label className="inventory-page-size">
            <span>Mostrar</span>
            <select
            value={pageSize}
            onChange={(event) => {
              const value =
                event.target.value === "all"
                  ? "all"
                  : Number(event.target.value);
              onPageSizeChange(value);
            }}
          >
            <option value={20}>20 productos</option>
            <option value={50}>50 productos</option>
            <option value={100}>100 productos</option>
            <option value="all">Todos los productos</option>
            </select>
          </label>
        </div>
      </div>

      <Table
        showRowNumber
        rowNumberStart={rowNumberStart}
        columns={[
          "title",
          "categoryName",
          "brandName",
          "imei",
          "invoice_number",
          "rucDisplay",
          "productLocationName",
          "purchase_price",
          "sale_price",
          "stockAvailable",
          "createdByName",
          "updatedByName",
          "registered_at",
          "updated_at",
        ]}
        rows={rows}
        moneyCols={["purchase_price", "sale_price"]}
        renderCell={(column, product) => {
          if (column !== "productLocationName" || Number(product.is_serialized) === 1) return undefined;
          const count = Number(product.locationCount || 0);
          if (count === 0) return "Sin stock físico";
          if (count === 1) return product.productLocationName;
          return <div style={{display:"flex",flexDirection:"column",gap:5,alignItems:"center"}}>
            <span>{count} ubicaciones</span>
            <button style={{textDecoration: "underline"}} type="button" onClick={() => setDistributionProduct(product)}>Ver distribución</button>
          </div>;
        }}
        actions={
          canView || canEdit || canDelete
            ? (product) => (
                <>
                  {canView && !canEdit && (
                    <button onClick={() => onView(product)}>Ver</button>
                  )}
                  {canEdit && (
                    <button onClick={() => onEdit(product)}>Editar</button>
                  )}
                  {canDelete && (
                    <button className="danger" onClick={() => onDelete(product)}>
                      Eliminar
                    </button>
                  )}
                </>
              )
            : null
        }
      />

      {distributionProduct && (
        <div role="presentation" style={{position:"fixed",inset:0,background:"rgba(15,23,42,.55)",zIndex:11000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onMouseDown={(event)=>{if(event.target===event.currentTarget)setDistributionProduct(null);}}>
          <section role="dialog" aria-modal="true" aria-label="Distribución de stock" style={{background:"white",borderRadius:16,padding:24,width:"min(100%,560px)",maxHeight:"85vh",overflowY:"auto"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12}}>
              <h3 style={{margin:0}}>Distribución de stock</h3>
              <button type="button" onClick={()=>setDistributionProduct(null)}>Cerrar</button>
            </div>
            <p>{distributionProduct.title}</p>
            <table style={{width:"100%"}}><thead><tr><th>Ubicación / sede</th><th>Disponible</th><th>Reservado</th></tr></thead>
              <tbody>{(distributionProduct.stockDistribution || []).map((item)=><tr key={item.locationId}>
                <td>{item.locationName}</td><td>{Number(item.available||0)}</td><td>{Number(item.reserved||0)}</td>
              </tr>)}</tbody>
            </table>
            <p><strong>Stock disponible según filtros: {Number(distributionProduct.stockAvailable||0)}</strong></p>
          </section>
        </div>
      )}
      {pageSize !== "all" && totalPages > 1 && (
        <Pagination
          page={currentPage}
          pages={totalPages}
          onChange={onPageChange}
        />
      )}
    </div>
  );
}


function ProductReadOnlyModal({ product, close }) {
  const [detail, setDetail] = useState(product || {});
  const [loadingDetail, setLoadingDetail] = useState(Boolean(product?.id));

  useEffect(() => {
    let active = true;
    if (!product?.id) {
      setLoadingDetail(false);
      return () => { active = false; };
    }

    inventoryApi.product(product.id)
      .then((response) => {
        if (!active) return;
        setDetail({ ...(product || {}), ...(unwrap(response) || {}) });
      })
      .catch((error) => {
        if (!active) return;
        toast.error(error.response?.data?.error || "No se pudo consultar el producto.");
      })
      .finally(() => {
        if (active) setLoadingDetail(false);
      });

    return () => { active = false; };
  }, [product]);

  const value = (v) => (v === null || v === undefined || v === "" ? "—" : v);
  const status = Number(detail.is_active ?? 1) === 1 ? "Activo" : "Inactivo";
  const rucText = [detail.rucName, detail.ruc].filter(Boolean).join(" · ") || "—";

  const items = [
    ["Producto", detail.title],
    ["SKU", detail.sku],
    ["Categoría", detail.categoryName],
    ["Marca", detail.brandName],
    ["Modelo", detail.modelName],
    ["IMEI 1", detail.imei],
    ["IMEI 2", detail.imei2],
    ["N. Factura", detail.invoice_number],
    ["RUC / empresa", rucText],
    ["Proveedor", detail.supplierName],
    ["Precio compra", money(detail.purchase_price)],
    ["Precio venta", money(detail.sale_price)],
    ["Stock disponible", detail.stockAvailable],
    ["Stock reservado", detail.stockReserved],
    ["En tránsito", detail.stockInTransit],
    ["Afectación IGV", detail.igv_affectation_code],
    ["Unidad", detail.unit_code],
    ["Estado", status],
  ];

  return (
    <div className="inventory-modal">
      <div className="backdrop" onClick={close} />
      <section className="inventory-product-modal">
        <header>
          <div>
            <small>CONSULTA DE PRODUCTO</small>
            <h2>Detalle del producto</h2>
            <p>Vista de solo lectura. No permite modificar ni eliminar información.</p>
          </div>
          <button type="button" onClick={close} aria-label="Cerrar">×</button>
        </header>

        <div className="inventory-form inventory-product-form">
          {loadingDetail ? (
            <div className="inventory-empty">Consultando información actualizada...</div>
          ) : (
            <>
              {items.map(([label, raw], index) => (
                <Field key={`${label}-${index}`} wide={label === "Producto"} label={label}>
                  <input
                    type="text"
                    value={value(raw)}
                    readOnly
                    disabled
                    aria-label={label}
                  />
                </Field>
              ))}
              <Field wide label="Descripción">
                <textarea value={value(detail.description)} readOnly disabled />
              </Field>
            </>
          )}
        </div>

        <footer className="inventory-modal-footer">
          <button
            type="button"
            className="modal-button modal-button-secondary"
            onClick={close}
          >
            Cerrar
          </button>
        </footer>
      </section>
    </div>
  );
}

function Pagination({ page, pages, onChange, ariaLabel = "Paginación" }) {
  const visiblePages = [];
  const start = Math.max(1, Math.min(page - 2, pages - 4));
  const end = Math.min(pages, start + 4);

  for (let value = start; value <= end; value += 1) {
    visiblePages.push(value);
  }

  return (
    <nav className="inventory-pagination" aria-label={ariaLabel}>
      <button
        type="button"
        onClick={() => onChange(1)}
        disabled={page === 1}
        aria-label="Ir a la primera página"
      >
        «
      </button>
      <button
        type="button"
        onClick={() => onChange(page - 1)}
        disabled={page === 1}
      >
        Anterior
      </button>

      <div className="inventory-pagination-pages">
        {visiblePages.map((value) => (
          <button
            type="button"
            key={value}
            className={value === page ? "active" : ""}
            onClick={() => onChange(value)}
            aria-current={value === page ? "page" : undefined}
          >
            {value}
          </button>
        ))}
      </div>

      <button
        type="button"
        onClick={() => onChange(page + 1)}
        disabled={page === pages}
      >
        Siguiente
      </button>
      <button
        type="button"
        onClick={() => onChange(pages)}
        disabled={page === pages}
        aria-label="Ir a la última página"
      >
        »
      </button>
      <span className="inventory-pagination-status">
        Página {page} de {pages}
      </span>
    </nav>
  );
}

function Stock({
  rows = [],
  pagination = {},
  pageSize,
  onPageSizeChange,
  onPageChange,
}) {
  const currentPage = Number(pagination.page || 1);
  const totalPages = Math.max(1, Number(pagination.pages || 1));
  const total = Number(pagination.total || rows.length || 0);
  const numericLimit = pageSize === "all" ? total || 1 : Number(pageSize || 20);
  const rowNumberStart = pageSize === "all" ? 0 : (currentPage - 1) * numericLimit;
  const firstVisible = total ? rowNumberStart + 1 : 0;
  const lastVisible = total ? Math.min(rowNumberStart + rows.length, total) : 0;

  return (
    <div className="inventory-stock-list">
      <div className="inventory-list-controls">
        <div className="inventory-list-summary" aria-live="polite">
          <strong>{total}</strong>
          <span>{total === 1 ? "registro de stock" : "registros de stock"}</span>
          <small>
            Mostrando {firstVisible}–{lastVisible}
          </small>
        </div>

        <label className="inventory-page-size">
          <span>Mostrar</span>
          <select
            value={pageSize}
            onChange={(event) => {
              const value =
                event.target.value === "all"
                  ? "all"
                  : Number(event.target.value);
              onPageSizeChange(value);
            }}
          >
            <option value={20}>20 registros</option>
            <option value={50}>50 registros</option>
            <option value={100}>100 registros</option>
            <option value="all">Todos los registros</option>
          </select>
        </label>
      </div>

      <Table
        showRowNumber
        rowNumberStart={rowNumberStart}
        columns={[
          "title",
          "sku",
          "categoryName",
          "brandName",
          "variantName",
          "locationName",
          "city",
          "quantity_available",
          "quantity_reserved",
          "quantity_in_transit",
        ]}
        rows={rows}
      />

      {pageSize !== "all" && totalPages > 1 && (
        <Pagination
          page={currentPage}
          pages={totalPages}
          onChange={onPageChange}
          ariaLabel="Paginación de stock global"
        />
      )}
    </div>
  );
}

const locationTypeLabel = (value) =>
  ({
    CENTRAL_WAREHOUSE: "Almacén central",
    BRANCH: "Sede / tienda",
    TRANSIT: "En tránsito",
    SUPPLIER: "Proveedor",
    DAMAGED: "Dañados",
    RETURNED: "Devoluciones",
  })[String(value || "").toUpperCase()] || value || "—";

const establishmentTypeLabel = (value) =>
  ({
    STORE: "Tienda",
    WAREHOUSE: "Almacén",
    OFFICE: "Oficina",
    OTHER: "Otro",
  })[String(value || "").toUpperCase()] || value || "—";

const locationStatusLabel = (value) =>
  ({
    CONFIGURED: "Configurado",
    SYSTEM: "Sistema",
    MISSING: "Sin establecimiento",
    MISSING_CITY: "Falta ciudad",
    INVALID: "Vínculo inválido",
    INVALID_TYPE: "Tipo inválido",
    INACTIVE: "Establecimiento inactivo",
    MISSING_ADDRESS: "Falta dirección",
    MISSING_UBIGEO: "Falta UBIGEO",
    MISSING_CODE: "Falta código",
  })[String(value || "").toUpperCase()] || value || "—";

function Locations({ rows = [], canManage, onEdit, onDelete }) {
  return (
    <div className="inventory-locations">
      <div className="inventory-locations-intro">
        <div>
          <span>MAESTRO LOGÍSTICO</span>
          <h3>Ubicaciones y establecimientos</h3>
          <p>
            Vincula cada ubicación física con su establecimiento real. El sistema
            admite un Almacén Central activo por ciudad operativa, por ejemplo Pucallpa
            e Iquitos. Dirección, UBIGEO y código se toman desde Sedes y se reutilizan
            automáticamente en traslados y GRE SUNAT.
          </p>
        </div>
        <div className="inventory-location-legend">
          <small>Fuente tributaria</small>
          <strong>fp_sedes</strong>
        </div>
      </div>

      <div className="inventory-table-wrap inventory-location-table">
        <table>
          <thead>
            <tr>
              <th>Ubicación</th>
              <th>Tipo</th>
              <th>Establecimiento</th>
              <th>Tipo establecimiento</th>
              <th>Ciudad</th>
              <th>UBIGEO</th>
              <th>Código</th>
              <th>Estado</th>
              {canManage && <th>Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {(rows || []).map((row) => (
              <tr key={row.id}>
                <td>
                  <strong>{row.name}</strong>
                  {Number(row.is_central) === 1 && <small>Almacén central{row.city ? ` · ${row.city}` : ""}</small>}
                </td>
                <td>{locationTypeLabel(row.type)}</td>
                <td>
                  <strong>{row.sedeName || "—"}</strong>
                  {row.sedeAddress && <small>{row.sedeAddress}</small>}
                </td>
                <td>{row.establishmentType ? establishmentTypeLabel(row.establishmentType) : "—"}</td>
                <td>{row.city || row.districtName || "—"}</td>
                <td>{row.sedeUbigeo || "—"}</td>
                <td>{row.sedeBranchCode || "—"}</td>
                <td>
                  <span
                    className={`inventory-location-status status-${String(
                      row.establishmentStatus || "",
                    ).toLowerCase()}`}
                  >
                    {locationStatusLabel(row.establishmentStatus)}
                  </span>
                </td>
                {canManage && (
                  <td className="row-actions">
                    {row.type !== "TRANSIT" && (
                      <>
                        <button type="button" onClick={() => onEdit(row)}>
                          Editar
                        </button>
                        <button
                          type="button"
                          className="danger"
                          onClick={() => onDelete(row)}
                        >
                          Eliminar
                        </button>
                      </>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {!rows.length && <Empty />}
      </div>
    </div>
  );
}

function Catalogs({ responses, canManage, onCreate, onEdit, onDelete }) {
  const cats = unwrap(responses?.[0]);
  const brands = unwrap(responses?.[1]);
  const models = unwrap(responses?.[2]);

  const actions = (type) =>
    canManage
      ? (record) => (
          <>
            <button onClick={() => onEdit(type, record)}>Editar</button>
            <button className="danger" onClick={() => onDelete(type, record)}>
              Eliminar
            </button>
          </>
        )
      : null;

  return (
    <div className="inventory-grid catalog-grid">
      <article className="inventory-card catalog-card">
        <div className="catalog-heading">
          <div>
            <span className="catalog-eyebrow">CLASIFICACIÓN</span>
            <h3>Categorías</h3>
            <p>Define el tipo de producto y si requiere control por IMEI o serie.</p>
          </div>
          {canManage && (
            <button
              type="button"
              className="catalog-create-button catalog-create-button-category"
              onClick={() => onCreate("category")}
            >
              <span className="catalog-create-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <path d="M4 5.5h6v6H4z" />
                  <path d="M14 5.5h6v6h-6z" />
                  <path d="M4 15.5h6v3H4z" />
                  <path d="M14 15.5h6v3h-6z" />
                </svg>
              </span>
              <span className="catalog-create-copy">
                <strong>Nueva categoría</strong>
                <small>Clasificar productos</small>
              </span>
              <span className="catalog-create-arrow">→</span>
            </button>
          )}
        </div>
        <Table
          columns={["name", "slug", "is_serialized", "is_active"]}
          rows={cats}
          actions={actions("category")}
        />
      </article>

      <article className="inventory-card catalog-card">
        <div className="catalog-heading">
          <div>
            <span className="catalog-eyebrow">FABRICANTE</span>
            <h3>Marcas</h3>
            <p>Crea y administra las marcas disponibles al registrar productos.</p>
          </div>
          {canManage && (
            <button
              type="button"
              className="catalog-create-button catalog-create-button-brand"
              onClick={() => onCreate("brand")}
            >
              <span className="catalog-create-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <path d="M4 7.5 12 3l8 4.5v9L12 21l-8-4.5z" />
                  <path d="m4 7.5 8 4.5 8-4.5" />
                  <path d="M12 12v9" />
                </svg>
              </span>
              <span className="catalog-create-copy">
                <strong>Nueva marca</strong>
                <small>Registrar fabricante</small>
              </span>
              <span className="catalog-create-arrow">→</span>
            </button>
          )}
        </div>
        <Table
          columns={["name", "slug", "description", "is_active"]}
          rows={brands}
          actions={actions("brand")}
        />
      </article>

      <article className="inventory-card catalog-card catalog-card-models">
        <div className="catalog-heading">
          <div>
            <span className="catalog-eyebrow">REFERENCIA COMERCIAL</span>
            <h3>Modelos</h3>
            <p>Relaciona cada modelo con su categoría y marca para evitar registros inconsistentes.</p>
          </div>
          {canManage && (
            <button
              type="button"
              className="catalog-create-button catalog-create-button-model"
              onClick={() => onCreate("model")}
            >
              <span className="catalog-create-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <path d="M7 3.5h10a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-13a2 2 0 0 1 2-2Z" />
                  <path d="M9 7h6" />
                  <path d="M9 11h6" />
                  <path d="M9 15h3" />
                </svg>
              </span>
              <span className="catalog-create-copy">
                <strong>Nuevo modelo</strong>
                <small>Vincular categoría y marca</small>
              </span>
              <span className="catalog-create-arrow">→</span>
            </button>
          )}
        </div>
        <Table
          columns={[
            "name",
            "model_code",
            "categoryName",
            "brandName",
            "description",
            "is_active",
          ]}
          rows={models}
          actions={actions("model")}
        />
      </article>
    </div>
  );
}
function ElectronicSequenceManager() {
  const [data, setData] = useState({ rucs: [], sedes: [], departments: [], sequences: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    kind: "DOCUMENT",
    issuerRucId: "",
    sedeId: "",
    departmentCode: "",
    documentType: "03",
    series: "BA25",
    nextCorrelative: 1,
    environment: "PRODUCTION",
  });

  const load = async () => {
    try {
      setLoading(true);
      const response = await electronicDocumentsApi.sequences();
      setData(response || { rucs: [], sedes: [], departments: [], sequences: [] });
    } catch (error) {
      toast.error(error.response?.data?.error || "No se pudo cargar la configuración de series y correlativos.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const selectSequence = (row) => {
    setForm({
      kind: row.kind,
      issuerRucId: String(row.issuer_ruc_id || ""),
      sedeId: String(row.sede_id || ""),
      departmentCode: String(row.department_code || ""),
      documentType: String(row.document_type || "03"),
      series: String(row.series || "").toUpperCase(),
      nextCorrelative: Number(row.safe_next_correlative || row.next_correlative || 1),
      environment: String(row.environment || "PRODUCTION"),
    });
  };

  const setKind = (kind) => {
    const documentType = "03";
    setForm((current) => ({
      ...current,
      kind,
      documentType,
      series: kind === "CREDIT_NOTE" ? "BC25" : "BA25",
    }));
  };

  const setDocumentType = (documentType) => {
    setForm((current) => {
      const dept = String(current.departmentCode || "25").padStart(2, "0").slice(-2);
      const series = current.kind === "CREDIT_NOTE"
        ? `${documentType === "01" ? "FC" : "BC"}${dept}`
        : `${documentType === "01" ? "FA" : "BA"}${dept}`;
      return { ...current, documentType, series };
    });
  };

  const setSede = (sedeId) => {
    const sede = (data.sedes || []).find((item) => String(item.sd_id) === String(sedeId));
    const dept = String(sede?.department_code || form.departmentCode || "").padStart(2, "0").slice(-2);
    setForm((current) => {
      const series = current.kind === "CREDIT_NOTE"
        ? `${current.documentType === "01" ? "FC" : "BC"}${dept}`
        : `${current.documentType === "01" ? "FA" : "BA"}${dept}`;
      return { ...current, sedeId, departmentCode: dept, series };
    });
  };

  const save = async () => {
    if (!form.issuerRucId || !form.sedeId || !form.series || !form.nextCorrelative) {
      toast.error("Completa RUC, sede, serie y próximo correlativo.");
      return;
    }
    const full = `${String(form.series).toUpperCase()}-${String(Number(form.nextCorrelative)).padStart(8, "0")}`;
    if (!window.confirm(`Se configurará el próximo comprobante como ${full}.\n\nEl sistema no permitirá retroceder a un correlativo ya utilizado. ¿Continuar?`)) return;

    try {
      setSaving(true);
      const result = await electronicDocumentsApi.configureSequence({
        ...form,
        issuerRucId: Number(form.issuerRucId),
        sedeId: Number(form.sedeId),
        nextCorrelative: Number(form.nextCorrelative),
      });
      toast.success(`Secuencia configurada. Próximo número: ${result.series}-${String(result.nextCorrelative).padStart(8, "0")}.`);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.error || "No se pudo guardar la secuencia.");
    } finally {
      setSaving(false);
    }
  };

  const rows = (data.sequences || []).filter((row) => row.environment === form.environment);

  return (
    <section className="electronic-sequence-manager">
      <div className="electronic-sequence-head">
        <div>
          <span>CONTROL DE NUMERACIÓN</span>
          <h4>Series y correlativos electrónicos</h4>
          <p>Define el punto de partida por RUC, departamento, tipo de documento y ambiente. El backend valida el mayor correlativo ya utilizado antes de reservar uno nuevo.</p>
        </div>
        <div className="electronic-sequence-badges">
          <span>Bloqueo transaccional</span><span>Anti-duplicados</span><span>Solo Admin</span>
        </div>
      </div>

      <div className="electronic-sequence-form">
        <label><span>Tipo</span><select value={form.kind} onChange={(e) => setKind(e.target.value)}><option value="DOCUMENT">Boleta / Factura</option><option value="CREDIT_NOTE">Nota de Crédito</option></select></label>
        <label><span>RUC emisor</span><select value={form.issuerRucId} onChange={(e) => setForm((c) => ({ ...c, issuerRucId: e.target.value }))}><option value="">Seleccionar...</option>{(data.rucs || []).map((r) => <option key={r.id} value={r.id}>{r.alias || r.business_name} · {r.ruc}</option>)}</select></label>
        <label><span>Sede de referencia</span><select value={form.sedeId} onChange={(e) => setSede(e.target.value)}><option value="">Seleccionar...</option>{(data.sedes || []).map((x) => <option key={x.sd_id} value={x.sd_id}>{x.sd_sede}</option>)}</select></label>
        <label><span>{form.kind === "CREDIT_NOTE" ? "Documento afectado" : "Documento"}</span><select value={form.documentType} onChange={(e) => setDocumentType(e.target.value)}><option value="03">03 · Boleta</option><option value="01">01 · Factura</option></select></label>
        <label><span>Ambiente</span><select value={form.environment} onChange={(e) => setForm((c) => ({ ...c, environment: e.target.value }))}><option value="PRODUCTION">PRODUCTION</option><option value="BETA">BETA</option></select></label>
        <label><span>Departamento</span><input value={form.departmentCode} maxLength={2} onChange={(e) => setForm((c) => ({ ...c, departmentCode: e.target.value.replace(/\D/g, "").slice(0, 2) }))} placeholder="25" /></label>
        <label><span>Serie</span><input value={form.series} maxLength={4} onChange={(e) => setForm((c) => ({ ...c, series: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 4) }))} placeholder="BA25" /></label>
        <label><span>Próximo correlativo</span><input type="number" min="1" step="1" value={form.nextCorrelative} onChange={(e) => setForm((c) => ({ ...c, nextCorrelative: e.target.value }))} /></label>
        <div className="electronic-sequence-preview"><small>PRÓXIMO NÚMERO</small><strong>{form.series || "----"}-{String(Number(form.nextCorrelative || 0)).padStart(8, "0")}</strong></div>
        <button type="button" className="electronic-sequence-save" disabled={saving} onClick={save}>{saving ? "Guardando..." : "Guardar punto de partida"}</button>
      </div>

      <div className="electronic-sequence-table-wrap">
        {loading ? <div className="electronic-sequence-loading">Cargando secuencias...</div> : (
          <table className="electronic-sequence-table">
            <thead><tr><th>Clase</th><th>RUC</th><th>Tipo</th><th>Serie</th><th>Usado</th><th>Contador</th><th>Próximo seguro</th><th>Estado</th><th></th></tr></thead>
            <tbody>{rows.map((row) => <tr key={`${row.kind}-${row.id}`}>
              <td>{row.kind === "CREDIT_NOTE" ? "Nota de Crédito" : "Comprobante"}</td>
              <td>{row.issuer_name}</td><td>{row.document_type}</td><td><strong>{row.series}</strong></td>
              <td>{String(Number(row.used_max || 0)).padStart(8, "0")}</td><td>{String(Number(row.current_correlative || 0)).padStart(8, "0")}</td>
              <td><strong>{String(Number(row.safe_next_correlative || 1)).padStart(8, "0")}</strong></td>
              <td><span className={`sequence-status ${row.synchronized ? "ok" : "warning"}`}>{row.synchronized ? "Sincronizado" : "Reconciliar"}</span></td>
              <td><button type="button" onClick={() => selectSequence(row)}>Configurar</button></td>
            </tr>)}</tbody>
          </table>
        )}
      </div>
    </section>
  );
}

function SaleReceipts({
  rows = [],
  pagination = {},
  filterOptions = {},
  pageSize = 50,
  filters = {},
  search = "",
  setSearch,
  dateFilter = { mode: "single", dateFrom: "", dateTo: "" },
  setDateFilter,
  setFilters,
  onPageSizeChange,
  onPageChange,
  reload: reloadParent,
}) {
  const auth = useAuth();
  const isAdmin = String(auth.rol || "") === "1";
  const [busyId, setBusyId] = useState(null);
  const [localRows, setLocalRows] = useState(rows);
  const [observationModal, setObservationModal] = useState(null);
  const [creditNoteModal, setCreditNoteModal] = useState(null);
  const [ncGiftInfo, setNcGiftInfo] = useState(null);
  const [ncGiftSelected, setNcGiftSelected] = useState([]);
  const [ncGiftConfirmed, setNcGiftConfirmed] = useState(false);
  const [showSequenceManager, setShowSequenceManager] = useState(false);

  useEffect(() => setLocalRows(rows || []), [rows]);

  const reload = async () => {
    if (reloadParent) {
      await reloadParent();
      return;
    }
    const response = await electronicDocumentsApi.list({
      rucId: filters.rucId || undefined,
      sedeId: filters.sedeId || undefined,
      advisorId: filters.advisorId || undefined,
      page: pagination.page || 1,
      limit: pageSize === "all" ? "all" : pageSize,
    });
    const payload = unwrap(response);
    setLocalRows(payload?.data || payload || []);
  };

  const download = async (receipt, type) => {
    try {
      setBusyId(receipt.id);
      const config = {
        pdf: { prefix: "", extension: "pdf" },
        ticket: { prefix: "", suffix: "-ticket", extension: "pdf" },
        xml: { prefix: "", extension: "xml" },
        zip: { prefix: "", extension: "zip" },
        cdr: { prefix: "R-", extension: "zip" },
        "cdr-xml": { prefix: "R-", extension: "xml" },
      }[type];
      if (!config) throw new Error("Tipo de archivo inválido.");
      await electronicDocumentsApi.download(
        receipt.id,
        type,
        `${config.prefix}${receipt.full_number}${config.suffix || ""}.${config.extension}`,
      );
    } catch (error) {
      toast.error(error.response?.data?.error || error.message || `No se pudo descargar ${String(type).toUpperCase()}.`);
    } finally {
      setBusyId(null);
    }
  };

  const downloadCreditNote = async (note, type) => {
    try {
      setBusyId(`nc-${note.id}`);
      const config = {
        pdf: { prefix: "", extension: "pdf" },
        ticket: { prefix: "", suffix: "-ticket", extension: "pdf" },
        xml: { prefix: "", extension: "xml" },
        zip: { prefix: "", extension: "zip" },
        cdr: { prefix: "R-", extension: "zip" },
        "cdr-xml": { prefix: "R-", extension: "xml" },
      }[type];
      await electronicDocumentsApi.downloadCreditNote(
        note.id,
        type,
        `${config.prefix}${note.full_number}${config.suffix || ""}.${config.extension}`,
      );
    } catch (error) {
      toast.error(error.response?.data?.error || "No se pudo descargar el archivo de la Nota de Crédito.");
    } finally {
      setBusyId(null);
    }
  };

  const showObservations = async (receipt) => {
    try {
      setBusyId(receipt.id);
      const result = await electronicDocumentsApi.observations(receipt.id);
      setObservationModal(result);
    } catch (error) {
      toast.error(error.response?.data?.error || "No se pudieron consultar las observaciones SUNAT.");
    } finally {
      setBusyId(null);
    }
  };

  const retry = async (receipt) => {
    if (!window.confirm(`Se reenviará ${receipt.full_number} usando el mismo correlativo. ¿Deseas continuar?`)) return;
    try {
      setBusyId(receipt.id);
      const result = await electronicDocumentsApi.retry(receipt.id);
      toast.success(`Reenvío completado. Estado: ${result?.document?.sunat_status || "PROCESADO"}.`);
      await reload();
    } catch (error) {
      toast.error(error.response?.data?.error || "No se pudo reenviar a SUNAT.");
      await reload();
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (receipt) => {
    const reason = window.prompt(
      `Motivo para eliminar ${receipt.full_number}:\n\nSolo se permite eliminar comprobantes no aceptados por SUNAT.`,
      "Error de emisión o venta cancelada",
    );
    if (!reason?.trim()) return;
    try {
      setBusyId(receipt.id);
      await electronicDocumentsApi.remove(receipt.id, reason.trim());
      toast.success("Comprobante eliminado y reserva de IMEI liberada.");
      await reload();
    } catch (error) {
      toast.error(error.response?.data?.error || "No se pudo eliminar el comprobante.");
    } finally {
      setBusyId(null);
    }
  };

  const openCreditNoteCreate = async (receipt) => {
    setNcGiftInfo(null);
    setNcGiftSelected([]);
    setNcGiftConfirmed(false);
    if (receipt.register_id) {
      try {
        const detail = await electronicDocumentsApi.promotionGiftReturnDetail(receipt.register_id);
        setNcGiftInfo(detail);
      } catch (error) {
        // No impedir la emisión de NC por una consulta auxiliar fallida.
        setNcGiftInfo({ error: error.response?.data?.message || "No se pudieron consultar los obsequios." });
      }
    }
    setCreditNoteModal({
      mode: "create",
      receipt,
      reasonCode: "01",
      reason: "Cliente desistió de la compra y devolvió el equipo.",
      confirmReturned: false,
      confirmRefunded: false,
    });
  };

  const openCreditNoteDetail = async (receipt) => {
    try {
      setBusyId(receipt.id);
      const note = await electronicDocumentsApi.creditNoteDetail(receipt.credit_note_id);
      setCreditNoteModal({ mode: "detail", receipt, note });
    } catch (error) {
      toast.error(error.response?.data?.error || "No se pudo consultar la Nota de Crédito.");
    } finally {
      setBusyId(null);
    }
  };

  const createCreditNote = async () => {
    const state = creditNoteModal;
    if (!state?.receipt) return;
    if (String(state.reason || "").trim().length < 5) {
      toast.error("Ingresa un sustento de al menos 5 caracteres.");
      return;
    }
    if (!state.confirmReturned) {
      toast.error("Confirma que el equipo fue devuelto físicamente.");
      return;
    }
    if (!state.confirmRefunded) {
      toast.error("Confirma que la devolución económica al cliente fue realizada o formalmente gestionada.");
      return;
    }
    if (ncGiftSelected.length && !ncGiftConfirmed) {
      toast.error("Confirma la recepción física de los obsequios seleccionados.");
      return;
    }
    if (ncGiftInfo?.error && state.receipt.register_id) {
      toast.error("No se pudo verificar el detalle de obsequios. Vuelve a abrir el formulario.");
      return;
    }
    if (!window.confirm(
      `Se procesará la anulación de la venta vinculada a ${state.receipt.full_number}.\n\nSi es una venta Efectiva, el sistema anulará automáticamente todos sus comprobantes aceptados y devolverá el IMEI una sola vez cuando corresponda. ¿Continuar?`,
    )) return;

    try {
      setBusyId(state.receipt.id);
      const result = await electronicDocumentsApi.cancelSale(state.receipt.id, {
        reasonCode: state.reasonCode,
        reason: state.reason.trim(),
        confirmReturned: true,
        confirmRefunded: true,
      });
      const note = result?.creditNote || result?.creditNotes?.[result.creditNotes.length - 1];
      if (result?.mode === "EFECTIVA_GROUP") {
        toast.success(
          result.completed
            ? `Venta Efectiva anulada: ${result.acceptedCreditNotes}/${result.requiredCreditNotes} Notas de Crédito aceptadas. El IMEI se procesó una sola vez.`
            : `Anulación Efectiva en proceso: ${result.acceptedCreditNotes}/${result.requiredCreditNotes} Notas de Crédito aceptadas.`,
        );
      } else {
        toast.success(
          note?.inventory_reversal_status === "APPLIED"
            ? `Nota de Crédito ${note.full_number} aceptada. El IMEI volvió al stock.`
            : `Nota de Crédito procesada. Estado SUNAT: ${note?.sunat_status || "PENDIENTE"}.`,
        );
      }
      // El servicio existente solo permite devolver regalos tras aceptación SUNAT
      // y reversión aplicada del IMEI. Un estado pendiente NO modifica existencias.
      if (ncGiftSelected.length && state.receipt.register_id) {
        try {
          const detail = await electronicDocumentsApi.promotionGiftReturnDetail(state.receipt.register_id);
          if (detail?.note?.id) {
            await electronicDocumentsApi.confirmPromotionGiftReturn(detail.note.id, ncGiftSelected);
            toast.success("Obsequios recibidos: stock restaurado en su ubicación original.");
          } else {
            toast.info("Nota de Crédito en proceso: obsequios pendientes de reincorporación. Confírmalos después de la aceptación SUNAT.");
          }
        } catch (giftError) {
          toast.error(giftError.response?.data?.message || "NC procesada, pero la devolución de regalos quedó pendiente. Revisar antes de reintentar.");
        }
      }
      setCreditNoteModal(null);
      await reload();
    } catch (error) {
      toast.error(error.response?.data?.error || "No se pudo emitir la Nota de Crédito.");
      await reload();
    } finally {
      setBusyId(null);
    }
  };

  const retryCreditNote = async (note) => {
    if (!note?.id) return;

    const errorText = `${note.error_code || ""} ${note.error_message || ""}`.toUpperCase();
    const isDuplicate1033 = errorText.includes("CLIENT.1033") || /(^|\D)1033(\D|$)/.test(errorText);
    const confirmation = isDuplicate1033
      ? `SUNAT informó que ${note.full_number} ya fue registrado previamente con otros datos (1033).\n\nEl backend aplicará la renumeración controlada ya implementada y reconstruirá XML/ZIP antes del reenvío. ¿Deseas continuar?`
      : `Se reenviará la Nota de Crédito ${note.full_number} a SUNAT.\n\nNo se creará una segunda Nota de Crédito y el IMEI solo volverá al stock si SUNAT la acepta. ¿Deseas continuar?`;

    if (!window.confirm(confirmation)) return;

    try {
      setBusyId(`nc-${note.id}`);
      const result = await electronicDocumentsApi.creditNoteRetry(note.id);
      const updated = result?.creditNote || note;
      toast.success(
        updated?.inventory_reversal_status === "APPLIED"
          ? `Nota de Crédito ${updated.full_number} aceptada. El IMEI volvió al stock.`
          : `Nota de Crédito procesada. Estado: ${renderSunatStatus(updated?.sunat_status)}.`,
      );
      setCreditNoteModal((current) => ({ ...current, note: updated }));
      await reload();
    } catch (error) {
      toast.error(error.response?.data?.error || "No se pudo reenviar la Nota de Crédito.");

      // Mantiene el modal sincronizado con el backend incluso cuando SUNAT vuelve
      // a responder con error. Así el botón de reintento no desaparece por un
      // estado antiguo del frontend y el usuario ve el último error persistido.
      try {
        const refreshed = await electronicDocumentsApi.creditNoteDetail(note.id);
        setCreditNoteModal((current) =>
          current ? { ...current, note: refreshed } : current,
        );
      } catch (_) {}

      await reload();
    } finally {
      setBusyId(null);
    }
  };

  const retryCreditNoteInventory = async (note) => {
    if (!window.confirm("¿Confirmas la devolución física? El sistema verificará el tipo de venta y evitará duplicar el stock.")) return;
    try {
      setBusyId(`nc-${note.id}`);
      // Primero se comprueba si pertenece a accesorios: su motor restituye SKU/cantidad.
      // Solo las NC de celulares utilizan la reversión existente por IMEI.
      let accessory = false;
      try {
        const restored = await electronicDocumentsApi.accessoryCreditNoteRestoreStock(note.id);
        accessory = true;
        if (restored?.register?.registerState === '0') {
          toast.success("Accesorios restituidos y registro desactivado correctamente.");
        } else {
          toast.success("Accesorios restituidos correctamente.");
        }
      } catch (error) {
        if (error.response?.status !== 404 || error.response?.data?.code !== 'NOT_ACCESSORY_NC') {
          throw error;
        }
      }
      if (!accessory) {
        await electronicDocumentsApi.creditNoteRetryInventory(note.id);
        toast.success("Devolución de equipo aplicada correctamente.");
      }
      const updated = await electronicDocumentsApi.creditNoteDetail(note.id);
      setCreditNoteModal((current) => current ? { ...current, note: updated } : current);
      await reload();
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data?.error || "No se pudo completar la devolución de inventario.");
    } finally {
      setBusyId(null);
    }
  };

  const retryableSunatStatuses = new Set([
    "PENDING",
    "GENERATING_XML",
    "SIGNED",
    "SENT",
    "PROCESSING",
    "RETRY_PENDING",
    "REJECTED",
    "ERROR",
  ]);

  const canRetry = (receipt) =>
    Number(receipt?.retryable ?? 1) === 1 &&
    retryableSunatStatuses.has(String(receipt?.sunat_status || "").toUpperCase());

  // La Nota de Crédito no se debe evaluar con canRetry(statusString).
  // canRetry espera un objeto de comprobante; pasar solo el string hacía que
  // receipt?.sunat_status fuera undefined y ocultaba siempre "Reenviar NC".
  const canRetryCreditNote = (note) =>
    Boolean(note) &&
    Number(note?.retryable ?? 1) === 1 &&
    retryableSunatStatuses.has(String(note?.sunat_status || "").toUpperCase());

  const canDelete = (status) => !["ACCEPTED", "OBSERVED", "INTERNAL_ONLY"].includes(status);
  const canCreateCreditNote = (receipt) =>
    isAdmin && receipt.operation_class !== "CUOTA_GENERADA" && ["ACCEPTED", "OBSERVED"].includes(receipt.sunat_status) && !receipt.credit_note_id;

  const currentPage = Number(pagination.page || 1);
  const totalPages = Math.max(1, Number(pagination.pages || 1));
  const total = Number(pagination.total || localRows.length || 0);
  const numericLimit = pageSize === "all" ? total || 1 : Number(pageSize || 50);
  const rowNumberStart = pageSize === "all" ? 0 : (currentPage - 1) * numericLimit;
  const firstVisible = total ? rowNumberStart + 1 : 0;
  const lastVisible = total ? Math.min(rowNumberStart + localRows.length, total) : 0;

  const rucOptions = (filterOptions.rucs || []).map((ruc) => ({
    value: Number(ruc.id),
    label: `${ruc.alias || ruc.business_name || "Empresa"}${ruc.ruc ? ` · ${ruc.ruc}` : ""}`,
  }));
  const sedeOptions = (filterOptions.sedes || []).map((sede) => ({
    value: Number(sede.id),
    label: sede.name || `Sede ${sede.id}`,
  }));
  const advisorOptions = (filterOptions.advisors || []).map((advisor) => ({
    value: Number(advisor.id),
    label: advisor.name || `Asesor ${advisor.id}`,
  }));
  const selectedRuc = rucOptions.find((item) => Number(item.value) === Number(filters.rucId)) || null;
  const selectedSede = sedeOptions.find((item) => Number(item.value) === Number(filters.sedeId)) || null;
  const selectedAdvisor = advisorOptions.find((item) => Number(item.value) === Number(filters.advisorId)) || null;
  const operationClassOptions = [
    {value: "VENTA_CREDITO", label: "Venta a crédito"},
    {value: "VENTA_CONTADO", label: "Venta al contado"},
    {value: "CUOTA_GENERADA", label: "Cuota generada"},
  ];
  const selectedOperationClass = operationClassOptions.find((item) => item.value === filters.operationClass) || null;
  const patchFilters = (patch) => {
    if (!setFilters) return;
    setFilters((current) => ({ ...current, ...patch }));
  };

  return (
    <div className="inventory-sale-receipts electronic-documents-panel">
      <div className="inventory-receipts-intro">
        <div>
          <span>FACTURACIÓN ELECTRÓNICA</span>
          <h3>Boletas, facturas y Notas de Crédito SUNAT</h3>
          <p>
            Un comprobante aceptado no se elimina. Si el cliente devuelve el equipo, se emite una Nota de Crédito y el IMEI vuelve al stock solo después de la aceptación SUNAT.
          </p>
        </div>
      </div>

      {isAdmin && (
        <section className={`electronic-sequence-access${showSequenceManager ? " is-open" : ""}`}>
          <div className="electronic-sequence-access-copy">
            <span>CONFIGURACIÓN ADMINISTRATIVA</span>
            <strong>Series y correlativos electrónicos</strong>
            <small>Administra el punto de partida de boletas, facturas y Notas de Crédito solo cuando lo necesites.</small>
          </div>
          <button
            type="button"
            className="electronic-sequence-toggle"
            aria-expanded={showSequenceManager}
            aria-controls="electronic-sequence-manager-panel"
            onClick={() => setShowSequenceManager((current) => !current)}
          >
            <span className="electronic-sequence-toggle-icon" aria-hidden="true">
              {showSequenceManager ? "−" : "+"}
            </span>
            <span>{showSequenceManager ? "Ocultar configuración" : "Configurar series y correlativos"}</span>
          </button>
        </section>
      )}

      {isAdmin && showSequenceManager && (
        <div id="electronic-sequence-manager-panel" className="electronic-sequence-manager-panel">
          <ElectronicSequenceManager />
        </div>
      )}

      <div className="electronic-documents-toolbar" aria-label="Filtros de comprobantes electrónicos">
        <div className="inventory-product-filter inventory-product-filter-calendar">
          <span className="inventory-product-filter-label">Periodo</span>
          <ProductToolbarCalendar
            value={dateFilter}
            minDate={filterOptions?.dateBounds?.minDate || undefined}
            maxDate={filterOptions?.dateBounds?.maxDate || inventoryTodayLima()}
            onChange={setDateFilter}
            ariaLabel="Periodo de emisión del comprobante"
            itemLabel="comprobantes"
          />
        </div>

        <label className="inventory-product-search electronic-documents-search">
          <span>Buscar</span>
          <input
            value={search}
            onChange={(event) => setSearch?.(event.target.value)}
            placeholder="Comprobante, cliente, documento, IMEI..."
            autoComplete="off"
          />
        </label>
      </div>

      <div className="inventory-list-controls electronic-documents-list-controls">
        <div className="inventory-list-summary" aria-live="polite">
          <strong>{total}</strong>
          <span>{total === 1 ? "comprobante encontrado" : "comprobantes encontrados"}</span>
          <small>Mostrando {firstVisible}–{lastVisible}</small>
        </div>

        <div className="inventory-list-filter-group electronic-documents-filter-group">
          <div className="inventory-list-filter">
            <span>Origen del comprobante</span>
            <Select
              styles={selectStyles}
              menuPortalTarget={document.body}
              options={operationClassOptions}
              value={selectedOperationClass}
              onChange={(item) => patchFilters({operationClass: item?.value || null})}
              isClearable
              placeholder="Todos los comprobantes"
              classNamePrefix="inventory-list-select"
            />
          </div>
          <div className="inventory-list-filter">
            <span>RUC emisor</span>
            <Select
              styles={selectStyles}
              menuPortalTarget={document.body}
              options={rucOptions}
              value={selectedRuc}
              onChange={(item) => patchFilters({ rucId: item?.value || null })}
              isClearable
              placeholder="Todos"
              noOptionsMessage={() => "Sin RUC emisores"}
              classNamePrefix="inventory-list-select"
            />
          </div>

          <div className="inventory-list-filter">
            <span>Sede</span>
            <Select
              styles={selectStyles}
              menuPortalTarget={document.body}
              options={sedeOptions}
              value={selectedSede}
              onChange={(item) => patchFilters({ sedeId: item?.value || null })}
              isClearable
              placeholder="Todas"
              noOptionsMessage={() => "Sin sedes"}
              classNamePrefix="inventory-list-select"
            />
          </div>

          <div className="inventory-list-filter">
            <span>Asesor</span>
            <Select
              styles={selectStyles}
              menuPortalTarget={document.body}
              options={advisorOptions}
              value={selectedAdvisor}
              onChange={(item) => patchFilters({ advisorId: item?.value || null })}
              isClearable
              placeholder="Todos"
              noOptionsMessage={() => "Sin asesores"}
              classNamePrefix="inventory-list-select"
            />
          </div>

          <label className="inventory-page-size">
            <span>Mostrar</span>
            <select
              value={pageSize}
              onChange={(event) => {
                const value = event.target.value === "all" ? "all" : Number(event.target.value);
                onPageSizeChange?.(value);
              }}
            >
              <option value={50}>50 comprobantes</option>
              <option value={100}>100 comprobantes</option>
              <option value={200}>200 comprobantes</option>
              <option value="all">Todos los comprobantes</option>
            </select>
          </label>
        </div>
      </div>

      <Table
        showRowNumber
        rowNumberStart={rowNumberStart}
        columns={[
          "full_number", "operation_class", "document_type", "customer_name", "customer_document_number", "imei",
          "issuer_name", "sede_name", "total_amount", "environment", "sunat_status",
          "credit_note_full_number", "credit_note_status", "error_category", "inventory_rs", "retry_after_at", "attempt_count", "issued_at",
        ]}
        rows={localRows}
        moneyCols={["total_amount"]}
        actions={(receipt) => (
          <div className="electronic-document-actions">
            <button disabled={busyId === receipt.id} onClick={() => download(receipt, "pdf")}>PDF A4</button>
            <button disabled={busyId === receipt.id} onClick={() => download(receipt, "ticket")}>Ticket</button>
            {receipt.xml_path && <button disabled={busyId === receipt.id} onClick={() => download(receipt, "xml")}>XML</button>}
            {receipt.zip_path && <button disabled={busyId === receipt.id} onClick={() => download(receipt, "zip")}>ZIP</button>}
            {receipt.cdr_path && <>
              <button disabled={busyId === receipt.id} onClick={() => download(receipt, "cdr")}>CDR</button>
              <button disabled={busyId === receipt.id} onClick={() => download(receipt, "cdr-xml")}>CDR XML</button>
            </>}
            {receipt.sunat_status === "OBSERVED" && (
              <button className="receipt-observations-button" disabled={busyId === receipt.id} onClick={() => showObservations(receipt)}>Observaciones</button>
            )}
            {canRetry(receipt) && (
              <button className="receipt-retry-button" disabled={busyId === receipt.id} onClick={() => retry(receipt)}>
                {busyId === receipt.id ? "Procesando..." : "Reenviar SUNAT"}
              </button>
            )}
            {canCreateCreditNote(receipt) && (
              <button className="danger" disabled={busyId === receipt.id} onClick={() => openCreditNoteCreate(receipt)}>Anular venta</button>
            )}
            {receipt.credit_note_id && (
              <button disabled={busyId === receipt.id} onClick={() => openCreditNoteDetail(receipt)}>Ver Nota de Crédito</button>
            )}
            {isAdmin && canDelete(receipt.sunat_status) && (
              <button className="danger" disabled={busyId === receipt.id} onClick={() => remove(receipt)}>Eliminar</button>
            )}
          </div>
        )}
      />

      {pageSize !== "all" && totalPages > 1 && (
        <Pagination
          page={currentPage}
          pages={totalPages}
          onChange={onPageChange}
          ariaLabel="Paginación de comprobantes"
        />
      )}

      {observationModal && (
        <div className="inventory-modal">
          <div className="backdrop" onClick={() => setObservationModal(null)} />
          <section>
            <header><div><h2>Observaciones SUNAT</h2><small>{observationModal.fullNumber}</small></div><button onClick={() => setObservationModal(null)}>×</button></header>
            <div className="inventory-form">
              <Field wide label="Estado"><div>{renderSunatStatus(observationModal.status)}</div></Field>
              <Field wide label="Respuesta SUNAT"><div>{observationModal.description || "Sin descripción."}</div></Field>
              <Field wide label="Detalle de observaciones"><div>{(observationModal.observations || []).length ? <ul>{observationModal.observations.map((item, index) => <li key={`${item.code || "note"}-${index}`}>{item.code ? <strong>{item.code}: </strong> : null}{item.message || item.raw || String(item)}</li>)}</ul> : <span>No existen observaciones registradas en el CDR.</span>}</div></Field>
            </div>
            <footer className="inventory-modal-footer"><button type="button" className="modal-button modal-button-secondary" onClick={() => setObservationModal(null)}>Cerrar</button></footer>
          </section>
        </div>
      )}

      {creditNoteModal && (
        <div className="inventory-modal">
          <div className="backdrop" onClick={() => setCreditNoteModal(null)} />
          <section>
            <header>
              <div>
                <h2>{creditNoteModal.mode === "create" ? "Anular venta con Nota de Crédito" : "Nota de Crédito electrónica"}</h2>
                <small>{creditNoteModal.receipt?.full_number}</small>
              </div>
              <button onClick={() => setCreditNoteModal(null)}>×</button>
            </header>
            {creditNoteModal.mode === "create" ? (
              <div className="inventory-form">
                <Field wide label="Comprobante original"><div><strong>{creditNoteModal.receipt.full_number}</strong> · {creditNoteModal.receipt.customer_name} · {money(creditNoteModal.receipt.total_amount)}</div></Field>
                <Field wide label="Motivo SUNAT">
                  <Select
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    isClearable={false}
                    options={[
                      { value: "01", label: "01 - Anulación de la operación" },
                      { value: "06", label: "06 - Devolución total" },
                    ]}
                    value={[
                      { value: "01", label: "01 - Anulación de la operación" },
                      { value: "06", label: "06 - Devolución total" },
                    ].find((item) => item.value === creditNoteModal.reasonCode)}
                    onChange={(item) => setCreditNoteModal((current) => ({ ...current, reasonCode: item?.value || "01" }))}
                  />
                </Field>
                <Field wide label="Sustento">
                  <textarea
                    maxLength={250}
                    value={creditNoteModal.reason || ""}
                    onChange={(event) => setCreditNoteModal((current) => ({ ...current, reason: event.target.value }))}
                    placeholder="Ejemplo: Cliente desistió de la compra y devolvió el equipo en tienda."
                  />
                </Field>
                <Field wide label="Confirmación física">
                  <label className="inventory-check">
                    <input
                      type="checkbox"
                      checked={Boolean(creditNoteModal.confirmReturned)}
                      onChange={(event) => setCreditNoteModal((current) => ({ ...current, confirmReturned: event.target.checked }))}
                    />
                    Confirmo que el equipo fue devuelto físicamente y puede regresar al stock disponible.
                  </label>
                  <small>El sistema no liberará el IMEI hasta que SUNAT acepte la Nota de Crédito.</small>
                </Field>
                {ncGiftInfo?.gifts?.length > 0 && (
                  <Field wide label="Devolución de obsequios promocionales">
                    <div className="inventory-gift-nc-panel">
                      <p>Seleccione solo los obsequios recibidos físicamente. Los no seleccionados permanecerán descontados.</p>
                      {ncGiftInfo.gifts.map((gift) => (
                        <label className="inventory-check" key={gift.id}>
                          <input type="checkbox" disabled={gift.status !== "DELIVERED"}
                            checked={ncGiftSelected.includes(Number(gift.id))}
                            onChange={(event) => {
                              setNcGiftConfirmed(false);
                              setNcGiftSelected((current) => event.target.checked
                                ? [...current, Number(gift.id)]
                                : current.filter((id) => id !== Number(gift.id)));
                            }} />
                          {gift.title} · {gift.quantity} unidad(es) · {gift.status === "RETURNED" ? "Ya devuelto" : "Pendiente"}
                        </label>
                      ))}
                      {ncGiftSelected.length > 0 && <label className="inventory-check">
                        <input type="checkbox" checked={ncGiftConfirmed} onChange={(event) => setNcGiftConfirmed(event.target.checked)} />
                        Confirmo la recepción física y verificación de los obsequios seleccionados.
                      </label>}
                      <small>El stock se restaurará solo después de que SUNAT acepte la NC y se complete la reversión del equipo. Si queda pendiente, usar la devolución de obsequios existente.</small>
                    </div>
                  </Field>
                )}
                {ncGiftInfo?.error && <Field wide label="Consulta de obsequios"><small role="alert">{ncGiftInfo.error}</small></Field>}
                <Field wide label="Confirmación económica">
                  <label className="inventory-check">
                    <input
                      type="checkbox"
                      checked={Boolean(creditNoteModal.confirmRefunded)}
                      onChange={(event) => setCreditNoteModal((current) => ({ ...current, confirmRefunded: event.target.checked }))}
                    />
                    Confirmo que la devolución económica al cliente fue realizada o quedó formalmente gestionada.
                  </label>
                  <small>Esta confirmación queda registrada con el usuario que emite la Nota de Crédito.</small>
                </Field>
              </div>
            ) : (
              <div className="inventory-form">
                <Field wide label="Nota de Crédito"><div><strong>{creditNoteModal.note?.full_number}</strong></div></Field>
                <Field label="Estado SUNAT"><div>{renderSunatStatus(creditNoteModal.note?.sunat_status)}</div></Field>
                <Field label="Stock"><div>{creditNoteModal.note?.inventory_reversal_status === "APPLIED" ? "Devuelto al inventario" : creditNoteModal.note?.inventory_reversal_status || "Pendiente"}</div></Field>
                <Field wide label="Comprobante afectado"><div>{creditNoteModal.note?.original_full_number}</div></Field>
                <Field wide label="Motivo"><div>{creditNoteModal.note?.reason_code} - {creditNoteModal.note?.reason}</div></Field>
                {creditNoteModal.note?.sunat_response_description && <Field wide label="Respuesta SUNAT"><div>{creditNoteModal.note.sunat_response_description}</div></Field>}
                {creditNoteModal.note?.error_message && (
                  <Field wide label="Último error de procesamiento">
                    <div className="credit-note-error-detail">
                      {creditNoteModal.note?.error_code && <strong>{creditNoteModal.note.error_code}</strong>}
                      <span>{creditNoteModal.note.error_message}</span>
                    </div>
                  </Field>
                )}
                {canRetryCreditNote(creditNoteModal.note) && (
                  <div className="credit-note-retry-panel wide" role="status">
                    <div className="credit-note-retry-icon" aria-hidden="true">↻</div>
                    <div>
                      <strong>Esta Nota de Crédito puede reenviarse</strong>
                      <span>
                        El reintento trabaja sobre la misma Nota de Crédito. No duplica la anulación y la devolución del IMEI continúa condicionada a la aceptación de SUNAT.
                      </span>
                    </div>
                  </div>
                )}
                {creditNoteModal.note?.inventory_reversal_error && <Field wide label="Incidencia inventario"><div>{creditNoteModal.note.inventory_reversal_error}</div></Field>}
                <Field wide label="Archivos">
                  <div className="electronic-document-actions">
                    {["ACCEPTED", "OBSERVED"].includes(creditNoteModal.note?.sunat_status) && <>
                      <button onClick={() => downloadCreditNote(creditNoteModal.note, "pdf")}>PDF A4 NC</button>
                      <button onClick={() => downloadCreditNote(creditNoteModal.note, "ticket")}>Ticket NC</button>
                    </>}
                    {creditNoteModal.note?.xml_path && <button onClick={() => downloadCreditNote(creditNoteModal.note, "xml")}>XML NC</button>}
                    {creditNoteModal.note?.zip_path && <button onClick={() => downloadCreditNote(creditNoteModal.note, "zip")}>ZIP NC</button>}
                    {creditNoteModal.note?.cdr_path && <>
                      <button onClick={() => downloadCreditNote(creditNoteModal.note, "cdr")}>CDR NC</button>
                      <button onClick={() => downloadCreditNote(creditNoteModal.note, "cdr-xml")}>CDR XML NC</button>
                    </>}
                  </div>
                </Field>
              </div>
            )}
            <footer className="inventory-modal-footer">
              <button type="button" className="modal-button modal-button-secondary" onClick={() => setCreditNoteModal(null)}>Cerrar</button>
              {creditNoteModal.mode === "create" && (
                <button className="modal-button modal-button-danger" disabled={busyId === creditNoteModal.receipt?.id} onClick={createCreditNote}>Anular venta</button>
              )}
              {creditNoteModal.mode === "detail" && isAdmin && canRetryCreditNote(creditNoteModal.note) && (
                <button
                  className="modal-button modal-button-primary credit-note-retry-button"
                  disabled={busyId === `nc-${creditNoteModal.note?.id}`}
                  onClick={() => retryCreditNote(creditNoteModal.note)}
                >
                  {busyId === `nc-${creditNoteModal.note?.id}` ? "Reenviando..." : "Reenviar NC a SUNAT"}
                </button>
              )}
              {creditNoteModal.mode === "detail" && isAdmin && ["ACCEPTED", "OBSERVED"].includes(creditNoteModal.note?.sunat_status) && creditNoteModal.note?.inventory_reversal_status !== "APPLIED" && (
                <button className="modal-button modal-button-primary" disabled={busyId === `nc-${creditNoteModal.note?.id}`} onClick={() => retryCreditNoteInventory(creditNoteModal.note)}>Reintentar devolución a stock</button>
              )}
            </footer>
          </section>
        </div>
      )}
    </div>
  );
}

function Audit({ rows = [] }) {
  const viewRows = (rows || []).map((row) => ({
    ...row,
    entity_type: auditEntityLabel(row.entity_type),
    action: auditActionLabel(row.action),
    user_role_id: auditRoleLabel(row.user_role_id),
  }));

  return (
    <div className="inventory-audit-view">
      <div className="inventory-section-note">
        <strong>Auditoría en español</strong>
        <span>
          Las claves técnicas del backend se conservan sin cambios; esta vista presenta
          entidades, acciones y roles con nombres operativos.
        </span>
      </div>
      <Table
        columns={[
          "created_at",
          "entity_type",
          "entity_id",
          "action",
          "user_display_name",
          "user_role_id",
          "ip_address",
        ]}
        rows={viewRows}
      />
    </div>
  );
}
function Receipts({ rows = [], permissions, reload }) {
  const transition = async (id, action) => {
    const note =
      window.prompt(
        action === "reject"
          ? "Motivo del rechazo:"
          : "Observación de confirmación:",
      ) || "";
    if (action === "reject" && note.trim().length < 5) return;
    try {
      action === "reject"
        ? await inventoryApi.rejectReceipt(id, note)
        : await inventoryApi.confirmReceipt(id, note);
      toast.success("Recojo actualizado.");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.error || "No se pudo procesar.");
    }
  };
  return (
    <Table
      columns={[
        "receipt_number",
        "supplierName",
        "logisticsUserName",
        "description",
        "imageCount",
        "status",
        "created_at",
      ]}
      rows={rows}
      actions={
        permissions.confirm
          ? (r) =>
              r.status === "PENDING_CONFIRMATION" && (
                <>
                  <button onClick={() => transition(r.id, "confirm")}>
                    Confirmar
                  </button>
                  <button
                    className="danger"
                    onClick={() => transition(r.id, "reject")}
                  >
                    Rechazar
                  </button>
                </>
              )
          : null
      }
    />
  );
}
function transferDocumentationText(row) {
  if (row.guideNumber) {
    if (row.guideMode === "SUNAT") {
      const total = Number(row.sunatDocumentCount || 0);
      const accepted = Number(row.sunatAcceptedCount || 0);
      if (row.guideStatus === "COMPLETED") return `${row.guideNumber} · GRE aceptada`;
      return `${row.guideNumber} · GRE ${accepted}/${total || "—"}`;
    }
    return `${row.guideNumber} · Guía interna`;
  }
  if (row.documentation_mode === "SUNAT") {
    if (row.documentation_status === "ERROR") return "GRE · Error documental";
    if (["DISPATCHED", "PARTIALLY_RECEIVED", "RECEIVED"].includes(row.status) && row.documentation_status === "PENDING_APPROVAL") {
      return "GRE · Solicitud fuera de flujo";
    }
    return row.status === "APPROVED" ? "GRE · Pendiente de preparar" : "GRE · Pendiente de aprobación";
  }
  if (row.documentation_mode === "INTERNAL") {
    return row.documentation_status === "ERROR" ? "Guía interna · Error" : "Guía interna · Pendiente";
  }
  return "Sin guía";
}

function Transfers({ rows = [], permissions, reload, onOpenGuides }) {
  const auth = useAuth();
  const isAdmin = String(auth.rol || "") === "1";
  const [documentationTransfer, setDocumentationTransfer] = useState(null);
  const [productsTransfer, setProductsTransfer] = useState(null);
  const [cancelTransfer, setCancelTransfer] = useState(null);
  const allowed = {
    DRAFT: "submit",
    PENDING_APPROVAL: "approve",
    APPROVED: "dispatch",
    DISPATCHED: "receive",
  };

  const run = async (r) => {
    const action = allowed[r.status];
    if (!action) return;
    try {
      const result = unwrap(await inventoryApi.transitionTransfer(r.id, action));
      toast.success("Traslado actualizado.");
      if (result?.documentation?.status === "ERROR") {
        toast.warning(
          `El traslado quedó ${String(result.status || "actualizado").toLowerCase()}, pero la documentación no pudo prepararse: ${result.documentation.error || "revisa el centro documental"}.`,
        );
      } else if (result?.documentation?.guide?.guide_number) {
        toast.success(`Documentación ${result.documentation.guide.guide_number} preparada.`);
      }
      reload();
    } catch (e) {
      toast.error(e.response?.data?.error || "No se pudo actualizar.");
    }
  };

  const viewRows = (rows || []).map((row) => ({
    ...row,
    statusDisplay: transferStatusMeta(row.status),
    documentationSummary: transferDocumentationText(row),
  }));

  return (
    <>
      <div className="transfer-first-intro">
        <div>
          <span>FLUJO LOGÍSTICO PRINCIPAL</span>
          <h3>Traslado primero, documentación después</h3>
          <p>
            El traslado controla stock, IMEI, origen y destino. La Guía Interna o GRE SUNAT
            documenta ese traslado y se genera después, únicamente cuando se requiera.
          </p>
        </div>
        <strong>Fuente de verdad: traslado</strong>
      </div>
      <Table
        columns={[
          "transfer_number",
          "originName",
          "destinationName",
          "requestedByName",
          "itemCount",
          "unitCount",
          "statusDisplay",
          "documentationSummary",
          "requested_at",
        ]}
        rows={viewRows}
        actions={(r) => (
          <div className="transfer-actions-compact">
            <button
              type="button"
              className="transfer-products-button"
              onClick={() => setProductsTransfer(r)}
              title="Ver productos trasladados"
            >
              Ver productos
            </button>

            {permissions.transfer && allowed[r.status] && (
              <button
                type="button"
                className="transfer-primary-action"
                onClick={() => run(r)}
                title={transferStatusMeta(r.status).step}
              >
                {{
                  submit: "Enviar a aprobación",
                  approve: "Aprobar",
                  dispatch: "Despachar",
                  receive: "Confirmar recepción",
                }[allowed[r.status]]}
              </button>
            )}

            <div className="transfer-secondary-actions">
              {permissions.transfer &&
                !["CANCELLED", "REJECTED"].includes(r.status) &&
                permissions.shippingGuidesWrite && (
                  <button
                    type="button"
                    className="transfer-document-button"
                    onClick={() => setDocumentationTransfer(r)}
                  >
                    {r.guideNumber ? "Documentación" : "Documentar"}
                  </button>
                )}

              {r.guideNumber && (
                <button
                  type="button"
                  className="transfer-guide-link"
                  onClick={() => onOpenGuides?.(r)}
                >
                  Ver guía
                </button>
              )}

              {isAdmin && ["DRAFT", "PENDING_APPROVAL", "APPROVED"].includes(String(r.status || "")) && (
                <button
                  type="button"
                  className="transfer-cancel-button"
                  onClick={() => setCancelTransfer(r)}
                  title="Anular traslado antes del despacho"
                >
                  Anular
                </button>
              )}
            </div>
          </div>
        )}
      />
      {productsTransfer && (
        <TransferProductsModal
          transfer={productsTransfer}
          close={() => setProductsTransfer(null)}
        />
      )}
      {cancelTransfer && (
        <TransferCancelModal
          transfer={cancelTransfer}
          close={() => setCancelTransfer(null)}
          saved={async () => {
            setCancelTransfer(null);
            await reload();
          }}
        />
      )}
      {documentationTransfer && (
        <TransferDocumentationModal
          transfer={documentationTransfer}
          close={() => setDocumentationTransfer(null)}
          saved={async () => {
            setDocumentationTransfer(null);
            await reload();
          }}
        />
      )}
    </>
  );
}

function transferItemStatus(item) {
  const receipt = String(item?.receiptStatus || "").toUpperCase();
  const dispatch = String(item?.dispatchStatus || "").toUpperCase();

  if (receipt === "RECEIVED") return { label: "Recibido", tone: "success" };
  if (receipt === "DAMAGED") return { label: "Dañado", tone: "danger" };
  if (receipt === "MISSING") return { label: "Faltante", tone: "danger" };
  if (dispatch === "DISPATCHED") return { label: "Despachado", tone: "primary" };
  if (dispatch === "CANCELLED") return { label: "Cancelado", tone: "danger" };
  return { label: "Pendiente", tone: "warning" };
}

function TransferCancelModal({ transfer, close, saved }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const valid = reason.trim().length >= 5;

  const submit = async () => {
    if (!valid || saving) return;
    setSaving(true);
    try {
      await inventoryApi.transitionTransfer(transfer.id, "cancel", { reason: reason.trim() });
      toast.success(`Traslado ${transfer.transfer_number} anulado correctamente.`);
      await saved();
    } catch (e) {
      toast.error(e.response?.data?.error || "No se pudo anular el traslado.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="inventory-modal transfer-cancel-modal" role="dialog" aria-modal="true" aria-labelledby="transfer-cancel-title">
      <section>
        <header className="inventory-modal-header">
          <div>
            <small>CONTROL DE TRAZABILIDAD</small>
            <h2 id="transfer-cancel-title">Anular traslado</h2>
            <p>{transfer.transfer_number}</p>
          </div>
          <button type="button" onClick={close} aria-label="Cerrar">×</button>
        </header>
        <div className="inventory-modal-body">
          <div className="transfer-cancel-warning">
            <strong>Esta acción no elimina el historial.</strong>
            <p>El traslado quedará CANCELADO. Solo puede anularse antes del despacho; no se crean movimientos inversos de stock porque el stock se mueve recién al despachar.</p>
          </div>
          <div className="transfer-cancel-route">
            <span><small>Origen</small><strong>{transfer.originName || "—"}</strong></span>
            <b>→</b>
            <span><small>Destino</small><strong>{transfer.destinationName || "—"}</strong></span>
          </div>
          <label className="field wide">
            <span>Motivo de anulación *</span>
            <textarea
              autoFocus
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ej.: Error documental GRE; el traslado no fue despachado."
            />
            <small>{reason.trim().length}/500 · mínimo 5 caracteres</small>
          </label>
        </div>
        <footer className="inventory-modal-footer">
          <button type="button" className="modal-button modal-button-secondary" onClick={close} disabled={saving}>Volver</button>
          <button type="button" className="modal-button transfer-cancel-confirm" onClick={submit} disabled={!valid || saving}>{saving ? "Anulando..." : "Confirmar anulación"}</button>
        </footer>
      </section>
    </div>
  );
}

function TransferProductsModal({ transfer, close }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const result = unwrap(await inventoryApi.transferItems(transfer.id));
        if (active) setDetail(result || null);
      } catch (e) {
        if (active) {
          setError(e.response?.data?.error || "No se pudieron consultar los productos del traslado.");
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [transfer.id]);

  const items = detail?.items || [];
  const summary = detail?.summary || {};
  const header = detail?.transfer || transfer;

  return (
    <div className="inventory-modal transfer-products-modal">
      <div className="backdrop" onClick={close} />
      <section>
        <header>
          <div>
            <small>DETALLE DEL TRASLADO</small>
            <h2>Productos trasladados</h2>
            <p>{header.transferNumber || transfer.transfer_number}</p>
          </div>
          <button type="button" onClick={close} aria-label="Cerrar">×</button>
        </header>

        <div className="transfer-products-content">
          <div className="transfer-products-route">
            <div>
              <small>ORIGEN</small>
              <strong>{header.originName || transfer.originName || "—"}</strong>
            </div>
            <span aria-hidden="true">→</span>
            <div>
              <small>DESTINO</small>
              <strong>{header.destinationName || transfer.destinationName || "—"}</strong>
            </div>
          </div>

          <div className="transfer-products-summary">
            <article>
              <small>Productos</small>
              <strong>{Number(summary.totalProducts ?? transfer.itemCount ?? 0)}</strong>
            </article>
            <article>
              <small>Unidades</small>
              <strong>{Number(summary.totalUnits ?? transfer.unitCount ?? 0)}</strong>
            </article>
            <article>
              <small>Solicitante</small>
              <strong>{header.requestedByName || transfer.requestedByName || "—"}</strong>
            </article>
            <article>
              <small>Fecha</small>
              <strong>{formatInventoryDate(header.createdAt || transfer.requested_at || transfer.created_at)}</strong>
            </article>
          </div>

          {loading && (
            <div className="transfer-products-state">
              <span className="transfer-products-spinner" aria-hidden="true" />
              <span>Consultando productos del traslado...</span>
            </div>
          )}

          {!loading && error && (
            <div className="transfer-products-error" role="alert">
              <strong>No se pudo cargar el detalle</strong>
              <span>{error}</span>
            </div>
          )}

          {!loading && !error && (
            <div className="transfer-products-table-wrap">
              <table className="transfer-products-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Producto</th>
                    <th>SKU</th>
                    <th>IMEI 1 / Serial</th>
                    <th>IMEI 2</th>
                    <th>Cant.</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length ? items.map((item, index) => {
                    const status = transferItemStatus(item);
                    const serialized = Number(item.isSerialized) === 1;
                    return (
                      <tr key={`${item.transferItemId}-${item.transferSerialId || index}`}>
                        <td>{index + 1}</td>
                        <td>
                          <strong>{item.productTitle || "Producto"}</strong>
                          {item.variantName && <small>{item.variantName}</small>}
                        </td>
                        <td><code>{item.sku || "—"}</code></td>
                        <td><code>{item.imei1 || item.serialNumber || "—"}</code></td>
                        <td><code>{item.imei2 || "—"}</code></td>
                        <td>{serialized ? 1 : Number(item.quantityRequested || 0)}</td>
                        <td>
                          <span className={`transfer-item-status transfer-item-status-${status.tone}`}>
                            {status.label}
                          </span>
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr>
                      <td colSpan="7" className="transfer-products-empty">
                        Este traslado no tiene productos asociados.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <footer className="inventory-modal-footer">
          <button type="button" className="modal-button modal-button-secondary" onClick={close}>
            Cerrar
          </button>
        </footer>
      </section>
    </div>
  );
}

function ShippingClientModal({ close, saved }) {
  const [form, setForm] = useState({ documentType: "1", documentNumber: "", fullName: "", names: "", surnames: "", email: "", phone: "", fiscalAddress: "", ubigeo: "" });
  const [ubigeos, setUbigeos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const searchUbigeo = (value = "") => inventoryApi.shippingUbigeos(value).then((r) => setUbigeos(unwrap(r) || [])).catch(() => setUbigeos([]));
  useEffect(() => { searchUbigeo(""); }, []);
  const lookupSequence = useRef(0);
  const lookupDocument = async (documentType, number, sequence) => {
    if (!((documentType === "1" && /^\d{8}$/.test(number)) || (documentType === "6" && /^\d{11}$/.test(number)))) return;
    try {
      setLookingUp(true);
      const url = documentType === "1" ? "https://apiperu.dev/api/dni" : "https://api.apiperu.dev/ruc";
      const token = process.env.REACT_APP_APIPERU_TOKEN;
      if (!token) throw new Error("Falta configurar APIPERU_TOKEN");
      const response = await fetch(url, { method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify(documentType === "1" ? { dni: number } : { ruc: number }) });
      const json = await response.json();
      if (!response.ok || json.success === false) throw new Error(json.message || "Consulta no disponible");
      if (sequence !== lookupSequence.current) return;
      const data = json.data || {};
      if (documentType === "1") {
        const names = data.nombres || data.nombre || "";
        const surnames = [data.apellido_paterno, data.apellido_materno].filter(Boolean).join(" ") || data.apellidos || "";
        setForm(c => ({ ...c, names, surnames, fullName: [names, surnames].filter(Boolean).join(" ") }));
      } else {
        setForm(c => ({ ...c, fullName: data.nombre_o_razon_social || data.razon_social || data.razonSocial || "" }));
      }
    } catch (error) {
      if (sequence === lookupSequence.current) toast.error(error.message || "No se pudo consultar APIDEV");
    } finally {
      if (sequence === lookupSequence.current) setLookingUp(false);
    }
  };
  const onDocumentChange = (value) => {
    const documentNumber = value.replace(/\D/g, "").slice(0, form.documentType === "6" ? 11 : 15);
    const sequence = ++lookupSequence.current;
    setLookingUp(false);
    setForm(c => ({ ...c, documentNumber, names: "", surnames: "", fullName: "" }));
    if ((form.documentType === "1" && documentNumber.length === 8) || (form.documentType === "6" && documentNumber.length === 11)) {
      lookupDocument(form.documentType, documentNumber, sequence);
    }
  };
  const onDocumentTypeChange = (documentType) => {
    ++lookupSequence.current;
    setLookingUp(false);
    setForm(c => ({ ...c, documentType, documentNumber: "", names: "", surnames: "", fullName: "" }));
  };
  const submit = async (e) => {
    e.preventDefault();
    if (form.documentType === "1" && !/^\d{8}$/.test(form.documentNumber)) return toast.error("El DNI debe tener 8 dígitos.");
    if (form.documentType === "6" && !/^\d{11}$/.test(form.documentNumber)) return toast.error("El RUC debe tener 11 dígitos.");
    if (!(form.documentType === "6" ? form.fullName.trim() : [form.names,form.surnames].filter(Boolean).join(" ").trim()) || !form.fiscalAddress.trim() || !form.ubigeo) return toast.error("Completa nombre, dirección y ubigeo del cliente.");
    try { setSaving(true); const client=unwrap(await inventoryApi.createShippingClient({...form,fullName:form.documentType==='6'?form.fullName:[form.names,form.surnames].filter(Boolean).join(' ')})); toast.success("Cliente guardado y seleccionado."); saved(client); }
    catch(error){ toast.error(error.response?.data?.error || "No se pudo guardar el cliente."); }
    finally { setSaving(false); }
  };
  return <div className="inventory-modal gre-client-modal"><div className="backdrop" onClick={close}/><section><header><div><small>DESTINATARIO DE LA GRE</small><h2>Agregar nuevo cliente</h2><p>Los datos se reutilizarán en futuras ventas Live o Callcenter.</p></div><button type="button" onClick={close}>×</button></header><form className="inventory-form" onSubmit={submit}>
    <Field label="Tipo de documento"><select value={form.documentType} onChange={(e)=>onDocumentTypeChange(e.target.value)}><option value="1">DNI</option><option value="6">RUC</option><option value="4">Carné de extranjería</option></select></Field>
    <Field label="N.º documento"><input inputMode="numeric" maxLength={form.documentType === "6" ? 11 : 15} value={form.documentNumber} onChange={(e)=>onDocumentChange(e.target.value)} placeholder="Número de documento"/>{lookingUp && <small>Consultando APIDEV...</small>}</Field>
    {form.documentType === "6" ? <Field wide label="Razón social"><input maxLength={250} value={form.fullName} disabled placeholder="Razón social"/></Field> : <><Field label="Nombres"><input maxLength={120} value={form.names} disabled={form.documentType !== "4"} onChange={(e)=>set("names",e.target.value)} placeholder="Nombres"/></Field><Field label="Apellidos"><input maxLength={120} value={form.surnames} disabled={form.documentType !== "4"} onChange={(e)=>set("surnames",e.target.value)} placeholder="Apellidos"/></Field></>}
    <Field label="Teléfono"><input maxLength={30} value={form.phone} onChange={(e)=>set("phone",e.target.value)} placeholder="Opcional"/></Field>
    <Field label="Email"><input type="email" maxLength={160} value={form.email} onChange={(e)=>set("email",e.target.value)} placeholder="Opcional"/></Field>
    <Field wide label="Dirección de entrega"><input maxLength={500} value={form.fiscalAddress} onChange={(e)=>set("fiscalAddress",e.target.value)} placeholder="Dirección exacta donde llegará el equipo"/></Field>
    <Field wide label="Ubigeo"><Select styles={selectStyles} menuPortalTarget={document.body} options={ubigeos.map((u)=>({value:u.ubigeo,label:u.label}))} value={ubigeos.map((u)=>({value:u.ubigeo,label:u.label})).find((o)=>o.value===form.ubigeo)||null} onInputChange={(value,meta)=>{ if(meta.action==="input-change") searchUbigeo(value); }} onChange={(o)=>set("ubigeo",o?.value||"")} placeholder="Busca departamento, provincia o distrito" noOptionsMessage={()=>"Sin resultados"}/></Field>
  </form><footer className="inventory-modal-footer"><button type="button" className="modal-button modal-button-secondary" onClick={close}>Cancelar</button><button type="button" className="modal-button modal-button-primary" disabled={saving || lookingUp} onClick={submit}>{saving ? "Guardando..." : "Guardar y seleccionar"}</button></footer></section></div>;
}

function TransferDocumentationModal({ transfer, close, saved }) {
  const initialMode = ["INTERNAL", "SUNAT"].includes(String(transfer.documentation_mode || "").toUpperCase())
    ? String(transfer.documentation_mode).toUpperCase()
    : "INTERNAL";
  const storedDocumentation = useMemo(() => {
    try {
      return typeof transfer.documentation_payload_json === "string"
        ? JSON.parse(transfer.documentation_payload_json || "{}")
        : transfer.documentation_payload_json || {};
    } catch (_) {
      return {};
    }
  }, [transfer.documentation_payload_json]);
  const [mode, setMode] = useState(initialMode);
  const [saving, setSaving] = useState(false);
  const [confirmingSunat, setConfirmingSunat] = useState(false);
  const [loadingContext, setLoadingContext] = useState(true);
  const [context, setContext] = useState(null);
  const [shippingClients, setShippingClients] = useState([]);
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [form, setForm] = useState({
    transferStartDate: storedDocumentation.transferStartDate || inventoryTodayLima(),
    transportModeCode: storedDocumentation.transportModeCode || "02",
    grossWeight: storedDocumentation.grossWeight ?? "",
    packageCount: storedDocumentation.packageCount ?? "1",
    vehiclePlate: storedDocumentation.vehiclePlate || "",
    originAddress: "",
    originUbigeo: "",
    originEstablishmentCode: "",
    destinationAddress: "",
    destinationUbigeo: "",
    destinationEstablishmentCode: "",
    driverDocumentType: storedDocumentation.driverDocumentType || "1",
    driverDocumentNumber: storedDocumentation.driverDocumentNumber || "",
    driverFirstNames: storedDocumentation.driverFirstNames || "",
    driverLastNames: storedDocumentation.driverLastNames || "",
    driverLicense: storedDocumentation.driverLicense || "",
    carrierRuc: storedDocumentation.carrierRuc || "",
    carrierBusinessName: storedDocumentation.carrierBusinessName || "",
    operationType: storedDocumentation.operationType || (String(transfer.destinationName || "").toLowerCase().includes("live") ? "SALE" : "INTERNAL_TRANSFER"),
    clientId: storedDocumentation.clientId || null,
    destinationAddress: storedDocumentation.destinationAddress || "",
    destinationUbigeo: storedDocumentation.destinationUbigeo || "",
    notes: storedDocumentation.notes || "",
  });
  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const driverDni = useDriverDniLookup(useCallback((dni, first, last) => {
    setForm(current => current.driverDocumentNumber === dni ? {
      ...current, driverFirstNames: first, driverLastNames: last,
    } : current);
  }, []));
  const sunat = mode === "SUNAT";
  const transferAlreadyStarted = ["DISPATCHED", "PARTIALLY_RECEIVED", "RECEIVED"].includes(String(transfer.status || ""));
  const canRequestSunat = ["DRAFT", "PENDING_APPROVAL", "APPROVED"].includes(String(transfer.status || ""));

  useEffect(() => {
    let active = true;
    setLoadingContext(true);
    inventoryApi.shippingGuideContext(transfer.id)
      .then((response) => {
        if (!active) return;
        const data = unwrap(response) || {};
        setContext(data);
        setForm((current) => ({
          ...current,
          transferStartDate: storedDocumentation.transferStartDate || data.suggested?.transferStartDate || current.transferStartDate,
          originAddress: data.suggested?.originAddress || "",
          originUbigeo: data.suggested?.originUbigeo || "",
          originEstablishmentCode: data.suggested?.originEstablishmentCode || "",
          destinationAddress: current.operationType === "SALE" ? (current.destinationAddress || "") : (data.suggested?.destinationAddress || ""),
          destinationUbigeo: current.operationType === "SALE" ? (current.destinationUbigeo || "") : (data.suggested?.destinationUbigeo || ""),
          destinationEstablishmentCode: data.suggested?.destinationEstablishmentCode || "",
        }));
      })
      .catch((error) => {
        if (active) toast.error(error.response?.data?.error || "No se pudo cargar la información logística del traslado.");
      })
      .finally(() => active && setLoadingContext(false));
    return () => { active = false; };
  }, [storedDocumentation.transferStartDate, transfer.id]);

  useEffect(() => {
    if (!sunat || form.operationType !== "SALE") return;
    inventoryApi.shippingClients("").then((response) => setShippingClients(unwrap(response) || [])).catch(() => setShippingClients([]));
  }, [sunat, form.operationType]);

  const selectedShippingClient = shippingClients.find((client) => Number(client.id) === Number(form.clientId)) || null;
  const selectShippingClient = (client) => {
    setShippingClients((current) => current.some((row) => Number(row.id) === Number(client.id)) ? current : [client, ...current]);
    setForm((current) => ({ ...current, clientId: client.id, destinationAddress: client.fiscalAddress || "", destinationUbigeo: client.ubigeo || "" }));
  };

  const validateSunatForm = () => {
    if (!sunat || transfer.status !== "APPROVED") return [];
    const missing = [];
    if (form.operationType === "SALE") {
      if (!form.clientId) missing.push("cliente destinatario");
      if (!String(form.destinationAddress || "").trim()) missing.push("dirección de llegada");
      if (!/^\d{6}$/.test(String(form.destinationUbigeo || ""))) missing.push("ubigeo de llegada");
    }
    if (!(Number(form.grossWeight) > 0)) missing.push("peso bruto");
    if (!String(form.transferStartDate || "").trim()) missing.push("fecha de inicio");
    if (String(form.transportModeCode) === "01") {
      if (!/^\d{11}$/.test(String(form.carrierRuc || ""))) missing.push("RUC del transportista");
      if (!String(form.carrierBusinessName || "").trim()) missing.push("razón social del transportista");
    } else {
      if (!String(form.vehiclePlate || "").trim()) missing.push("placa");
      if (!String(form.driverDocumentNumber || "").trim()) missing.push("DNI conductor");
      if (!String(form.driverFirstNames || "").trim()) missing.push("nombres conductor");
      if (!String(form.driverLastNames || "").trim()) missing.push("apellidos conductor");
      if (!String(form.driverLicense || "").trim()) missing.push("licencia");
    }
    return missing;
  };

  const requestSubmit = () => {
    if (sunat && !canRequestSunat) {
      return toast.error("No se puede solicitar una GRE SUNAT porque el traslado ya inició. Debe prepararse y aceptarse antes del despacho.");
    }
    const missing = validateSunatForm();
    if (missing.length) return toast.error(`Completa los datos para GRE SUNAT: ${missing.join(", ")}.`);
    if (sunat && transfer.status === "APPROVED") {
      const blocked = (context?.groups || []).filter((group) => form.operationType === "SALE"
        ? !(group.hasGreOAuth && group.hasSol && group.hasCertificate && group.hasOriginEstablishment && String(group.configuredEnvironment || "PRODUCTION") === "PRODUCTION")
        : !group.canEmitGre);
      if (blocked.length) {
        return toast.error(`Hay ${blocked.length} RUC sin configuración GRE completa. Revisa credenciales SUNAT y los códigos Sede + RUC de partida/llegada.`);
      }
      setConfirmingSunat(true);
      return;
    }
    return submit();
  };

  const submit = async () => {
    setConfirmingSunat(false);
    setSaving(true);
    try {
      const result = unwrap(await inventoryApi.documentTransfer(transfer.id, { mode, documentation: form }));
      toast.success(result?.guide?.guide_number
        ? `${result.guide.guide_number} vinculada correctamente al traslado.`
        : result?.message || "La documentación quedó solicitada para este traslado.");
      await saved();
    } catch (error) {
      toast.error(error.response?.data?.error || error.message || "No se pudo documentar el traslado.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="inventory-modal transfer-documentation-modal">
      <div className="backdrop" onClick={close} />
      <section>
        <header><div><small>DOCUMENTACIÓN DEL TRASLADO</small><h2>{transfer.transfer_number}</h2></div><button type="button" onClick={close}>×</button></header>
        <div className="inventory-form transfer-documentation-form">
          <Field wide label="Ruta logística"><div className="transfer-documentation-route"><strong>{transfer.originName}</strong><span>→</span><strong>{transfer.destinationName}</strong></div></Field>
          <Field wide label="Tipo de documentación"><div className="transfer-documentation-picker">
            <button type="button" className={mode === "INTERNAL" ? "active" : ""} onClick={() => setMode("INTERNAL")}><strong>Guía Interna</strong><small>Control empresarial · no SUNAT</small></button>
            <button type="button" className={mode === "SUNAT" ? "active sunat" : ""} onClick={() => setMode("SUNAT")}><strong>GRE SUNAT</strong><small>Documento tributario por RUC</small></button>
          </div></Field>
          {sunat && <Field wide label="Motivo / destino de la GRE"><div className="transfer-documentation-picker gre-operation-picker">
            <button type="button" className={form.operationType === "INTERNAL_TRANSFER" ? "active" : ""} onClick={() => setForm((c) => ({...c, operationType:"INTERNAL_TRANSFER", clientId:null, destinationAddress:context?.suggested?.destinationAddress || "", destinationUbigeo:context?.suggested?.destinationUbigeo || ""}))}><strong>Traslado entre establecimientos</strong><small>Destino: otra sede de la empresa</small></button>
            <button type="button" className={form.operationType === "SALE" ? "active sunat" : ""} onClick={() => setForm((c) => ({...c, operationType:"SALE", destinationAddress:"", destinationUbigeo:""}))}><strong>Venta / entrega a cliente</strong><small>Destino: dirección externa del cliente</small></button>
          </div></Field>}
          {sunat && form.operationType === "SALE" && <Field wide label="Cliente destinatario"><div className="gre-client-row"><Select styles={selectStyles} menuPortalTarget={document.body} options={shippingClients.map((client) => ({value:client.id,label:`${client.documentNumber} · ${client.fullName}`,raw:client}))} value={selectedShippingClient ? {value:selectedShippingClient.id,label:`${selectedShippingClient.documentNumber} · ${selectedShippingClient.fullName}`,raw:selectedShippingClient} : null} onChange={(option) => option?.raw && selectShippingClient(option.raw)} placeholder="Busca o selecciona un cliente" noOptionsMessage={() => "No hay clientes registrados"}/><button type="button" className="gre-add-client-button" onClick={() => setClientModalOpen(true)}>+ Agregar nuevo cliente</button></div>{selectedShippingClient && <small className="field-help">{selectedShippingClient.documentNumber} · {selectedShippingClient.ubigeoLabel || selectedShippingClient.ubigeo}</small>}</Field>}
          {sunat && transferAlreadyStarted && <div className="transfer-documentation-notice wide"><strong>GRE no disponible para este traslado.</strong> El traslado ya inició o fue recibido. La GRE SUNAT debe prepararse y obtener aceptación antes del despacho.</div>}
          {sunat && !transferAlreadyStarted && transfer.status !== "APPROVED" && <div className="transfer-documentation-notice wide">La GRE quedará solicitada. Se preparará automáticamente cuando el traslado llegue a estado APROBADO.</div>}
          {context?.groups?.length > 0 && <Field wide label="Distribución automática por RUC"><div className="guide-ruc-summary">{context.groups.map((group) => <article key={group.issuerRucId}><div><strong>{group.businessName}</strong><span>{group.ruc}</span><small className={group.canEmitGre ? "gre-config-ok" : "gre-config-pending"}>{group.canEmitGre ? "Configuración GRE lista" : "Configuración GRE incompleta"}</small></div><div className="guide-ruc-establishments"><label>Partida<input disabled value={group.originEstablishmentCode || ""} placeholder="Sin configurar" /></label><label>Llegada<input disabled value={group.destinationEstablishmentCode || ""} placeholder="Sin configurar" /></label></div><b>{group.unitCount} unidad(es)</b></article>)}</div></Field>}
          {sunat && <Field wide label="Modalidad de transporte"><div className="transfer-transport-picker">
            <button type="button" className={form.transportModeCode === "02" ? "active" : ""} onClick={() => set("transportModeCode", "02")}><strong>Transporte privado</strong><small>Vehículo y conductor de la operación</small></button>
            <button type="button" className={form.transportModeCode === "01" ? "active" : ""} onClick={() => set("transportModeCode", "01")}><strong>Transporte público</strong><small>Empresa transportista con RUC</small></button>
          </div></Field>}
          <Field label="Fecha inicio traslado"><input type="date" value={form.transferStartDate} onChange={(e) => set("transferStartDate", e.target.value)} /><small className="field-help">Fecha SUNAT: {form.transferStartDate ? form.transferStartDate.split("-").reverse().join("/") : "--/--/----"}</small></Field>
          <Field label="Peso bruto (KGM)"><input type="number" min="0" step="0.001" value={form.grossWeight} onChange={(e) => set("grossWeight", e.target.value)} placeholder="13.000" /></Field>
          <Field label="Bultos / pallets"><input type="number" min="0" step="1" value={form.packageCount} onChange={(e) => set("packageCount", e.target.value)} /></Field>
          {(!sunat || form.transportModeCode === "02") && <Field label="Placa vehículo"><input maxLength={8} value={form.vehiclePlate} onChange={(e) => set("vehiclePlate", e.target.value.toUpperCase())} placeholder="ABC123" /></Field>}
          {sunat && form.transportModeCode === "01" && <>
            <Field label="RUC transportista"><input inputMode="numeric" maxLength={11} value={form.carrierRuc} onChange={(e) => set("carrierRuc", e.target.value.replace(/\D/g, "").slice(0, 11))} placeholder="20123456789" /></Field>
            <Field label="Razón social transportista"><input maxLength={250} value={form.carrierBusinessName} onChange={(e) => set("carrierBusinessName", e.target.value)} placeholder="EMPRESA DE TRANSPORTES S.A.C." /></Field>
          </>}
          <Field wide label="Dirección de partida"><input disabled value={form.originAddress} placeholder={loadingContext ? "Cargando..." : "Sin dirección configurada"} /></Field>
          <Field label="Ubigeo partida"><input disabled value={form.originUbigeo} placeholder="------" /></Field>
          {!sunat && <Field label="Código establecimiento partida"><input disabled value={form.originEstablishmentCode} placeholder="----" /></Field>}
          <Field wide label={form.operationType === "SALE" ? "Dirección de entrega al cliente" : "Dirección de llegada"}><input disabled={form.operationType !== "SALE"} value={form.destinationAddress} onChange={(e) => set("destinationAddress", e.target.value)} placeholder={form.operationType === "SALE" ? "Dirección exacta de entrega" : (loadingContext ? "Cargando..." : "Sin dirección configurada")} /></Field>
          <Field label="Ubigeo llegada"><input disabled value={form.destinationUbigeo} placeholder="------" /></Field>
          {!sunat && <Field label="Código establecimiento llegada"><input disabled value={form.destinationEstablishmentCode} placeholder="----" /></Field>}
          {(!sunat || form.transportModeCode === "02") && <>
            <Field label="DNI conductor"><input inputMode="numeric" maxLength={8} value={form.driverDocumentNumber} onChange={(e) => {const dni=e.target.value.replace(/\D/g, "").slice(0,8);driverDni.invalidate();setForm(current=>({...current,driverDocumentNumber:dni,driverFirstNames:"",driverLastNames:""}));if(dni.length===8)driverDni.lookup(dni);}} onBlur={() => {if(form.driverDocumentNumber.length===8 && !form.driverFirstNames)driverDni.lookup(form.driverDocumentNumber);}} />{driverDni.loading && <small>Consultando DNI…</small>}{driverDni.message && <small role="status">{driverDni.message}</small>}</Field>
            <Field label="Licencia"><input maxLength={10} value={form.driverLicense} onChange={(e) => set("driverLicense", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))} /></Field>
            <Field label="Nombres conductor"><input maxLength={250} value={form.driverFirstNames} onChange={(e) => set("driverFirstNames", e.target.value)} /></Field>
            <Field label="Apellidos conductor"><input maxLength={250} value={form.driverLastNames} onChange={(e) => set("driverLastNames", e.target.value)} /></Field>
          </>}
          <Field wide label="Observaciones"><textarea maxLength={1000} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Observación logística opcional" /></Field>
        </div>
        {confirmingSunat && <div className="gre-confirmation-panel">
          <div className="gre-confirmation-card">
            <small>CONFIRMACIÓN TRIBUTARIA</small>
            <h3>Confirmar preparación de GRE SUNAT</h3>
            <p>Se reservará numeración GRE por cada RUC y se preparará la documentación tributaria del traslado.</p>
            <dl>
              <div><dt>Ruta</dt><dd>{transfer.originName} → {transfer.destinationName}</dd></div>
              <div><dt>Fecha</dt><dd>{form.transferStartDate ? form.transferStartDate.split("-").reverse().join("/") : "-"}</dd></div>
              <div><dt>Modalidad</dt><dd>{form.transportModeCode === "01" ? "Transporte público" : "Transporte privado"}</dd></div>
              <div><dt>RUC involucrados</dt><dd>{context?.groups?.length || 0}</dd></div>
              <div><dt>Unidades</dt><dd>{context?.unitCount || 0}</dd></div>
            </dl>
            <div className="gre-confirmation-actions"><button type="button" className="modal-button modal-button-secondary" onClick={() => setConfirmingSunat(false)}>Volver</button><button type="button" className="modal-button modal-button-primary" disabled={saving} onClick={submit}>Confirmar GRE</button></div>
          </div>
        </div>}
        {clientModalOpen && <ShippingClientModal close={() => setClientModalOpen(false)} saved={(client) => { selectShippingClient(client); setClientModalOpen(false); }} />}
        <footer className="inventory-modal-footer"><button type="button" className="modal-button modal-button-secondary" onClick={close}>Cancelar</button><button type="button" className="modal-button modal-button-primary" disabled={saving || loadingContext || (sunat && !canRequestSunat)} onClick={requestSubmit}>{saving ? "Procesando..." : sunat && transferAlreadyStarted ? "GRE no disponible" : sunat && transfer.status !== "APPROVED" ? "Solicitar GRE" : sunat ? "Preparar GRE" : "Generar documentación"}</button></footer>
      </section>
    </div>
  );
}

function Trace() {
  const auth = useAuth();
  const isAdmin = String(auth.rol || "") === "1";
  const [q, setQ] = useState("");
  const [result, setResult] = useState(null);
  const [reactivateOpen, setReactivateOpen] = useState(false);
  const [reactivateReason, setReactivateReason] = useState("");
  const [reactivating, setReactivating] = useState(false);

  const refreshTrace = async () => {
    const next = unwrap(await inventoryApi.trace(q));
    setResult(next);
    return next;
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await refreshTrace();
    } catch (err) {
      setResult(null);
      toast.error(err.response?.data?.error || "No se encontró el equipo.");
    }
  };

  const currentStatus = result?.serial?.current_status;
  const currentLocation = result?.serial?.currentLocation || "—";
  const canReactivate = isAdmin && String(currentStatus || "").toUpperCase() === "WRITTEN_OFF";

  const reactivate = async () => {
    const reason = String(reactivateReason || "").trim();
    if (reason.length < 5) {
      toast.error("Indica el motivo de reactivación (mínimo 5 caracteres).");
      return;
    }
    try {
      setReactivating(true);
      const restored = unwrap(await inventoryApi.reactivateSerial(result.serial.id, { reason }));
      toast.success(`Equipo reactivado en ${restored.locationName || "su sede"}.`);
      setReactivateOpen(false);
      setReactivateReason("");
      await refreshTrace();
    } catch (err) {
      toast.error(err.response?.data?.error || "No se pudo reactivar el equipo.");
    } finally {
      setReactivating(false);
    }
  };

  return (
    <>
      <form className="trace-search" onSubmit={submit}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="IMEI, IMEI 2, número de serie o ID"
        />
        <button className="primary">Consultar trazabilidad</button>
      </form>
      {result && (
        <div className="inventory-grid traceability-grid">
          <article className="inventory-card traceability-summary-card">
            <div className="traceability-card-heading">
              <span className="traceability-eyebrow">EQUIPO CONSULTADO</span>
              <h3>{result.serial?.title}</h3>
            </div>
            <dl className="traceability-summary-list">
              <div><dt>IMEI 1</dt><dd>{result.serial?.imei_1 || "—"}</dd></div>
              <div><dt>IMEI 2</dt><dd>{result.serial?.imei_2 || "—"}</dd></div>
              <div><dt>N.° de serie</dt><dd>{result.serial?.serial_number || "—"}</dd></div>
              <div className="traceability-location-row"><dt>Ubicación actual</dt><dd>{currentLocation}</dd></div>
              <div>
                <dt>Estado</dt>
                <dd>
                  <span className={`trace-status-label trace-status-label--${traceStatusTone(currentStatus)}`}>
                    {inventoryStatusLabel(currentStatus)}
                  </span>
                </dd>
              </div>
            </dl>
            {canReactivate && (
              <div className="trace-reactivate-panel">
                {!reactivateOpen ? (
                  <button type="button" className="primary" onClick={() => setReactivateOpen(true)}>
                    Reactivar equipo
                  </button>
                ) : (
                  <div className="trace-reactivate-form">
                    <strong>Revertir baja de inventario</strong>
                    <p>El equipo volverá a la ubicación donde se encontraba al momento de la baja. El historial anterior no se eliminará.</p>
                    <textarea
                      rows={3}
                      maxLength={500}
                      value={reactivateReason}
                      onChange={(e) => setReactivateReason(e.target.value)}
                      placeholder="Motivo obligatorio. Ej.: Baja realizada por error operativo"
                    />
                    <small>{reactivateReason.length}/500 caracteres</small>
                    <div className="trace-reactivate-actions">
                      <button type="button" onClick={() => { setReactivateOpen(false); setReactivateReason(""); }} disabled={reactivating}>Cancelar</button>
                      <button type="button" className="primary" onClick={reactivate} disabled={reactivating || reactivateReason.trim().length < 5}>
                        {reactivating ? "Reactivando..." : "Confirmar reactivación"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </article>
          <article className="inventory-card traceability-timeline-card">
            <div className="traceability-card-heading traceability-card-heading--timeline">
              <span className="traceability-eyebrow">HISTORIAL DEL EQUIPO</span>
              <h3>Línea de tiempo</h3>
              <p>Movimientos, traslados, ventas, bajas y documentación asociados al IMEI.</p>
            </div>
            <div className="traceability-timeline-list">
              {(result.timeline || []).map((x, i) => {
                const route = traceEventLocation(x);
                const actor = String(x.userName || x.user_display_name || "").trim();
                return (
                  <div className="timeline traceability-event" key={`${x.source || "EVENT"}-${x.id || i}-${i}`}>
                    <div className="traceability-event-top">
                      <b>{String(x.reference_type || "").toUpperCase() === "WRITE_OFF_REVERSAL" ? "Reactivación de inventario" : movementTypeLabel(x.movement_type || x.action)}</b>
                      <time>{formatInventoryDate(x.performed_at || x.created_at)}</time>
                    </div>
                    <p className="traceability-event-description">{traceEventDescription(x)}</p>
                    {(route || actor) && (
                      <div className="traceability-event-meta">
                        {route && <span><strong>Ruta:</strong> {route}</span>}
                        {actor && <span><strong>Responsable:</strong> {actor}</span>}
                      </div>
                    )}
                  </div>
                );
              })}
              {!(result.timeline || []).length && (
                <div className="inventory-empty">No hay movimientos registrados para este equipo.</div>
              )}
            </div>
          </article>
        </div>
      )}
    </>
  );
}
function Table({
  columns,
  rows = [],
  moneyCols = [],
  actions,
  renderCell,
  showRowNumber = false,
  rowNumberStart = 0,
}) {
  return (
    <div className="inventory-table-wrap">
      <table>
        <thead>
          <tr>
            {showRowNumber && <th>ID</th>}
            {columns.map((c) => (
              <th key={c}>{labels[c] || c}</th>
            ))}
            {actions && <th>Acciones</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || i}>
              {showRowNumber && <td>{rowNumberStart + i + 1}</td>}
              {columns.map((c) => (
                <td key={c}>
                  {renderCell ? (() => {
                    const custom = renderCell(c, r);
                    return custom !== undefined ? custom : moneyCols.includes(c) ? money(r[c]) : renderValue(c, r[c]);
                  })() : moneyCols.includes(c) ? money(r[c]) : renderValue(c, r[c])}
                </td>
              ))}
              {actions && <td className="row-actions">{actions(r)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
      {!rows.length && <Empty />}
    </div>
  );
}
const labels = {
  operation_class: "Origen",
  title: "Producto",
  sku: "SKU",
  categoryName: "Categoría",
  brandName: "Marca",
  imei: "IMEI 1",
  imei2: "IMEI 2",
  invoice_number: "N. Factura",
  rucDisplay: "RUC",
  productLocationName: "Ubicación / sede",
  purchase_price: "Precio compra",
  sale_price: "Precio venta",
  stockAvailable: "Stock",
  expiration_date: "Vencimiento",
  createdByName: "Creó",
  updatedByName: "Modificó",
  registered_at: "Registro",
  updated_at: "Modificación",
  variantName: "Variante",
  locationName: "Ubicación",
  city: "Ciudad",
  quantity_available: "Disponible",
  quantity_reserved: "Reservado",
  quantity_in_transit: "Tránsito",
  receipt_number: "Recojo",
  supplierName: "Proveedor",
  logisticsUserName: "Logística",
  description: "Descripción",
  imageCount: "Fotos",
  status: "Estado",
  statusDisplay: "Estado",
  created_at: "Creado",
  transfer_number: "Traslado",
  originName: "Origen",
  destinationName: "Destino",
  requestedByName: "Solicitante",
  itemCount: "Ítems",
  unitCount: "Unidades",
  requested_at: "Fecha",
  documentationSummary: "Documentación",
  slug: "Código",
  model_code: "Código modelo",
  is_serialized: "Serializado",
  is_active: "Activo",
  entity_type: "Entidad",
  entity_id: "ID",
  action: "Acción",
  user_display_name: "Usuario",
  user_role_id: "Rol",
  ip_address: "IP",
  full_number: "Comprobante",
  document_type: "Tipo",
  customer_name: "Cliente",
  customer_document_number: "Documento",
  issuer_name: "RUC emisor",
  sede_name: "Sede",
  created_by_name: "Usuario",
  total: "Total",
  total_amount: "Total",
  environment: "Ambiente",
  sunat_status: "Estado SUNAT",
  sunat_response_code: "Código SUNAT",
  credit_note_full_number: "Nota de crédito",
  credit_note_status: "Estado NC",
  attempt_count: "Intentos",
  issued_at: "Emitido",
};
const renderSunatStatus = (status) =>
  ({
    PENDING: "Pendiente",
    GENERATING_XML: "Generando XML",
    SIGNED: "Firmado",
    SENT: "Enviado a SUNAT",
    ACCEPTED: "Aceptado",
    OBSERVED: "Aceptado con observación",
    REJECTED: "Rechazado",
    ERROR: "Error de procesamiento",
    PROCESSING: "Procesando SUNAT",
    RETRY_PENDING: "Reintento pendiente",
    INTERNAL_ONLY: "Documento interno",
    DELETED: "Eliminado",
  })[String(status || "").toUpperCase()] || status || "—";

const renderValue = (key, v) => {
  if (key === "operation_class") return ({VENTA_CREDITO:"Venta a crédito",VENTA_CONTADO:"Venta al contado",CUOTA_GENERADA:"Cuota generada"})[v] || "Sin clasificar";
  if (["is_serialized", "is_active"].includes(key))
    return Number(v) ? "Sí" : "No";

  if (key === "statusDisplay" && v) {
    return (
      <span
        className={`transfer-status-badge transfer-status-${v.tone || "neutral"}`}
        title={v.step || v.label}
      >
        {v.label || "—"}
      </span>
    );
  }

  if (key === "status") return inventoryStatusLabel(v);
  if (["sunat_status", "credit_note_status"].includes(key))
    return v ? renderSunatStatus(v) : "—";
  if (isInventoryDateField(key)) return formatInventoryDate(v);
  return v ?? "—";
};
function Empty() {
  return <div className="inventory-empty">No hay registros para mostrar.</div>;
}

function ProductFieldIcon({ name }) {
  const paths = {
    sku: <><path d="M4 7h16M4 17h16M8 4 6 20M18 4l-2 16" /></>,
    title: <><path d="M5 5h14v14H5z" /><path d="M8 9h8M8 13h6" /></>,
    category: <><path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z" /></>,
    brand: <><path d="M4 8 12 3l8 5v8l-8 5-8-5z" /><path d="m4 8 8 5 8-5M12 13v8" /></>,
    model: <><rect x="7" y="3" width="10" height="18" rx="2" /><path d="M10 6h4M11 18h2" /></>,
    ruc: <><path d="M4 20V7l8-4 8 4v13" /><path d="M8 10h2M14 10h2M8 14h2M14 14h2M9 20v-3h6v3" /></>,
    supplier: <><path d="M3 7h11v10H3zM14 10h4l3 3v4h-7z" /><path d="M6 20a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM17 20a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" /></>,
    invoice: <><path d="M6 3h12v18l-2-1.5L14 21l-2-1.5L10 21l-2-1.5L6 21z" /><path d="M9 8h6M9 12h6M9 16h4" /></>,
    imei: <><rect x="7" y="2.5" width="10" height="19" rx="2" /><path d="M10 6h4M10 10h4M10 14h4M11 18h2" /></>,
    money: <><circle cx="12" cy="12" r="9" /><path d="M15 8.5c-.7-.6-1.7-1-3-1-1.7 0-3 .8-3 2s1 1.8 3.1 2.2c2 .4 2.9 1 2.9 2.3s-1.3 2.2-3 2.2c-1.2 0-2.4-.4-3.2-1.1M12 5.5v13" /></>,
    warehouse: <><path d="m3 9 9-5 9 5v11H3z" /><path d="M7 12h10v8H7zM7 15h10" /></>,
    stock: <><path d="m4 7 8-4 8 4-8 4zM4 7v10l8 4 8-4V7M12 11v10" /></>,
    tax: <><path d="M5 4h14v16H5z" /><path d="M8 8h8M8 12h8M8 16h5" /></>,
    description: <><path d="M4 5h16v14H4z" /><path d="M8 9h8M8 13h8M8 17h5" /></>,
  };

  return (
    <span className="inventory-field-icon" aria-hidden="true">
      <svg viewBox="0 0 24 24">{paths[name] || paths.description}</svg>
    </span>
  );
}

function Field({ label, children, wide = false, icon = null }) {
  return (
    <label className={`${wide ? "field wide" : "field"}${icon ? " inventory-field-with-icon" : ""}`}>
      <span className={icon ? "inventory-field-label" : undefined}>
        {icon && <ProductFieldIcon name={icon} />}
        <span>{label}</span>
      </span>
      {children}
    </label>
  );
}
function ProductImportModal({ close, saved }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [preview, setPreview] = useState(null);
  const [validating, setValidating] = useState(false);
  const [importing, setImporting] = useState(false);

  const reset = () => {
    setFile(null);
    setRows([]);
    setPreview(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const chooseFile = async (selectedFile) => {
    if (!selectedFile) return;
    setValidating(true);
    setPreview(null);
    try {
      const parsedRows = await parseProductImportFile(selectedFile);
      setFile(selectedFile);
      setRows(parsedRows);
      const response = await inventoryApi.previewProductImport(parsedRows);
      setPreview(unwrap(response));
    } catch (error) {
      console.error(error);
      reset();
      toast.error(error.response?.data?.error || error.message || "No se pudo validar el Excel.");
    } finally {
      setValidating(false);
    }
  };

  const submit = async () => {
    if (!rows.length || Number(preview?.summary?.invalid || 0) > 0) return;
    setImporting(true);
    try {
      const response = await inventoryApi.importProducts(rows);
      const result = unwrap(response);
      toast.success(result?.message || `${result?.importedCount || rows.length} equipos importados correctamente.`);
      if ((result?.transferErrors || []).length) {
        toast.warning(`${result.transferErrors.length} grupo(s) de traslado requieren revisión. Los productos sí quedaron importados.`);
      }
      saved();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.error || "No se pudo completar la importación. No se registró ninguna fila.");
      try {
        const response = await inventoryApi.previewProductImport(rows);
        setPreview(unwrap(response));
      } catch (_) {}
    } finally {
      setImporting(false);
    }
  };

  const summary = preview?.summary || {};
  const invalidRows = (preview?.rows || []).filter((row) => (row.errors || []).length);
  const warningRows = (preview?.rows || []).filter((row) => (row.warnings || []).length);
  const canImport = Boolean(rows.length) && Number(summary.invalid || 0) === 0 && !validating && !importing;

  return (
    <div className="inventory-modal inventory-import-modal-wrap">
      <div className="backdrop" onClick={importing ? undefined : close} />
      <section className="inventory-import-modal">
        <header>
          <div>
            <h2>Importar celulares desde Excel</h2>
            <small>Validación completa antes de registrar. Una fila representa un equipo físico.</small>
          </div>
          <button type="button" onClick={close} disabled={importing}>×</button>
        </header>

        <div className="inventory-import-content">
          <div className="inventory-import-rules">
            <strong>Protecciones aplicadas</strong>
            <span>IMEI 1 obligatorio · IMEI 2 opcional · duplicados cruzados bloqueados · fecha histórica opcional · Almacén Central o Sede/Tienda válidos · traslados DRAFT posteriores</span>
          </div>

          <label className={`inventory-import-dropzone${validating ? " is-loading" : ""}`}>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              onChange={(event) => chooseFile(event.target.files?.[0])}
              disabled={validating || importing}
            />
            <strong>{validating ? "Validando archivo..." : file ? file.name : "Seleccionar archivo Excel"}</strong>
            <span>{file ? `${rows.length} fila(s) detectadas` : "Usa la plantilla descargada desde Productos para evitar errores de formato."}</span>
          </label>

          {preview && (
            <>
              <div className="inventory-import-summary">
                <div><strong>{summary.total || 0}</strong><span>Filas</span></div>
                <div className="is-valid"><strong>{summary.valid || 0}</strong><span>Correctas</span></div>
                <div className={Number(summary.invalid || 0) ? "is-invalid" : ""}><strong>{summary.invalid || 0}</strong><span>Con errores</span></div>
                <div className={Number(summary.warnings || 0) ? "is-warning" : ""}><strong>{summary.warnings || 0}</strong><span>Advertencias</span></div>
              </div>

              {invalidRows.length > 0 && (
                <div className="inventory-import-issues is-error">
                  <div className="inventory-import-issues-title">
                    <strong>Corrige estas filas antes de importar</strong>
                    <span>No se registrará ningún producto mientras exista un error.</span>
                  </div>
                  <div className="inventory-import-issues-list">
                    {invalidRows.slice(0, 50).map((row) => (
                      <div key={`error-${row.rowNumber}`} className="inventory-import-issue-row">
                        <b>Fila {row.rowNumber}</b>
                        <div>{(row.errors || []).map((message, index) => <span key={index}>{message}</span>)}</div>
                      </div>
                    ))}
                  </div>
                  {invalidRows.length > 50 && <small>Se muestran los primeros 50 registros con error.</small>}
                </div>
              )}

              {invalidRows.length === 0 && (
                <div className="inventory-import-ready">
                  <strong>Archivo listo para importar</strong>
                  <span>El backend volverá a validar IMEI, SKU, catálogos, fecha y ubicación física dentro de una única transacción. Si una sede activa todavía no tiene ubicación BRANCH, la crea y vincula automáticamente; después genera los traslados DRAFT indicados en DESTINO_SEDE.</span>
                </div>
              )}

              {warningRows.length > 0 && (
                <div className="inventory-import-issues is-warning">
                  <div className="inventory-import-issues-title"><strong>Advertencias</strong></div>
                  <div className="inventory-import-issues-list">
                    {warningRows.slice(0, 20).map((row) => (
                      <div key={`warning-${row.rowNumber}`} className="inventory-import-issue-row">
                        <b>Fila {row.rowNumber}</b>
                        <div>{(row.warnings || []).map((message, index) => <span key={index}>{message}</span>)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <footer className="inventory-modal-footer">
          {file && <button type="button" className="modal-button modal-button-secondary" onClick={reset} disabled={importing}>Cambiar archivo</button>}
          <button type="button" className="modal-button modal-button-secondary" onClick={close} disabled={importing}>Cancelar</button>
          <button type="button" className="modal-button modal-button-primary" onClick={submit} disabled={!canImport}>
            {importing ? "Importando..." : `Importar ${summary.valid || 0} equipo(s)`}
          </button>
        </footer>
      </section>
    </div>
  );
}

function Modal({
  type,
  product,
  catalogType,
  deleting,
  setDeleting,
  meta,
  close,
  saved,
}) {
  const modalAuth = useAuth();
  const modalRole = String(modalAuth.rol || "");
  const userInventoryContext = meta.userInventoryContext || null;
  const automaticInitialWarehouse = modalRole === "7";
  const kind = type === "catalogs" ? "category" : type;
  const isEditingProduct = kind === "products" && Boolean(product?.id);
  const isEditingLocation = kind === "locations" && Boolean(product?.id);
  const isEditingCatalog = ["category", "brand", "model"].includes(kind) && Boolean(product?.id);
  const deleteCatalogType = kind === "catalog-delete" ? catalogType : null;
  const [form, setForm] = useState({
    isActive: true,
    stock: 0,
    initialLocationId: null,
    imei: "",
    imei2: "",
    invoiceNumber: "",
    annulReason: "",
    igvAffectationCode: "20",
    unitCode: "NIU",
    serials: [{ imei1: "", imei2: "", serialNumber: "" }],
    items: [{ productId: null, quantity: 1, unitCost: 0 }],
    documentationMode: "NONE",
    documentation: {
      transferStartDate: inventoryTodayLima(),
      grossWeight: "",
      packageCount: "1",
      vehiclePlate: "",
      driverDocumentType: "1",
      driverDocumentNumber: "",
      driverFirstNames: "",
      driverLastNames: "",
      driverLicense: "",
      notes: "",
    },
    ...(kind === "locations" && product
      ? {
          name: product.name || "",
          type: product.type || "BRANCH",
          city: product.city || "",
          sedeId: product.sede_id || null,
          isActive: Boolean(Number(product.is_active)),
        }
      : kind === "locations"
        ? {
            name: "",
            type: "BRANCH",
            city: "",
            sedeId: null,
            isActive: true,
          }
        : {}),
    ...(["category", "brand", "model"].includes(kind) && product
      ? {
          name: product.name || "",
          description: product.description || "",
          color: product.color || "",
          logoPath: product.logo_path || "",
          modelCode: product.model_code || "",
          categoryId: product.category_id || null,
          brandId: product.brand_id || null,
          isSerialized: Boolean(Number(product.is_serialized)),
          isActive: Boolean(Number(product.is_active)),
        }
      : {}),
    ...(kind === "products" && product
      ? {
          title: product.title || "",
          categoryId: product.category_id || null,
          brandId: product.brand_id || null,
          modelId: product.model_id || null,
          rucId: product.ruc_id || null,
          supplierId: product.supplier_id || null,
          sku: product.sku || "",
          imei: product.imei || "",
          imei2: product.imei2 || "",
          invoiceNumber: product.invoice_number || "",
          purchasePrice: formatMoneyInput(product.purchase_price),
          salePrice: formatMoneyInput(product.sale_price),
          stock: Number(product.stockAvailable || 0),
          description: product.description || "",
          igvAffectationCode: product.igv_affectation_code || "20",
          unitCode: product.unit_code || "NIU",
          isActive: Boolean(Number(product.is_active)),
        }
      : {}),
  });
  const [files, setFiles] = useState([]);
  const [imeiStatus, setImeiStatus] = useState(null);
  const [imei2Status, setImei2Status] = useState(null);
  const [validatingImei, setValidatingImei] = useState(false);
  const [validatingImei2, setValidatingImei2] = useState(false);
  const [skuEditable, setSkuEditable] = useState(false);
  const [skuStatus, setSkuStatus] = useState(null);
  const [validatingSku, setValidatingSku] = useState(false);
  const [generatingSku, setGeneratingSku] = useState(false);
  const [validatingModelTitle, setValidatingModelTitle] = useState(false);
  const [modelTitleServerStatus, setModelTitleServerStatus] = useState(null);
  const [transferAvailability, setTransferAvailability] = useState([]);
  const [transferAvailabilityError, setTransferAvailabilityError] = useState(null);
  const [loadingTransferAvailability, setLoadingTransferAvailability] = useState(false);
  const [transferEntryMode, setTransferEntryMode] = useState("SERIALS");
  const [liveDeliveryMode, setLiveDeliveryMode] = useState("BRANCH");
  const [liveShippingClients, setLiveShippingClients] = useState([]);
  const [liveUbigeos, setLiveUbigeos] = useState([]);
  const [liveClientModalOpen, setLiveClientModalOpen] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const selectedCategory = meta.categories.find(
    (category) => Number(category.id) === Number(form.categoryId),
  );
  const isCellphone =
    String(selectedCategory?.slug || "").toLowerCase() === "celulares";

  const selectedModel = meta.models.find(
    (model) => Number(model.id) === Number(form.modelId),
  );

  const availableModels = useMemo(
    () =>
      meta.models.filter(
        (model) =>
          Number(model.is_active) !== 0 &&
          (!form.categoryId ||
            Number(model.category_id) === Number(form.categoryId)) &&
          (!form.brandId || Number(model.brand_id) === Number(form.brandId)),
      ),
    [form.brandId, form.categoryId, meta.models],
  );

  const modelTitleMatch = useMemo(
    () => getModelTitleMatch(form.title, selectedModel?.name),
    [form.title, selectedModel?.name],
  );

  const cellphoneCategory = useMemo(
    () =>
      meta.categories.find((category) => {
        const slug = String(category?.slug || "").trim().toLowerCase();
        const name = String(category?.name || "").trim().toLowerCase();

        return slug === "celulares" || name === "celulares";
      }) || null,
    [meta.categories],
  );

  const centralWarehouses = useMemo(
    () =>
      (meta.locations || []).filter(
        (location) =>
          location.type === "CENTRAL_WAREHOUSE" &&
          Number(location.is_active ?? 1) !== 0,
      ),
    [meta.locations],
  );

  const selectedTransferOrigin = useMemo(
    () => (meta.locations || []).find((location) => Number(location.id) === Number(form.originLocationId)) || null,
    [form.originLocationId, meta.locations],
  );
  const transferOriginName = String(selectedTransferOrigin?.name || "").trim().toLowerCase();
  const isLiveTransferOrigin = transferOriginName === "live";
  const isCallcenterTransferOrigin = transferOriginName === "callcenter" || transferOriginName === "call center";
  const isExternalChannelOrigin = isLiveTransferOrigin || isCallcenterTransferOrigin;
  const externalChannelLabel = isCallcenterTransferOrigin ? "Callcenter" : "Live";

  useEffect(() => {
    if (kind !== "transfers" || !isExternalChannelOrigin || liveDeliveryMode !== "EXTERNAL") return;
    inventoryApi.shippingClients("", isCallcenterTransferOrigin ? "callcenter" : "live")
      .then((response) => setLiveShippingClients(unwrap(response) || []))
      .catch(() => setLiveShippingClients([]));
    inventoryApi.shippingUbigeos("")
      .then((response) => setLiveUbigeos(unwrap(response) || []))
      .catch(() => setLiveUbigeos([]));
  }, [kind, isExternalChannelOrigin, isCallcenterTransferOrigin, liveDeliveryMode]);

  const transferProductOptions = useMemo(
    () =>
      (transferAvailability || []).map((item) => ({
        value: Number(item.productId),
        label: `${item.title} · ${Number(item.availableQuantity || 0)} disponible${Number(item.availableQuantity || 0) === 1 ? "" : "s"}`,
        raw: item,
      })),
    [transferAvailability],
  );

  const selectedTransferProduct = useMemo(
    () =>
      (transferAvailability || []).find(
        (item) => Number(item.productId) === Number(form.items?.[0]?.productId),
      ) || null,
    [form.items, transferAvailability],
  );

  const transferSerialOptions = useMemo(
    () =>
      (selectedTransferProduct?.serials || []).map((serial) => ({
        value: Number(serial.serialId),
        label: serial.imei1
          ? `IMEI ${serial.imei1}${serial.serialNumber ? ` · Serie ${serial.serialNumber}` : ""}`
          : `Serie ${serial.serialNumber || serial.serialId}`,
        raw: serial,
      })),
    [selectedTransferProduct],
  );

  const transferUnitOptions = useMemo(
    () =>
      (transferAvailability || []).flatMap((product) =>
        product.isSerialized
          ? (product.serials || []).map((serial) => ({
              value: Number(serial.serialId),
              label: `${product.title} · IMEI ${serial.imei1 || serial.serialNumber || serial.serialId}`,
              raw: {
                ...serial,
                productId: Number(product.productId),
                variantId: product.variantId ?? null,
                title: product.title,
                unitCost: Number(product.unitCost || serial.purchaseCost || 0),
              },
            }))
          : [],
      ),
    [transferAvailability],
  );

  const selectedTransferUnitOptions = useMemo(() => {
    const ids = new Set(
      (form.items || []).flatMap((item) => (item.serialIds || []).map((id) => Number(id))),
    );
    return transferUnitOptions.filter((item) => ids.has(Number(item.value)));
  }, [form.items, transferUnitOptions]);

  const transferNonSerializedProductOptions = useMemo(
    () => transferProductOptions.filter((item) => !item.raw?.isSerialized),
    [transferProductOptions],
  );

  const selectTransferUnits = (selectedOptions) => {
    const grouped = new Map();
    for (const selected of selectedOptions || []) {
      const unit = selected.raw || {};
      const key = `${Number(unit.productId)}:${unit.variantId ?? "null"}`;
      if (!grouped.has(key)) {
        grouped.set(key, {
          productId: Number(unit.productId),
          variantId: unit.variantId === null || unit.variantId === undefined ? null : Number(unit.variantId),
          quantity: 0,
          unitCost: Number(unit.unitCost || 0),
          serialIds: [],
        });
      }
      const item = grouped.get(key);
      item.serialIds.push(Number(unit.serialId));
      item.quantity = item.serialIds.length;
    }
    set("items", [...grouped.values()]);
  };

  useEffect(() => {
    if (
      kind !== "products" ||
      isEditingProduct ||
      form.categoryId ||
      !cellphoneCategory?.id
    ) {
      return;
    }

    setForm((current) => ({
      ...current,
      categoryId: Number(cellphoneCategory.id),
      stock: 1,
      imei: current.imei || "",
      imei2: current.imei2 || "",
    }));
    setImeiStatus(null);
    setImei2Status(null);
  }, [
    cellphoneCategory?.id,
    form.categoryId,
    isEditingProduct,
    kind,
  ]);

  useEffect(() => {
    if (kind !== "products" || isEditingProduct) return;

    if (automaticInitialWarehouse) {
      const automaticWarehouseId = Number(
        userInventoryContext?.centralWarehouseId || 0,
      );

      setForm((current) => ({
        ...current,
        initialLocationId: automaticWarehouseId || null,
      }));
      return;
    }

    if (centralWarehouses.length === 1 && !form.initialLocationId) {
      setForm((current) => ({
        ...current,
        initialLocationId: Number(centralWarehouses[0].id),
      }));
    }
  }, [
    automaticInitialWarehouse,
    centralWarehouses,
    form.initialLocationId,
    isEditingProduct,
    kind,
    userInventoryContext?.centralWarehouseId,
  ]);

  useEffect(() => {
    if (kind !== "transfers") return undefined;

    const originLocationId = Number(form.originLocationId || 0);
    if (!originLocationId) {
      setTransferAvailability([]);
      setTransferAvailabilityError(null);
      return undefined;
    }

    let active = true;
    setLoadingTransferAvailability(true);
    setTransferAvailabilityError(null);

    inventoryApi
      .transferAvailability(originLocationId, { externalSale: isExternalChannelOrigin && liveDeliveryMode === "EXTERNAL" })
      .then((response) => {
        if (!active) return;
        const payload = unwrap(response);
        setTransferAvailability(payload?.products || []);
      })
      .catch((error) => {
        if (!active) return;
        setTransferAvailability([]);
        const apiMessage = error.response?.data?.error;
        const status = error.response?.status;
        const code = error.response?.data?.code;
        const message =
          apiMessage ||
          (status
            ? `No se pudo consultar el stock disponible del origen (HTTP ${status}).`
            : "No se pudo conectar con el servicio de stock del origen.");
        setTransferAvailabilityError({ message, status, code });
        toast.error(message);
      })
      .finally(() => {
        if (active) setLoadingTransferAvailability(false);
      });

    return () => {
      active = false;
    };
  }, [form.originLocationId, kind, isExternalChannelOrigin, liveDeliveryMode]);

  const validateSku = async (value = form.sku) => {
    const normalized = String(value ?? "")
      .replace(/\D/g, "")
      .slice(0, 10);
    const local = {
      sku: normalized,
      valid: /^\d{10}$/.test(normalized),
      available: false,
      message: /^\d{10}$/.test(normalized)
        ? "Validando disponibilidad..."
        : `${normalized.length}/10 dígitos`,
    };
    setSkuStatus(local);
    if (!local.valid) return local;

    setValidatingSku(true);
    try {
      const response = unwrap(
        await inventoryApi.validateSku(normalized, product?.id || 0),
      );
      const status = {
        ...response,
        valid: Boolean(response.valid && response.available),
      };
      setSkuStatus(status);
      return status;
    } catch (error) {
      const status = {
        ...local,
        valid: false,
        code: error.response?.data?.code || "SKU_VALIDATION_ERROR",
        message: error.response?.data?.error || "No se pudo validar el SKU.",
      };
      setSkuStatus(status);
      return status;
    } finally {
      setValidatingSku(false);
    }
  };

  useEffect(() => {
    if (kind !== "products") return;

    if (isEditingProduct) {
      const currentSku = String(product?.sku || "")
        .replace(/\D/g, "")
        .slice(0, 10);
      setSkuStatus({
        sku: currentSku,
        valid: /^\d{10}$/.test(currentSku),
        available: true,
        message: /^\d{10}$/.test(currentSku)
          ? "SKU actual del producto."
          : "El SKU actual debe corregirse a 10 dígitos.",
      });
      return;
    }

    let mounted = true;
    setGeneratingSku(true);
    inventoryApi
      .generateSku()
      .then((response) => {
        if (!mounted) return;
        const generated = unwrap(response);
        set("sku", generated.sku);
        setSkuStatus(generated);
      })
      .catch((error) => {
        if (!mounted) return;
        setSkuEditable(true);
        setSkuStatus({
          valid: false,
          available: false,
          message: error.response?.data?.error || "No se pudo generar el SKU.",
        });
      })
      .finally(() => mounted && setGeneratingSku(false));

    return () => {
      mounted = false;
    };
  }, [kind, isEditingProduct, product?.id]);

  const validateImei = async (value = form.imei) => {
    const local = validateImeiValue(value);
    setImeiStatus(local);
    if (!local.valid) return local;

    if (typeof inventoryApi.validateImei !== "function") return local;

    setValidatingImei(true);
    try {
      const response = unwrap(
        await inventoryApi.validateImei(local.normalized, product?.id || 0),
      );
      const status = {
        ...local,
        ...response,
        valid: Boolean(response.valid && response.available),
      };
      setImeiStatus(status);
      return status;
    } catch (error) {
      const status = {
        ...local,
        valid: false,
        code: error.response?.data?.code || "IMEI_VALIDATION_ERROR",
        message: error.response?.data?.error || "No se pudo validar el IMEI.",
      };
      setImeiStatus(status);
      return status;
    } finally {
      setValidatingImei(false);
    }
  };

  const validateImei2 = async (value = form.imei2) => {
    const normalized = normalizeImeiValue(value);
    if (!normalized) {
      const status = { normalized: "", valid: true, available: true, optional: true, code: "IMEI_2_EMPTY", message: "IMEI 2 opcional." };
      setImei2Status(status);
      return status;
    }
    const local = validateImeiValue(normalized);
    setImei2Status(local);
    if (!local.valid) return local;
    if (normalized === normalizeImeiValue(form.imei)) {
      const status = { ...local, valid: false, available: false, code: "IMEI_FIELDS_DUPLICATE", message: "IMEI 2 debe ser diferente de IMEI 1." };
      setImei2Status(status);
      return status;
    }
    setValidatingImei2(true);
    try {
      const response = unwrap(await inventoryApi.validateImei(normalized, product?.id || 0));
      const status = { ...local, ...response, valid: Boolean(response.valid && response.available) };
      setImei2Status(status);
      return status;
    } catch (error) {
      const status = { ...local, valid: false, code: error.response?.data?.code || "IMEI_2_VALIDATION_ERROR", message: error.response?.data?.error || "No se pudo validar el IMEI 2." };
      setImei2Status(status);
      return status;
    } finally { setValidatingImei2(false); }
  };
  const submit = async () => {
    try {
      if (kind === "location-delete") {
        setDeleting(true);
        await inventoryApi.deleteLocation(product.id);
        toast.success("Ubicación eliminada correctamente.");
        saved();
        return;
      }

      if (kind === "product-delete") {
        setDeleting(true);
        const reason = String(form.annulReason || "").trim();
        if (reason.length < 5) {
          toast.error("Indica el motivo de eliminación/anulación (mínimo 5 caracteres).");
          setDeleting(false);
          return;
        }
        const result = await inventoryApi.deleteProduct(product.id, {
          annul: true,
          mode: "ANNUL",
          reason,
        });
        toast.success(result?.annulled ? "Producto anulado correctamente y retirado del stock." : "Producto eliminado correctamente.");
        saved();
        return;
      }

      if (kind === "catalog-delete") {
        setDeleting(true);
        const deleteMethods = {
          category: inventoryApi.deleteCategory,
          brand: inventoryApi.deleteBrand,
          model: inventoryApi.deleteModel,
        };
        const method = deleteMethods[deleteCatalogType];
        if (!method) throw new Error("Catálogo inválido.");
        await method(product.id);
        toast.success("Registro de catálogo eliminado correctamente.");
        saved();
        return;
      }

      if (kind === "locations") {
        const type = String(form.type || "").toUpperCase();
        const physical = ["CENTRAL_WAREHOUSE", "BRANCH"].includes(type);

        if (!String(form.name || "").trim()) {
          toast.error("Ingresa el nombre de la ubicación.");
          return;
        }
        if (physical && !form.sedeId) {
          toast.error("Selecciona el establecimiento físico asociado.");
          return;
        }

        if (type === "CENTRAL_WAREHOUSE" && !String(form.city || "").trim()) {
          toast.error("Indica la ciudad operativa del Almacén Central.");
          return;
        }

        if (type === "CENTRAL_WAREHOUSE") {
          const selectedEstablishment = (meta.sedes || []).find(
            (row) => Number(row.id) === Number(form.sedeId),
          );

          if (
            String(selectedEstablishment?.establishmentType || "").toUpperCase() !==
            "WAREHOUSE"
          ) {
            toast.error(
              "Un Almacén Central debe vincularse a un establecimiento de tipo Almacén.",
            );
            return;
          }
        }

        const payload = {
          name: String(form.name || "").trim(),
          type,
          city: String(form.city || "").trim(),
          sedeId: form.sedeId || null,
          isActive: form.isActive !== false,
        };

        if (isEditingLocation)
          await inventoryApi.saveLocation(product.id, payload);
        else await inventoryApi.saveLocation(null, payload);
      }

      if (kind === "products") {
        const requiredFields = [
          [/^\d{10}$/.test(String(form.sku || "")), "SKU de 10 dígitos"],
          [form.title?.trim(), "Título"],
          [form.categoryId, "Categoría"],
          [form.brandId, "Marca"],
          [form.modelId, "Modelo"],
          [form.rucId, "RUC"],
          [form.supplierId, "Proveedor"],
          [
            ["10", "20", "30", "40"].includes(String(form.igvAffectationCode || "")),
            "Afectación IGV",
          ],
          [
            form.purchasePrice !== "" && form.purchasePrice !== undefined,
            "Precio compra",
          ],
          [
            form.salePrice !== "" && form.salePrice !== undefined,
            "Precio venta",
          ],
        ];
        const missing = requiredFields
          .filter(([valid]) => !valid)
          .map(([, label]) => label);

        if (missing.length) {
          toast.error(`Completa: ${missing.join(", ")}.`);
          return;
        }

        if (!modelTitleMatch.valid) {
          toast.error(
            modelTitleMatch.message ||
              "El título debe contener el modelo seleccionado.",
          );
          return;
        }

        setValidatingModelTitle(true);
        try {
          const modelValidation = unwrap(
            await inventoryApi.validateProductModelTitle({
              title: form.title,
              modelId: form.modelId,
              categoryId: form.categoryId,
              brandId: form.brandId,
            }),
          );
          setModelTitleServerStatus(modelValidation);
          if (!modelValidation.valid) {
            toast.error(
              modelValidation.message ||
                "El título no coincide con el modelo seleccionado.",
            );
            return;
          }
        } finally {
          setValidatingModelTitle(false);
        }

        const skuResult = await validateSku(form.sku);
        if (!skuResult.valid) {
          toast.error(skuResult.message || "Revisa el SKU ingresado.");
          return;
        }

        const stock = isCellphone
          ? 1
          : Math.max(0, Number(form.stock || 0));
        if (!Number.isInteger(stock)) {
          toast.error("El stock debe ser un número entero.");
          return;
        }
        if (isCellphone) {
          const imeiResult = await validateImei(form.imei);
          if (!imeiResult.valid) {
            toast.error(imeiResult.message || "Revisa el IMEI 1 ingresado.");
            return;
          }
          const imei2Result = await validateImei2(form.imei2);
          if (!imei2Result.valid) {
            toast.error(imei2Result.message || "IMEI 2 es opcional, pero si se ingresa debe contener exactamente 15 dígitos numéricos.");
            return;
          }
        }

        const normalizedPurchasePrice = normalizePriceValue(form.purchasePrice);
        const normalizedSalePrice = normalizePriceValue(form.salePrice);

        if (normalizedPurchasePrice === "" || Number(normalizedPurchasePrice) < 0) {
          toast.error("El precio de compra no tiene un formato válido.");
          return;
        }

        if (normalizedSalePrice === "" || Number(normalizedSalePrice) < 0) {
          toast.error("El precio de venta no tiene un formato válido.");
          return;
        }

        let initialLocationId = Number(form.initialLocationId || 0) || null;

        if (!isEditingProduct && stock > 0) {
          if (automaticInitialWarehouse) {
            initialLocationId = Number(
              userInventoryContext?.centralWarehouseId || 0,
            ) || null;

            if (!initialLocationId) {
              toast.error(
                userInventoryContext?.code === "USER_CITY_WAREHOUSE_AMBIGUOUS"
                  ? "La ciudad de tu usuario tiene más de un Almacén Central candidato. Corrige la configuración logística."
                  : "No existe un Almacén Central activo configurado para la ciudad de tu usuario.",
              );
              return;
            }
          } else {
            if (!initialLocationId && centralWarehouses.length === 1) {
              initialLocationId = Number(centralWarehouses[0].id);
            }

            const selectedWarehouse = centralWarehouses.find(
              (row) => Number(row.id) === Number(initialLocationId),
            );

            if (!selectedWarehouse) {
              toast.error(
                centralWarehouses.length > 1
                  ? "Selecciona el Almacén Central donde ingresará el stock inicial."
                  : "Configura un Almacén Central activo antes de registrar stock.",
              );
              return;
            }
          }
        }

        const payload = {
          ...form,
          initialLocationId,
          purchasePrice: normalizedPurchasePrice,
          salePrice: normalizedSalePrice,
          stock,
        };
        if (isEditingProduct)
          await inventoryApi.updateProduct(product.id, payload);
        else await inventoryApi.createProduct(payload);
      }
      if (kind === "receipts") {
        const fd = new FormData();
        fd.append("supplierId", form.supplierId || "");
        fd.append("description", form.description || "");
        files.forEach((f) => fd.append("photos", f));
        await inventoryApi.createReceipt(fd);
      }
      if (kind === "stock") {
        if (!form.productId) {
          toast.error("Selecciona el producto.");
          return;
        }
        if (!form.locationId) {
          toast.error("Selecciona el Almacén Central de ingreso.");
          return;
        }
        await inventoryApi.initialEntry({ ...form, serials: form.serials });
      }
      if (kind === "transfers") {

        if (!form.originLocationId || !form.destinationLocationId) {
          toast.error(isExternalChannelOrigin && liveDeliveryMode === "EXTERNAL" ? `Selecciona el origen ${externalChannelLabel} y la sede logística que controlará el despacho.` : "Selecciona el origen y el destino del traslado.");
          return;
        }
        if (isExternalChannelOrigin && liveDeliveryMode === "EXTERNAL") {
          const missingLive = [];
          if (!form.liveClientId) missingLive.push("cliente destinatario");
          if (!/^\d{6}$/.test(String(form.liveDestinationUbigeo || ""))) missingLive.push("ubigeo de entrega");
          if (!String(form.liveDestinationAddress || "").trim()) missingLive.push("dirección exacta");
          if (missingLive.length) { toast.error(`Completa la entrega Live: ${missingLive.join(", ")}.`); return; }
        }

        if (Number(form.originLocationId) === Number(form.destinationLocationId)) {
          toast.error("El origen y el destino deben ser diferentes.");
          return;
        }

        if (transferEntryMode === "SERIALS") {
          const serialCount = (form.items || []).reduce(
            (total, item) => total + (Array.isArray(item.serialIds) ? item.serialIds.length : 0),
            0,
          );
          if (!serialCount) {
            toast.error("Selecciona al menos un equipo / IMEI para trasladar.");
            return;
          }
        } else {
          const rows = form.items || [];
          if (!rows.length || rows.some((item) => !item.productId)) {
            toast.error("Agrega al menos un producto válido al traslado.");
            return;
          }
          const keys = new Set();
          for (const item of rows) {
            const key = `${item.productId}:${item.variantId ?? "null"}`;
            if (keys.has(key)) {
              toast.error("Un producto no puede repetirse en el mismo traslado.");
              return;
            }
            keys.add(key);
            const availableProduct = transferNonSerializedProductOptions.find(
              (option) => Number(option.raw.productId) === Number(item.productId) &&
                String(option.raw.variantId ?? "null") === String(item.variantId ?? "null"),
            )?.raw;
            if (!availableProduct) {
              toast.error("Uno de los productos ya no está disponible en el origen.");
              return;
            }
            const quantity = Number(item.quantity);
            if (!Number.isInteger(quantity) || quantity < 1 || quantity > Number(availableProduct.availableQuantity || 0)) {
              toast.error(`Cantidad inválida para ${availableProduct.title}. Disponible: ${availableProduct.availableQuantity}.`);
              return;
            }
          }
        }

        if (form.documentationMode === "SUNAT") {
          const documentation = form.documentation || {};
          const missing = [];
          if (!(Number(documentation.grossWeight) > 0)) missing.push("peso bruto");
          if (!String(documentation.vehiclePlate || "").trim()) missing.push("placa");
          if (!String(documentation.driverDocumentNumber || "").trim()) missing.push("DNI conductor");
          if (!String(documentation.driverFirstNames || "").trim()) missing.push("nombres conductor");
          if (!String(documentation.driverLastNames || "").trim()) missing.push("apellidos conductor");
          if (!String(documentation.driverLicense || "").trim()) missing.push("licencia");
          if (missing.length) {
            toast.error(`Completa los datos para solicitar GRE SUNAT: ${missing.join(", ")}.`);
            return;
          }
        }

        const transferResult = unwrap(await inventoryApi.createTransfer({
          originLocationId: form.originLocationId,
          destinationLocationId: form.destinationLocationId,
          notes: form.notes,
          items: form.items,
          documentationMode: "NONE",
          documentation: {},
          transferPurpose: isExternalChannelOrigin && liveDeliveryMode === "EXTERNAL" ? "EXTERNAL_SALE" : "INTERNAL",
        }));

        if (isExternalChannelOrigin && liveDeliveryMode === "EXTERNAL" && transferResult?.id) {
          try {
            await inventoryApi.documentTransfer(transferResult.id, {
              mode: "SUNAT",
              documentation: {
                operationType: "SALE",
                clientId: Number(form.liveClientId),
                destinationAddress: String(form.liveDestinationAddress || "").trim(),
                destinationUbigeo: String(form.liveDestinationUbigeo || "").trim(),
                transferStartDate: inventoryTodayLima(),
                transportModeCode: "02",
                packageCount: "1",
              },
            });
            toast.success(`Destino externo ${externalChannelLabel} guardado. La GRE quedó configurada como Venta / entrega a cliente.`);
          } catch (documentationError) {
            toast.warning(documentationError.response?.data?.error || "El traslado se creó, pero no se pudo guardar la configuración GRE de entrega externa.");
          }
        }
        toast.success(`Traslado ${transferResult.transferNumber || "creado"} registrado correctamente.`);
        if (transferResult?.documentation?.status === "ERROR") {
          toast.warning(
            `El traslado fue creado, pero la guía no pudo generarse: ${transferResult.documentation.error || "revisa la documentación del traslado"}.`,
          );
        } else if (transferResult?.documentation?.guide?.guide_number) {
          toast.success(`Guía ${transferResult.documentation.guide.guide_number} creada sobre el traslado.`);
        } else if (transferResult?.documentation?.mode === "SUNAT") {
          toast.info("La GRE quedó solicitada y se preparará después de aprobar el traslado.");
        }
        saved();
        return;
      }
      if (["category", "brand", "model"].includes(kind)) {
        const name = String(form.name || "").trim();
        if (name.length < 2) {
          toast.error("El nombre debe tener al menos 2 caracteres.");
          return;
        }

        if (kind === "model" && (!form.categoryId || !form.brandId)) {
          toast.error("Selecciona la categoría y la marca del modelo.");
          return;
        }

        const catalogId = product?.id || null;
        if (kind === "category") {
          await inventoryApi.saveCategory(catalogId, form);
        } else if (kind === "brand") {
          await inventoryApi.saveBrand(catalogId, form);
        } else {
          await inventoryApi.saveModel(catalogId, form);
        }
      }
      const successMessage = isEditingProduct
        ? "Producto actualizado correctamente."
        : isEditingLocation
          ? "Ubicación actualizada correctamente."
          : kind === "locations"
            ? "Ubicación creada correctamente."
            : isEditingCatalog
              ? `${kind === "brand" ? "Marca" : kind === "model" ? "Modelo" : "Categoría"} actualizada correctamente.`
              : `${kind === "brand" ? "Marca" : kind === "model" ? "Modelo" : kind === "category" ? "Categoría" : "Registro"} creada correctamente.`;
      toast.success(successMessage);
      saved();
    } catch (e) {
      toast.error(e.response?.data?.error || "No se pudo guardar.");
    } finally {
      setDeleting(false);
    }
  };
  const cats = option(meta.categories),
    brands = option(meta.brands),
    establishmentOptions = (meta.sedes || []).map((row) => ({
      value: Number(row.id),
      label: `${row.name} · ${establishmentTypeLabel(row.establishmentType)} · ${row.branchCode || "sin código"}`,
      raw: row,
    })),
    locationEstablishmentOptions = establishmentOptions.filter((item) =>
      form.type === "CENTRAL_WAREHOUSE"
        ? String(item.raw?.establishmentType || "").toUpperCase() === "WAREHOUSE"
        : true,
    ),
    modelOptions = option(availableModels),
    suppliers = option(meta.suppliers, "business_name"),
    locations = option(meta.locations),
    centralWarehouseOptions = centralWarehouses.map((row) => ({
      value: Number(row.id),
      label: `${row.name}${row.city ? ` · ${row.city}` : ""}${row.sedeBranchCode ? ` · Est. ${row.sedeBranchCode}` : ""}`,
      raw: row,
    })),
    products = option(meta.products || [], "title"),
    igvAffectations = (meta.igvAffectations || []).map((item) => ({
      value: String(item.code),
      label: `${item.name} (${item.code})`,
      raw: item,
    }));
  return (
    <div className={`inventory-modal${kind === "transfers" ? " transfer-create-modal" : ""}`}>
      <div className="backdrop" onClick={close} />
      <section className={kind === "products" ? "inventory-product-modal" : ""}>
        <header>
          <div>
            <h2>
              {kind === "location-delete"
                ? "Eliminar ubicación"
                : kind === "product-delete"
                  ? "Eliminar producto"
                  : kind === "catalog-delete"
                    ? "Eliminar catálogo"
                    : isEditingLocation
                      ? "Editar ubicación"
                      : kind === "locations"
                        ? "Nueva ubicación"
                        : isEditingProduct
                          ? "Editar producto"
                    : isEditingCatalog
                      ? "Editar catálogo"
                      : "Nuevo registro"}
            </h2>
          </div>
          <button onClick={close}>×</button>
        </header>
        <div className={`inventory-form ${kind === "products" ? "inventory-product-form" : ""}`}>
          {kind === "location-delete" && (
            <div className="delete-product-message">
              <strong>¿Eliminar “{product?.name}”?</strong>
              <p>
                La eliminación es lógica y quedará en auditoría. Se bloqueará si
                todavía existe stock, IMEI/series o un traslado activo asociado.
              </p>
            </div>
          )}
          {kind === "product-delete" && (
            <div className="delete-product-message inventory-annul-product">
              <strong>¿Retirar “{product?.title}” del inventario?</strong>
              <p>
                Si el producto no tiene historial se aplicará la eliminación lógica habitual.
                Si ya tiene stock, IMEI o movimientos, el administrador realizará una
                <b> anulación controlada</b>: se retirará el stock disponible, el IMEI quedará
                como baja y toda la trazabilidad histórica se conservará.
              </p>
              <div className="inventory-annul-warning">
                No se permitirá anular automáticamente un equipo vendido, facturado, reservado,
                en tránsito, dañado o asociado a un traslado activo.
              </div>
              <label className="inventory-annul-reason-label" htmlFor="inventory-annul-reason">
                Motivo <span>*</span>
              </label>
              <textarea
                id="inventory-annul-reason"
                className="inventory-annul-reason"
                rows={3}
                maxLength={500}
                value={form.annulReason || ""}
                onChange={(e) => set("annulReason", e.target.value)}
                placeholder="Ej.: Equipo registrado por error / IMEI ingresado incorrectamente"
              />
              <small>{String(form.annulReason || "").length}/500 caracteres</small>
            </div>
          )}
          {kind === "catalog-delete" && (
            <div className="delete-product-message">
              <strong>¿Eliminar “{product?.name}”?</strong>
              <p>
                Se realizará una eliminación lógica y quedará registrada en auditoría.
                La operación será bloqueada si el registro está relacionado con productos,
                equipos o modelos activos.
              </p>
            </div>
          )}
          {kind === "locations" && (
            <>
              <Field label="Tipo de ubicación">
                <Select
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  isClearable={false}
                  options={[
                    { value: "CENTRAL_WAREHOUSE", label: "Almacén central" },
                    { value: "BRANCH", label: "Sede / tienda" },
                    { value: "SUPPLIER", label: "Proveedor" },
                    { value: "DAMAGED", label: "Dañados" },
                    { value: "RETURNED", label: "Devoluciones" },
                  ]}
                  value={[
                    { value: "CENTRAL_WAREHOUSE", label: "Almacén central" },
                    { value: "BRANCH", label: "Sede / tienda" },
                    { value: "SUPPLIER", label: "Proveedor" },
                    { value: "DAMAGED", label: "Dañados" },
                    { value: "RETURNED", label: "Devoluciones" },
                  ].find((item) => item.value === form.type) || null}
                  onChange={(item) => {
                    const nextType = item?.value || "BRANCH";
                    setForm((current) => ({
                      ...current,
                      type: nextType,
                      sedeId: ["CENTRAL_WAREHOUSE", "BRANCH"].includes(nextType)
                        ? nextType === "CENTRAL_WAREHOUSE" &&
                          String(
                            (meta.sedes || []).find(
                              (row) => Number(row.id) === Number(current.sedeId),
                            )?.establishmentType || "",
                          ).toUpperCase() !== "WAREHOUSE"
                          ? null
                          : current.sedeId
                        : null,
                    }));
                  }}
                />
              </Field>

              <Field label="Establecimiento físico">
                <Select
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  options={locationEstablishmentOptions}
                  value={
                    locationEstablishmentOptions.find(
                      (item) => item.value === Number(form.sedeId),
                    ) || null
                  }
                  isDisabled={!["CENTRAL_WAREHOUSE", "BRANCH"].includes(form.type)}
                  onChange={(item) => {
                    const row = item?.raw || null;
                    setForm((current) => ({
                      ...current,
                      sedeId: item?.value || null,
                      name: current.name || row?.name || "",
                      city:
                        current.city ||
                        row?.districtName ||
                        row?.provinceName ||
                        row?.departmentName ||
                        "",
                    }));
                  }}
                  placeholder={
                    ["CENTRAL_WAREHOUSE", "BRANCH"].includes(form.type)
                      ? form.type === "CENTRAL_WAREHOUSE"
                        ? "Selecciona un establecimiento tipo Almacén"
                        : "Selecciona tienda, almacén u oficina"
                      : "No requerido para esta ubicación"
                  }
                  noOptionsMessage={() => "No hay establecimientos activos"}
                />
              </Field>

              <Field wide label="Nombre de la ubicación">
                <input
                  maxLength={160}
                  value={form.name || ""}
                  onChange={(event) => set("name", event.target.value)}
                  placeholder="Ejemplo: Almacén Central"
                />
              </Field>

              <Field label="Ciudad / agrupación operativa">
                <input
                  maxLength={100}
                  value={form.city || ""}
                  onChange={(event) => set("city", event.target.value)}
                  placeholder="Ejemplo: Pucallpa"
                />
                {form.type === "CENTRAL_WAREHOUSE" && (
                  <small>
                    Regla logística: máximo un Almacén Central activo por ciudad.
                  </small>
                )}
              </Field>

              <Field label="Estado">
                <label className="inventory-check">
                  <input
                    type="checkbox"
                    checked={form.isActive !== false}
                    onChange={(event) => set("isActive", event.target.checked)}
                  />
                  Ubicación activa
                </label>
              </Field>

              {form.sedeId && (
                <Field wide label="Validación del establecimiento">
                  {(() => {
                    const row = (meta.sedes || []).find(
                      (item) => Number(item.id) === Number(form.sedeId),
                    );
                    if (!row) return <div className="inventory-location-preview invalid">Establecimiento no disponible.</div>;
                    const valid =
                      String(row.address || "").trim() &&
                      /^\d{6}$/.test(String(row.ubigeo || "")) &&
                      /^\d{4}$/.test(String(row.branchCode || ""));
                    return (
                      <div className={`inventory-location-preview ${valid ? "valid" : "invalid"}`}>
                        <strong>{row.name}</strong>
                        <span>{row.address || "Sin dirección"}</span>
                        <small>
                          UBIGEO {row.ubigeo || "------"} · Código {row.branchCode || "----"} · {establishmentTypeLabel(row.establishmentType)}
                        </small>
                        <b>{valid ? "Configuración apta para GRE" : "Completa dirección, UBIGEO y código en Sedes"}</b>
                      </div>
                    );
                  })()}
                </Field>
              )}
            </>
          )}
          {kind === "products" && (
            <>
              <Field label="SKU" icon="sku">
                <div className="inventory-sku-control">
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={10}
                    value={form.sku || ""}
                    disabled={!skuEditable || generatingSku}
                    className={
                      skuStatus
                        ? skuStatus.valid
                          ? "input-valid"
                          : "input-invalid"
                        : ""
                    }
                    onChange={(e) => {
                      handleSkuInput(e);
                      set("sku", e.target.value);
                      setSkuStatus({
                        valid: false,
                        available: false,
                        message: `${e.target.value.length}/10 dígitos`,
                      });
                    }}
                    onBlur={() => skuEditable && validateSku()}
                    aria-invalid={Boolean(skuStatus && !skuStatus.valid)}
                  />
                  <button
                    type="button"
                    className={
                      skuEditable ? "sku-lock-button" : "sku-edit-button"
                    }
                    onClick={() => {
                      if (skuEditable) {
                        validateSku();
                        setSkuEditable(false);
                      } else {
                        setSkuEditable(true);
                      }
                    }}
                    disabled={generatingSku || validatingSku}
                  >
                    {skuEditable ? "Bloquear" : "Editar SKU"}
                  </button>
                </div>
                <small
                  className={
                    skuStatus?.valid
                      ? "field-feedback success messageSku"
                      : "field-feedback error messageSku"
                  }
                >
                  {generatingSku
                    ? "Generando SKU único..."
                    : validatingSku
                      ? "Validando SKU..."
                      : skuStatus?.message ||
                        "Código numérico único de 10 dígitos."}
                </small>
              </Field>
              <Field wide label="Título" icon="title">
                <input
                  value={form.title || ""}
                  onChange={(e) => {
                    set("title", e.target.value);
                    setModelTitleServerStatus(null);
                  }}
                  className={
                    form.title && form.modelId
                      ? modelTitleMatch.valid
                        ? "input-valid"
                        : "input-invalid"
                      : ""
                  }
                />
              </Field>
              <Field label="Categoría" icon="category">
                <Select
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  options={cats}
                  value={
                    cats.find((o) => o.value === Number(form.categoryId)) ||
                    null
                  }
                  placeholder={
                    cellphoneCategory
                      ? "Celulares"
                      : "Selecciona una categoría"
                  }
                  noOptionsMessage={() => "No hay categorías disponibles"}
                  onChange={(o) => {
                    const nextCategoryId = o?.value || null;
                    const nextIsCellphone =
                      String(o?.raw?.slug || "").trim().toLowerCase() ===
                      "celulares";

                    setForm((current) => ({
                      ...current,
                      categoryId: nextCategoryId,
                      modelId:
                        meta.models.some(
                          (model) =>
                            Number(model.id) === Number(current.modelId) &&
                            Number(model.category_id) === Number(nextCategoryId),
                        )
                          ? current.modelId
                          : null,
                      stock: nextIsCellphone ? 1 : current.stock,
                      imei: nextIsCellphone ? current.imei || "" : "",
                      imei2: nextIsCellphone ? current.imei2 || "" : "",
                    }));
                    setImeiStatus(null);
                    setImei2Status(null);
                  }}
                />
              </Field>
              <Field label="Marca" icon="brand">
                <Select
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  options={brands}
                  value={
                    brands.find((o) => o.value === Number(form.brandId)) || null
                  }
                  onChange={(item) => {
                    const nextBrandId = item?.value || null;
                    setForm((current) => ({
                      ...current,
                      brandId: nextBrandId,
                      modelId:
                        meta.models.some(
                          (model) =>
                            Number(model.id) === Number(current.modelId) &&
                            Number(model.brand_id) === Number(nextBrandId),
                        )
                          ? current.modelId
                          : null,
                    }));
                    setModelTitleServerStatus(null);
                  }}
                  placeholder="Selecciona una marca"
                />
              </Field>
              <Field label="Modelo" icon="model">
                <Select
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  options={modelOptions}
                  value={
                    modelOptions.find(
                      (item) => item.value === Number(form.modelId),
                    ) || null
                  }
                  onChange={(item) => {
                    set("modelId", item?.value || null);
                    setModelTitleServerStatus(null);
                  }}
                  isDisabled={!form.categoryId || !form.brandId}
                  placeholder={
                    !form.categoryId || !form.brandId
                      ? "Selecciona categoría y marca"
                      : "Selecciona un modelo"
                  }
                  noOptionsMessage={() =>
                    "No existen modelos para esta categoría y marca"
                  }
                />
                {!modelTitleMatch.pending && (
                  <small
                    className={`model-title-feedback ${
                      modelTitleMatch.valid ? "success" : "error"
                    }`}
                    aria-live="polite"
                  >
                    {modelTitleServerStatus?.message || modelTitleMatch.message}
                  </small>
                )}
              </Field>
              <Field label="RUC" icon="ruc">
                <Select
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  options={option(meta.rucs, "business_name")}
                  value={
                    option(meta.rucs, "business_name").find(
                      (o) => o.value === Number(form.rucId),
                    ) || null
                  }
                  onChange={(o) => set("rucId", o?.value)}
                  placeholder="Selecciona un RUC"
                />
              </Field>
              <Field label="Proveedor" icon="supplier">
                <Select
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  options={suppliers}
                  value={suppliers.find((item) => item.value === Number(form.supplierId)) || null}
                  onChange={(item) => set("supplierId", item?.value || null)}
                  placeholder="Selecciona un proveedor"
                  noOptionsMessage={() => "No hay proveedores activos"}
                />
              </Field>
              <Field label="N. Factura" icon="invoice">
                <input type="text" maxLength={80} value={form.invoiceNumber || ""}
                  onChange={(e) => set("invoiceNumber", String(e.target.value || "").toUpperCase().replace(/[^A-Z0-9\-\/._ ]/g, "").slice(0, 80))}
                  placeholder="Ejemplo: F001-00001234" />
                <small>Número de factura o documento de compra del proveedor.</small>
              </Field>
              {isCellphone && (<>
                <Field label="IMEI 1" icon="imei">
                  <input type="text" inputMode="numeric" maxLength={15}
                    className={imeiStatus ? (imeiStatus.valid ? "input-valid" : "input-invalid") : ""}
                    value={form.imei || ""}
                    onChange={(e) => { handleImeiInput(e); const value=normalizeImeiValue(e.target.value); set("imei",value); setImeiStatus(validateImeiValue(value)); }}
                    onBlur={() => validateImei()} placeholder="15 dígitos" aria-invalid={Boolean(imeiStatus && !imeiStatus.valid)} />
                  <small className={imeiStatus?.valid ? "field-feedback success" : "field-feedback error"}>{imeiStatus?.message || "Obligatorio · solo números · 15 dígitos."}</small>
                </Field>
                <Field label="IMEI 2" icon="imei">
                  <input type="text" inputMode="numeric" maxLength={15}
                    className={imei2Status && form.imei2 ? (imei2Status.valid ? "input-valid" : "input-invalid") : ""}
                    value={form.imei2 || ""}
                    onChange={(e) => { handleImeiInput(e); const value=normalizeImeiValue(e.target.value); set("imei2",value); if(!value){setImei2Status(null);return;} const local=validateImeiValue(value); setImei2Status(value===normalizeImeiValue(form.imei)?{...local,valid:false,available:false,code:"IMEI_FIELDS_DUPLICATE",message:"IMEI 2 debe ser diferente de IMEI 1."}:local); }}
                    onBlur={() => validateImei2()} placeholder="Opcional · 15 dígitos" aria-invalid={Boolean(form.imei2 && imei2Status && !imei2Status.valid)} />
                  <small className={!form.imei2 || imei2Status?.valid ? "field-feedback success" : "field-feedback error"}>{!form.imei2 ? "Opcional. Si escribes 1 dígito o más, debes completar exactamente 15." : imei2Status?.message || `${form.imei2.length}/15 dígitos`}</small>
                </Field>
              </>)}
              <Field label="Precio compra" icon="money">
                <input
                  type="text"
                  inputMode="decimal"
                  value={form.purchasePrice || ""}
                  onChange={(e) => {
                    handlePriceInput(e);
                    set("purchasePrice", e.target.value);
                  }}
                  placeholder="S/ 0,00"
                />
              </Field>
              <Field label="Precio venta" icon="money">
                <input
                  type="text"
                  inputMode="decimal"
                  value={form.salePrice || ""}
                  onChange={(e) => {
                    handlePriceInput(e);
                    set("salePrice", e.target.value);
                  }}
                  placeholder="S/ 0,00"
                />
              </Field>
              {!isEditingProduct &&
                (isCellphone || Number(form.stock || 0) > 0) && (
                  <Field label="Almacén de ingreso inicial" icon="warehouse">
                    {automaticInitialWarehouse ? (
                      <>
                        <div
                          className={`inventory-auto-warehouse ${
                            userInventoryContext?.configured ? "configured" : "missing"
                          }`}
                          style={{
                            minHeight: 44,
                            border: "1px solid #d7deea",
                            borderRadius: 10,
                            padding: "9px 12px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            background: "#f8fafc",
                          }}
                        >
                          <div>
                            <strong>
                              {userInventoryContext?.centralWarehouseName ||
                                "Almacén Central no configurado"}
                            </strong>
                            <span>
                              {userInventoryContext?.operationalCity
                                ? `Ciudad: ${userInventoryContext.operationalCity}`
                                : "Ciudad operativa pendiente de configuración"}
                            </span>
                          </div>
                          <b aria-label="Asignación automática">🔒</b>
                        </div>
                        <small>
                          Asignado automáticamente según la ciudad operativa de tu usuario.
                          No puede modificarse manualmente.
                        </small>
                      </>
                    ) : (
                      <>
                        <Select
                          styles={selectStyles}
                          menuPortalTarget={document.body}
                          options={centralWarehouseOptions}
                          value={
                            centralWarehouseOptions.find(
                              (item) =>
                                Number(item.value) === Number(form.initialLocationId),
                            ) || null
                          }
                          onChange={(item) =>
                            set("initialLocationId", item?.value || null)
                          }
                          placeholder={
                            centralWarehouseOptions.length > 1
                              ? "Selecciona Pucallpa, Iquitos, etc."
                              : "Selecciona el Almacén Central"
                          }
                          noOptionsMessage={() =>
                            "No existen Almacenes Centrales activos configurados"
                          }
                        />
                        <small>
                          Como administrador, puedes seleccionar el Almacén Central de ingreso.
                        </small>
                      </>
                    )}
                  </Field>
                )}
              <Field label={isEditingProduct ? "Stock actual" : "Stock"} icon="stock">
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={isCellphone && !isEditingProduct ? 1 : (form.stock ?? 0)}
                  disabled={isCellphone || isEditingProduct}
                  aria-disabled={isCellphone || isEditingProduct}
                  title={
                    isEditingProduct
                      ? "El stock se modifica mediante ingresos, ventas o movimientos de inventario."
                      : isCellphone
                        ? "Los celulares se registran siempre como una unidad por IMEI."
                        : "Ingresa el stock inicial del producto."
                  }
                  onChange={(e) => {
                    if (!isCellphone && !isEditingProduct) {
                      set("stock", e.target.value);
                    }
                  }}
                />
                {isEditingProduct && (
                  <small>
                    El stock se controla mediante movimientos; no se altera al editar
                    los datos maestros.
                  </small>
                )}
              </Field>
              <Field wide label="Afectación IGV" icon="tax">
                <Select
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  options={igvAffectations}
                  value={
                    igvAffectations.find(
                      (item) => String(item.value) === String(form.igvAffectationCode),
                    ) || null
                  }
                  onChange={(item) =>
                    set("igvAffectationCode", item?.value || "20")
                  }
                  isClearable={false}
                  placeholder="Selecciona la afectación tributaria"
                  noOptionsMessage={() => "No hay afectaciones IGV disponibles"}
                />
                <small>
                  Se usa automáticamente al generar la boleta o factura SUNAT. Para
                  productos nuevos se selecciona por defecto Exonerado - Operación
                  Onerosa (20).
                </small>
              </Field>
              <Field wide label="Descripción" icon="description">
                <textarea
                  value={form.description || ""}
                  onChange={(e) => set("description", e.target.value)}
                />
              </Field>
            </>
          )}
          {kind === "receipts" && (
            <>
              <Field label="Proveedor">
                <Select
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  options={option(meta.suppliers, "business_name")}
                  onChange={(o) => set("supplierId", o?.value)}
                />
              </Field>
              <Field wide label="Descripción">
                <textarea
                  value={form.description || ""}
                  onChange={(e) => set("description", e.target.value)}
                />
              </Field>
              <Field wide label="Fotografías (2 a 7)">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => setFiles([...e.target.files].slice(0, 7))}
                />
                <small>{files.length} archivo(s)</small>
              </Field>
            </>
          )}
          {kind === "stock" && (
            <>
              <Field label="Producto">
                <Select
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  options={products}
                  onChange={(o) => set("productId", o?.value)}
                />
              </Field>
              <Field label="Almacén Central de ingreso">
                <Select
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  options={centralWarehouseOptions}
                  value={
                    centralWarehouseOptions.find(
                      (item) => Number(item.value) === Number(form.locationId),
                    ) || null
                  }
                  onChange={(o) => set("locationId", o?.value || null)}
                  placeholder="Selecciona Pucallpa, Iquitos, etc."
                  noOptionsMessage={() =>
                    "No existen Almacenes Centrales activos configurados"
                  }
                />
                <small>
                  El stock se registrará físicamente en el almacén seleccionado.
                </small>
              </Field>
              <Field label="Cantidad">
                <input
                  type="number"
                  value={form.quantity || 1}
                  onChange={(e) => set("quantity", Number(e.target.value))}
                />
              </Field>
              <Field wide label="IMEI 1 (producto serializado)">
                <input
                  value={form.serials[0].imei1}
                  onChange={(e) =>
                    set("serials", [
                      { ...form.serials[0], imei1: e.target.value },
                    ])
                  }
                />
              </Field>
            </>
          )}
          {kind === "transfers" && (
            <>
              <Field label="Origen">
                <Select
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  options={locations.filter((o) => o.raw.type !== "TRANSIT")}
                  value={
                    locations.find(
                      (o) => Number(o.value) === Number(form.originLocationId),
                    ) || null
                  }
                  onChange={(o) => {
                    const nextOriginId = o?.value || null;
                    setForm((current) => ({
                      ...current,
                      originLocationId: nextOriginId,
                      destinationLocationId:
                        Number(current.destinationLocationId) === Number(nextOriginId)
                          ? null
                          : current.destinationLocationId,
                      items: [
                        {
                          productId: null,
                          variantId: null,
                          quantity: 1,
                          unitCost: 0,
                          serialIds: [],
                        },
                      ],
                    }));
                    setTransferAvailability([]);
                    setTransferAvailabilityError(null);
                    setTransferEntryMode("SERIALS");
                    setLiveDeliveryMode("BRANCH");
                  }}
                  placeholder="Selecciona el origen"
                />
              </Field>
              {isExternalChannelOrigin && (
                <Field wide label={`Tipo de entrega ${externalChannelLabel}`}>
                  <div className="transfer-entry-picker live-delivery-picker">
                    <button type="button" className={liveDeliveryMode === "BRANCH" ? "active" : ""} onClick={() => setLiveDeliveryMode("BRANCH")}>
                      <strong>Entrega en sede</strong><small>Traslado interno hacia otra ubicación de la empresa.</small>
                    </button>
                    <button type="button" className={liveDeliveryMode === "EXTERNAL" ? "active" : ""} onClick={() => setLiveDeliveryMode("EXTERNAL")}>
                      <strong>Otra dirección</strong><small>Entrega a cliente y preparación de GRE por venta.</small>
                    </button>
                  </div>
                </Field>
              )}
              <Field label={isExternalChannelOrigin && liveDeliveryMode === "EXTERNAL" ? "Sede logística de control" : "Destino"}>
                <Select
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  options={locations.filter(
                    (o) => o.raw.type !== "TRANSIT" && Number(o.value) !== Number(form.originLocationId),
                  )}
                  value={locations.find((o) => Number(o.value) === Number(form.destinationLocationId)) || null}
                  onChange={(o) => set("destinationLocationId", o?.value || null)}
                  placeholder={isExternalChannelOrigin && liveDeliveryMode === "EXTERNAL" ? "Selecciona la sede que controla el despacho" : "Selecciona el destino"}
                />
                {isExternalChannelOrigin && liveDeliveryMode === "EXTERNAL" && <small>Esta sede solo conserva el control logístico. La dirección que irá en la GRE será la dirección externa indicada abajo.</small>}
              </Field>
              {isExternalChannelOrigin && liveDeliveryMode === "EXTERNAL" && (
                <>
                  <Field wide label="Cliente destinatario">
                    <div className="gre-client-row"><Select styles={selectStyles} menuPortalTarget={document.body}
                      options={liveShippingClients.map((client) => ({ value: client.id, label: `${client.documentNumber} · ${client.fullName}`, raw: client }))}
                      value={(() => { const c=liveShippingClients.find((x)=>String(x.id)===String(form.liveClientId)); return c ? {value:c.id,label:`${c.documentNumber} · ${c.fullName}`,raw:c} : null; })()}
                      onChange={(option) => { const c=option?.raw; setForm((current)=>({...current,liveClientId:c?.source === "register" && String(c.id).startsWith("register-") ? null : c?.id||null,liveDestinationAddress:c?.fiscalAddress||"",liveDestinationUbigeo:c?.ubigeo||""})); }}
                      placeholder="Busca o selecciona el cliente" noOptionsMessage={() => "No hay clientes registrados"} />
                      <button type="button" className="gre-add-client-button" onClick={() => setLiveClientModalOpen(true)}>+ Nuevo cliente</button></div>
                  </Field>
                  <Field label="Ubigeo de entrega">
                    <Select styles={selectStyles} menuPortalTarget={document.body}
                      options={liveUbigeos.map((u)=>({value:u.ubigeo || u.code,label:u.label || `${u.department || ""} · ${u.province || ""} · ${u.district || ""}`,raw:u}))}
                      value={(() => { const u=liveUbigeos.find((x)=>String(x.ubigeo||x.code)===String(form.liveDestinationUbigeo||"")); return u ? {value:u.ubigeo||u.code,label:u.label || `${u.department || ""} · ${u.province || ""} · ${u.district || ""}`,raw:u} : null; })()}
                      onInputChange={(value,meta)=>{if(meta.action==="input-change") inventoryApi.shippingUbigeos(value).then((r)=>setLiveUbigeos(unwrap(r)||[])).catch(()=>{});}} onChange={(option)=>set("liveDestinationUbigeo", option?.value || "")} placeholder="Busca departamento, provincia o distrito" />
                  </Field>
                  <Field wide label="Dirección exacta de entrega">
                    <input value={form.liveDestinationAddress || ""} onChange={(e)=>set("liveDestinationAddress",e.target.value)} placeholder="Ej. Av. Participación 1234, referencia..." />
                  </Field>
                </>
              )}
              <Field wide label="Selecciona el tipo de productos">
                <div className="transfer-entry-picker" role="group" aria-label="Tipo de productos del traslado">
                  <button type="button" aria-pressed={transferEntryMode === "SERIALS"}
                    className={transferEntryMode === "SERIALS" ? "active" : ""}
                    onClick={() => { setTransferEntryMode("SERIALS"); set("items", []); }}>
                    <strong>📱 Celulares con IMEI</strong>
                    <small>Selecciona uno o varios equipos identificados por IMEI.</small>
                  </button>
                  <button type="button" aria-pressed={transferEntryMode === "QUANTITY"}
                    className={transferEntryMode === "QUANTITY" ? "active" : ""}
                    onClick={() => { setTransferEntryMode("QUANTITY"); set("items", [{ productId: null, variantId: null, quantity: 1, unitCost: 0, serialIds: [] }]); }}>
                    <strong>📦 Accesorios por cantidad</strong>
                    <small>Agrega varios productos diferentes y define cuántas unidades trasladar de cada uno.</small>
                  </button>
                </div>
              </Field>
              {transferAvailabilityError && (
                <div className="inventory-transfer-availability-error" role="alert">
                  <strong>No se pudo cargar la disponibilidad del origen.</strong>
                  <span>{transferAvailabilityError.message}</span>
                  {(transferAvailabilityError.code || transferAvailabilityError.status) && (
                    <small>
                      {transferAvailabilityError.code ? `Código: ${transferAvailabilityError.code}` : ""}
                      {transferAvailabilityError.code && transferAvailabilityError.status ? " · " : ""}
                      {transferAvailabilityError.status ? `HTTP ${transferAvailabilityError.status}` : ""}
                    </small>
                  )}
                </div>
              )}
              {transferEntryMode === "SERIALS" ? (
                <Field wide label="Equipos / IMEI a trasladar">
                  <Select
                    isMulti
                    closeMenuOnSelect={false}
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    options={transferUnitOptions}
                    value={selectedTransferUnitOptions}
                    isDisabled={!form.originLocationId || loadingTransferAvailability}
                    isLoading={loadingTransferAvailability}
                    onChange={selectTransferUnits}
                    placeholder={
                      !form.originLocationId
                        ? "Selecciona primero el origen"
                        : "Busca y selecciona uno o varios IMEI"
                    }
                    noOptionsMessage={() =>
                      transferAvailabilityError
                        ? "No se pudo consultar la disponibilidad"
                        : "No hay IMEI disponibles en este origen"
                    }
                  />
                  <div className="transfer-selection-summary">
                    <strong>{selectedTransferUnitOptions.length}</strong>
                    <span> equipo(s) seleccionado(s)</span>
                    <small>{(form.items || []).length} producto(s) agrupado(s) en un solo traslado</small>
                  </div>
                </Field>
              ) : (
                // No envolver múltiples react-select en un <label>: al hacer clic en
                // una fila posterior, el navegador puede enfocar el primer select.
                <div className="field wide">
                  <span>Productos y cantidades a trasladar</span>
                  <div className="transfer-quantity-list">
                    {(form.items || []).map((item, index) => {
                      const currentOption = transferNonSerializedProductOptions.find(
                        (option) => Number(option.raw.productId) === Number(item.productId) &&
                          String(option.raw.variantId ?? "null") === String(item.variantId ?? "null"),
                      );
                      const options = transferNonSerializedProductOptions.filter((option) =>
                        (Number(option.raw.productId) === Number(item.productId) &&
                          String(option.raw.variantId ?? "null") === String(item.variantId ?? "null")) ||
                        !(form.items || []).some((other, otherIndex) => otherIndex !== index &&
                          Number(other.productId) === Number(option.raw.productId) &&
                          String(other.variantId ?? "null") === String(option.raw.variantId ?? "null")),
                      );
                      return (
                        <div className="transfer-quantity-row" key={index}>
                          <div className="transfer-quantity-product">
                            <label>Producto {index + 1}</label>
                            <Select styles={selectStyles} menuPortalTarget={document.body}
                              options={options} value={currentOption || null}
                              isDisabled={!form.originLocationId || loadingTransferAvailability}
                              isLoading={loadingTransferAvailability}
                              onChange={(option) => {
                                const selected = option?.raw;
                                set("items", (form.items || []).map((row, i) => i !== index ? row : ({
                                  productId: selected?.productId ?? null,
                                  variantId: selected?.variantId ?? null,
                                  quantity: 1,
                                  unitCost: Number(selected?.unitCost || 0), serialIds: [],
                                })));
                              }}
                              placeholder="Busca un accesorio" />
                          </div>
                          <div className="transfer-quantity-count">
                            <label>Cantidad</label>
                            <input type="number" min="1" step="1"
                              max={currentOption?.raw?.availableQuantity || undefined}
                              value={item.quantity ?? 1} disabled={!currentOption}
                              onChange={(event) => set("items", (form.items || []).map((row, i) =>
                                i === index ? { ...row, quantity: event.target.value === "" ? "" : Number(event.target.value) } : row))} />
                            {currentOption && <small>Stock disponible: {currentOption.raw.availableQuantity}</small>}
                          </div>
                          <button type="button" className="transfer-quantity-remove"
                            aria-label={`Quitar producto ${index + 1}`} title="Quitar producto"
                            onClick={() => set("items", (form.items || []).filter((_, i) => i !== index))}>×</button>
                        </div>
                      );
                    })}
                    <button type="button" className="transfer-quantity-add"
                      disabled={!form.originLocationId || loadingTransferAvailability ||
                        (form.items || []).length >= transferNonSerializedProductOptions.length}
                      onClick={() => set("items", [...(form.items || []),
                        { productId: null, variantId: null, quantity: 1, unitCost: 0, serialIds: [] }])}>
                      + Agregar otro producto
                    </button>
                    <div className="transfer-quantity-summary">
                      {(form.items || []).filter((item) => item.productId).length} productos ·{' '}
                      {(form.items || []).reduce((total, item) => total + (Number(item.quantity) > 0 ? Number(item.quantity) : 0), 0)} unidades en este traslado
                    </div>
                  </div>
                </div>
              )}
              {false && (
              <Field wide label="Documentación del traslado">
                <div className="transfer-documentation-picker transfer-create-documentation-picker">
                  <button
                    type="button"
                    className={form.documentationMode === "NONE" ? "active neutral" : ""}
                    onClick={() => set("documentationMode", "NONE")}
                  >
                    <strong>Sin guía por ahora</strong>
                    <small>El traslado puede documentarse después.</small>
                  </button>
                  <button
                    type="button"
                    className={form.documentationMode === "INTERNAL" ? "active" : ""}
                    onClick={() => set("documentationMode", "INTERNAL")}
                  >
                    <strong>Guía Interna</strong>
                    <small>Se genera después de crear el traslado.</small>
                  </button>
                  <button
                    type="button"
                    className={form.documentationMode === "SUNAT" ? "active sunat" : ""}
                    onClick={() => set("documentationMode", "SUNAT")}
                  >
                    <strong>GRE SUNAT</strong>
                    <small>Se solicita ahora y se prepara al aprobar.</small>
                  </button>
                </div>
              </Field>
              )}
              {false && form.documentationMode !== "NONE" && (
                <>
                  <Field label="Fecha inicio traslado">
                    <input
                      type="date"
                      value={form.documentation?.transferStartDate || ""}
                      onChange={(e) =>
                        set("documentation", { ...form.documentation, transferStartDate: e.target.value })
                      }
                    />
                  </Field>
                  <Field label="Peso bruto (KGM)">
                    <input
                      type="number"
                      min="0"
                      step="0.001"
                      value={form.documentation?.grossWeight ?? ""}
                      onChange={(e) =>
                        set("documentation", { ...form.documentation, grossWeight: e.target.value })
                      }
                      placeholder="13.000"
                    />
                  </Field>
                  <Field label="Bultos / pallets">
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={form.documentation?.packageCount ?? "1"}
                      onChange={(e) =>
                        set("documentation", { ...form.documentation, packageCount: e.target.value })
                      }
                    />
                  </Field>
                  <Field label="Placa vehículo">
                    <input
                      maxLength={8}
                      value={form.documentation?.vehiclePlate || ""}
                      onChange={(e) =>
                        set("documentation", { ...form.documentation, vehiclePlate: e.target.value.toUpperCase() })
                      }
                      placeholder="ABC123"
                    />
                  </Field>
                  {form.documentationMode === "SUNAT" && (
                    <>
                      <Field label="DNI conductor">
                        <input
                          inputMode="numeric"
                          maxLength={15}
                          value={form.documentation?.driverDocumentNumber || ""}
                          onChange={(e) =>
                            set("documentation", {
                              ...form.documentation,
                              driverDocumentNumber: e.target.value.replace(/\D/g, "").slice(0, 15),
                            })
                          }
                        />
                      </Field>
                      <Field label="Licencia">
                        <input
                          maxLength={10}
                          value={form.documentation?.driverLicense || ""}
                          onChange={(e) =>
                            set("documentation", {
                              ...form.documentation,
                              driverLicense: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""),
                            })
                          }
                        />
                      </Field>
                      <Field label="Nombres conductor">
                        <input
                          maxLength={250}
                          value={form.documentation?.driverFirstNames || ""}
                          onChange={(e) =>
                            set("documentation", { ...form.documentation, driverFirstNames: e.target.value })
                          }
                        />
                      </Field>
                      <Field label="Apellidos conductor">
                        <input
                          maxLength={250}
                          value={form.documentation?.driverLastNames || ""}
                          onChange={(e) =>
                            set("documentation", { ...form.documentation, driverLastNames: e.target.value })
                          }
                        />
                      </Field>
                    </>
                  )}
                  <div className="transfer-documentation-notice wide">
                    El traslado se registra primero. Si la generación documental falla, el traslado se conserva y podrás reintentar desde la columna Documentación.
                  </div>
                </>
              )}
              <Field wide label="Notas">
                <textarea
                  value={form.notes || ""}
                  onChange={(e) => set("notes", e.target.value)}
                  placeholder="Observación opcional del traslado"
                />
              </Field>
            </>
          )}
          {["category", "brand", "model"].includes(kind) && (
            <>
              <Field
                wide
                label={
                  kind === "brand"
                    ? "Nombre de la marca"
                    : kind === "model"
                      ? "Nombre del modelo"
                      : "Nombre de la categoría"
                }
              >
                <input
                  autoFocus
                  maxLength={100}
                  value={form.name || ""}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder={
                    kind === "brand"
                      ? "Ejemplo: Samsung"
                      : kind === "model"
                        ? "Ejemplo: Galaxy A56"
                        : "Ejemplo: Celulares"
                  }
                />
                <small>
                  {kind === "model"
                    ? "Usa el nombre comercial exacto del modelo."
                    : "El código interno se genera automáticamente desde el nombre."}
                </small>
              </Field>

              {kind === "model" && (
                <>
                  <Field label="Categoría">
                    <Select
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                      options={option(meta.categories)}
                      value={
                        option(meta.categories).find(
                          (item) => item.value === Number(form.categoryId),
                        ) || null
                      }
                      onChange={(item) => set("categoryId", item?.value || null)}
                      placeholder="Selecciona categoría"
                    />
                  </Field>
                  <Field label="Marca">
                    <Select
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                      options={option(meta.brands)}
                      value={
                        option(meta.brands).find(
                          (item) => item.value === Number(form.brandId),
                        ) || null
                      }
                      onChange={(item) => set("brandId", item?.value || null)}
                      placeholder="Selecciona marca"
                    />
                  </Field>
                  <Field wide label="Código del modelo (opcional)">
                    <input
                      maxLength={80}
                      value={form.modelCode || ""}
                      onChange={(event) => set("modelCode", event.target.value)}
                      placeholder="Ejemplo: SM-A566E"
                    />
                  </Field>
                </>
              )}

              {kind === "category" && (
                <>
                  <Field label="Color identificador">
                    <input
                      type="color"
                      value={form.color || "#2563eb"}
                      onChange={(e) => set("color", e.target.value)}
                    />
                  </Field>
                  <Field label="Control serializado">
                    <label className="inventory-check">
                      <input
                        type="checkbox"
                        checked={Boolean(form.isSerialized)}
                        onChange={(e) => set("isSerialized", e.target.checked)}
                      />
                      Requiere IMEI o número de serie
                    </label>
                  </Field>
                </>
              )}

              {kind === "brand" && (
                <Field wide label="Ruta del logotipo (opcional)">
                  <input
                    maxLength={255}
                    value={form.logoPath || ""}
                    onChange={(e) => set("logoPath", e.target.value)}
                    placeholder="/uploads/brands/samsung.webp"
                  />
                </Field>
              )}

              <Field wide label="Descripción">
                <textarea
                  maxLength={500}
                  value={form.description || ""}
                  onChange={(e) => set("description", e.target.value)}
                  placeholder="Información breve para identificar este catálogo."
                />
              </Field>

              <Field wide label="Estado">
                <label className="inventory-check">
                  <input
                    type="checkbox"
                    checked={form.isActive !== false}
                    onChange={(e) => set("isActive", e.target.checked)}
                  />
                  Disponible para seleccionar en nuevos productos
                </label>
              </Field>
            </>
          )}
        </div>
        {kind === "transfers" && liveClientModalOpen && (
          <ShippingClientModal
            close={() => setLiveClientModalOpen(false)}
            saved={(client) => {
              setLiveShippingClients((current) => {
                const filtered = current.filter((item) => Number(item.id) !== Number(client.id));
                return [client, ...filtered];
              });
              setForm((current) => ({
                ...current,
                liveClientId: client.id,
                liveDestinationAddress: client.fiscalAddress || "",
                liveDestinationUbigeo: client.ubigeo || "",
              }));
              setLiveClientModalOpen(false);
            }}
          />
        )}
        <footer className="inventory-modal-footer">
          <button
            type="button"
            className="modal-button modal-button-secondary"
            onClick={close}
          >
            Cancelar
          </button>
          <button
            className={`modal-button ${["location-delete", "product-delete", "catalog-delete"].includes(kind) ? "modal-button-danger" : "modal-button-primary"}`}
            onClick={submit}
            disabled={
              deleting ||
              validatingImei ||
              validatingImei2 ||
              validatingSku ||
              generatingSku ||
              validatingModelTitle
            }
          >
            {deleting
              ? "Procesando..."
              : kind === "product-delete"
                ? "Anular / eliminar"
                : kind === "location-delete"
                  ? "Sí, eliminar"
                : isEditingLocation
                  ? "Guardar cambios"
                : isEditingProduct
                  ? "Guardar cambios"
                  : "Guardar"}
          </button>
        </footer>
      </section>
    </div>
  );
}
